import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { io } from 'socket.io-client';
// 2026-10-15-line-history-import-v85
import LineHistoryImport from './LineHistoryImport';
import LineReplyPreview from './LineReplyPreview';
import {updateLineReplyFromEvent} from './lineReplyEvents';
import { chatApi } from './api';
import { getCurrentEmployeeId, getToken } from './auth';
import { CHAT_OPEN_VERSION, createRoomRequests, positiveId, roomOpenTarget, roomTargetFromUrl, rememberRoomTarget } from './chatRoomNavigation';
import { autoEnsureDevicePushSubscription, requestAndEnsureDevicePushSubscription, resetDevicePushSubscription, sendPushTest, clearChatNotifications, setChatAppBadge } from './webPushClient';

const CHAT_SOCKET_PATH = process.env.REACT_APP_CHAT_SOCKET_PATH || '/chat-socket.io';
const MESSAGE_PAGE_SIZE = 50;
const CHAT_MESSAGE_DRAG_MIME = 'application/x-hansero-chat-message';
const CHAT_MOBILE_MEDIA_QUERY = '(max-width: 820px)';

function isMobileChatViewport() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(CHAT_MOBILE_MEDIA_QUERY).matches;
}

function uniquePositiveIds(values) {
  const input = Array.isArray(values) ? values : [values];
  const result = [];
  const seen = new Set();
  input.forEach((value) => {
    const id = Number(value);
    if (!Number.isInteger(id) || id <= 0 || seen.has(id)) return;
    seen.add(id);
    result.push(id);
  });
  return result;
}

function toDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatRoomTime(value) {
  const d = toDate(value);
  if (!d) return value ? String(value).slice(0, 16) : '';
  return d.toLocaleString('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function formatMessageTime(value) {
  const d = toDate(value);
  if (!d) return value ? String(value).slice(11, 16) : '';
  return d.toLocaleTimeString('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function getDateKey(value) {
  const d = toDate(value);
  if (!d) return value ? String(value).slice(0, 10) : '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getMessageDisplayTimestamp(msg = {}) {
  const metadata = getMessageMetadata(msg);
  if (metadata.source === 'LINE_LIVE' && metadata.original_created_at) return metadata.original_created_at;
  return msg.display_created_at || msg.line_import_display_created_at || msg.created_at;
}

function getMessageDisplayBody(msg = {}) {
  const body = typeof msg.display_body === 'string' ? msg.display_body : (msg.body || '');
  if (getMessageMetadata(msg).source === 'LINE_HISTORY_BACKFILL') {
    return body.replace(/^\[원본 발신자:\s*[^\]\n]+\]\s*\n?/, '');
  }
  return body;
}

function formatMessageDate(value) {
  const d = toDate(value);
  if (!d) return value ? String(value).slice(0, 10) : '';
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
}



function sanitizeSystemMessageBody(value) {
  // 예전 시스템 메시지에 저장된 직책 표기(예: 이사(부서장))는 화면에서만 제거한다.
  return String(value || '')
    .replace(/(대표|부사장|전무|상무|이사|부장|차장|과장|대리|주임|사원|소장|실장|책임|선임|연구원)\((부서장|팀장|관리자|방장|대표)\)/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function formatDateInputValue(value) {
  const d = toDate(value);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function normalizeDateJumpInput(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const exact = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (exact) return `${exact[1]}-${exact[2]}-${exact[3]}`;

  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
}

function isValidDateJumpInput(value) {
  const text = normalizeDateJumpInput(value);
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
}

function getSafeJumpDate(value) {
  const text = normalizeDateJumpInput(value);
  return isValidDateJumpInput(text) ? text : '';
}

function formatDateInputLabel(value) {
  if (!value) return '';
  const d = toDate(`${value}T00:00:00`);
  if (!d) return value;
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
}

function formatFileSize(value) {
  const size = Number(value || 0);
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} MB`;
  return `${(size / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function getClipboardImageExtension(mimeType = '') {
  const mime = String(mimeType || '').toLowerCase();
  if (mime.includes('jpeg') || mime.includes('jpg')) return 'jpg';
  if (mime.includes('webp')) return 'webp';
  if (mime.includes('gif')) return 'gif';
  if (mime.includes('bmp')) return 'bmp';
  return 'png';
}

function makeClipboardImageFile(file, index = 0) {
  if (!file) return null;
  const ext = getClipboardImageExtension(file.type);
  const d = new Date();
  const stamp = [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
    '_',
    String(d.getHours()).padStart(2, '0'),
    String(d.getMinutes()).padStart(2, '0'),
    String(d.getSeconds()).padStart(2, '0'),
  ].join('');
  const name = `clipboard_${stamp}${index > 0 ? `_${index + 1}` : ''}.${ext}`;

  try {
    return new File([file], name, { type: file.type || `image/${ext}`, lastModified: Date.now() });
  } catch (err) {
    // Some older WebView implementations do not support the File constructor.
    file.name = name;
    return file;
  }
}

function formatEmployeePosition(rankName) {
  return String(rankName || '').trim();
}

function findLineSenderMember(originalSender, roomMembers = []) {
  const sender = String(originalSender || '').normalize('NFKC').replace(/\s+/g, '');
  if (!sender) return null;
  const suffixes = ['', '님', '이사님', '부장님', '차장님', '과장님', '대리님', '팀장님', '사장님', '대표님', '사원님'];
  const matches = roomMembers.filter((member) => {
    const name = String(member.name || '').normalize('NFKC').replace(/\s+/g, '');
    return name && suffixes.some((suffix) => sender === `${name}${suffix}`);
  });
  return matches.length === 1 ? matches[0] : null;
}

function getEmployeeDisplayPartsFromMessage(msg = {}, roomMembers = []) {
  const metadata = getMessageMetadata(msg);
  if (metadata.source === 'LINE_LIVE' || metadata.source === 'LINE_HISTORY_BACKFILL') {
    const name = String(metadata.original_sender || '').trim() || '사용자';
    if (metadata.source === 'LINE_HISTORY_BACKFILL' && msg.sender_employee_id && msg.sender_name) {
      return {
        department: String(msg.sender_department_name || '').trim(),
        name: String(msg.sender_name).trim(),
        position: formatEmployeePosition(msg.sender_rank_name, msg.sender_title_name),
        source: 'LINE',
      };
    }
    const member = findLineSenderMember(name, roomMembers);
    return member ? {
      department: String(member.department_name || '').trim(),
      name: String(member.name || '').trim(),
      position: formatEmployeePosition(member.rank_name, member.title_name),
      source: 'LINE',
    } : { department: 'LINE', name, position: '' };
  }
  return {
    department: String(msg.sender_department_name || '').trim(),
    name: String(msg.sender_name || '').trim() || '알 수 없음',
    position: formatEmployeePosition(msg.sender_rank_name, msg.sender_title_name),
  };
}

function formatEmployeeDisplayFromMessage(msg = {}, roomMembers = []) {
  const parts = getEmployeeDisplayPartsFromMessage(msg, roomMembers);
  return [parts.department, parts.name, parts.position].filter(Boolean).join(' ') + (parts.source ? ` - ${parts.source}` : '');
}

function SenderName({ parts }) {
  const safeParts = parts || {};
  return (
    <strong className="hc-sender-display">
      {safeParts.department ? <span className="hc-sender-department">{safeParts.department}</span> : null}
      <span className="hc-sender-person">{safeParts.name || '알 수 없음'}</span>
      {safeParts.position ? <span className="hc-sender-position">{safeParts.position}</span> : null}
      {safeParts.source ? <span className="hc-sender-source">- {safeParts.source}</span> : null}
    </strong>
  );
}

function formatEmployeeDisplayFromUser(user = {}) {
  const department = String(user.department_name || '').trim();
  const name = String(user.name || '').trim() || '이름 없음';
  const position = formatEmployeePosition(user.rank_name, user.title_name);
  return [department, name, position].filter(Boolean).join(' ');
}





function hcNotifyState(room = {}) {
  const mode = String(room.notification_mode || 'ALL').toUpperCase();
  const mutedUntil = room.muted_until ? new Date(room.muted_until) : null;
  const isMuted = mutedUntil && !Number.isNaN(mutedUntil.getTime()) && mutedUntil.getTime() > Date.now();
  const vibrationEnabled = room.vibration_enabled === true || String(room.vibration_enabled) === '1' || Number(room.vibration_enabled) === 1;

  if (mode === 'OFF' || isMuted) {
    return { label: '끄기', className: 'off', nextLabel: '알림' };
  }
  if (mode === 'VIBRATE_ONLY') {
    return { label: '진동', className: 'vibrate-only', nextLabel: '알림+진동' };
  }
  if (vibrationEnabled) {
    return { label: '알림+진동', className: 'vibrate', nextLabel: '끄기' };
  }
  return { label: '알림', className: 'notice', nextLabel: '진동' };
}

function hcNotifyNextPayload(room = {}) {
  const state = hcNotifyState(room);
  if (state.className === 'notice') {
    return { notification_mode: 'VIBRATE_ONLY', vibration_enabled: true, muted_until: null };
  }
  if (state.className === 'vibrate-only') {
    return { notification_mode: 'ALL', vibration_enabled: true, muted_until: null };
  }
  if (state.className === 'vibrate') {
    return { notification_mode: 'OFF', vibration_enabled: false, muted_until: null };
  }
  return { notification_mode: 'ALL', vibration_enabled: false, muted_until: null };
}

function HcNotifyBellIcon() {
  return (
    <span className="hc-notify-bell" aria-hidden="true">
      <span className="hc-notify-wave hc-notify-wave-left" />
      <span className="hc-notify-bell-shape">
        <span className="hc-notify-bell-top" />
        <span className="hc-notify-bell-body" />
        <span className="hc-notify-bell-clapper" />
      </span>
      <span className="hc-notify-wave hc-notify-wave-right" />
      <span className="hc-notify-off-slash" />
    </span>
  );
}

function clampBadgeCount(count) {
  const n = Number(count || 0);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(999, Math.floor(n));
}

function formatBadgeCount(count) {
  const n = clampBadgeCount(count);
  if (n <= 0) return '';
  return n > 99 ? '99+' : String(n);
}

function updateBrowserBadge(count) {
  const n = clampBadgeCount(count);

  if (typeof navigator !== 'undefined') {
    try {
      if (n > 0 && typeof navigator.setAppBadge === 'function') {
        navigator.setAppBadge(n);
      } else if (n <= 0 && typeof navigator.clearAppBadge === 'function') {
        navigator.clearAppBadge();
      }
    } catch (err) {
      // PWA Badging API is optional. Ignore unsupported browser errors.
    }
  }

  if (typeof document === 'undefined') return;

  const existing = document.querySelector('link[rel~="icon"]');
  let link = existing;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }

  if (!link.dataset.originalHref && link.getAttribute('href')) {
    link.dataset.originalHref = link.getAttribute('href');
  }

  if (n <= 0) {
    if (link.dataset.originalHref) {
      link.href = link.dataset.originalHref;
    }
    link.removeAttribute('data-hc-badged');
    return;
  }

  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.fillStyle = '#2563eb';
  ctx.fillRect(0, 0, 64, 64);
  ctx.fillStyle = '#ffffff';
  ctx.font = '900 31px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('H', 32, 33);

  ctx.beginPath();
  ctx.arc(48, 16, 15, 0, Math.PI * 2);
  ctx.fillStyle = '#ef4444';
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.strokeStyle = '#ffffff';
  ctx.stroke();

  const label = n > 99 ? '99+' : String(n);
  ctx.fillStyle = '#ffffff';
  ctx.font = label.length > 2 ? '800 10px system-ui, sans-serif' : '900 14px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, 48, 16);

  link.href = canvas.toDataURL('image/png');
  link.dataset.hcBadged = 'true';
}

function getFileExt(name = '') {
  const match = String(name || '').toLowerCase().match(/\.([a-z0-9]+)$/);
  return match ? match[1] : '';
}

function getAttachmentKind(att = {}) {
  const mime = String(att.mime_type || '').toLowerCase();
  const ext = getFileExt(att.original_name);

  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('text/') || ['txt', 'log', 'csv', 'json', 'xml', 'md', 'sql', 'js', 'jsx', 'ts', 'tsx', 'css', 'html', 'py', 'cs', 'java', 'yml', 'yaml'].includes(ext)) return 'text';
  return 'file';
}

function getAttachmentIcon(att = {}) {
  const kind = getAttachmentKind(att);
  if (kind === 'image') return '🖼️';
  if (kind === 'pdf') return '📄';
  if (kind === 'video') return '🎬';
  if (kind === 'audio') return '🎧';
  if (kind === 'text') return '📝';
  return '📎';
}

function canPreviewAttachment(att = {}) {
  if (att?.is_expired) return false;
  return ['image', 'pdf', 'video', 'audio', 'text'].includes(getAttachmentKind(att));
}

function AttachmentCard({ attachment, mine, onDownload, onPreview, onContentResize }) {
  const [thumbUrl, setThumbUrl] = useState('');
  const kind = getAttachmentKind(attachment);
  const canPreview = canPreviewAttachment(attachment);
  const previewHint = kind === 'image' && canPreview ? '이미지 클릭 시 미리보기' : canPreview ? '클릭 시 미리보기' : '다운로드 가능';

  useEffect(() => {
    let canceled = false;
    let objectUrl = '';

    async function loadThumbnail() {
      if (kind !== 'image' || attachment?.is_expired || !attachment?.id) return;
      try {
        const res = await chatApi.get(`/attachments/${attachment.id}/preview`, { responseType: 'blob' });
        if (canceled) return;
        objectUrl = window.URL.createObjectURL(res.data);
        setThumbUrl(objectUrl);
      } catch (err) {
        if (!canceled) setThumbUrl('');
      }
    }

    loadThumbnail();
    return () => {
      canceled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [attachment?.id, attachment?.is_expired, kind]);

  const handleOpen = () => {
    if (attachment?.is_expired) return;
    if (canPreview) onPreview(attachment);
    else onDownload(attachment);
  };

  return (
    <div className={`hc-attachment-card ${mine ? 'mine' : ''} ${attachment?.is_expired ? 'expired' : ''}`}>
      {thumbUrl ? (
        <button
          type="button"
          className="hc-attachment-thumb"
          onClick={() => onPreview(attachment)}
          title="이미지 미리보기"
          aria-label={`${attachment.original_name || '첨부 이미지'} 미리보기`}
        >
          <img src={thumbUrl} alt={attachment.original_name || '첨부 이미지'} onLoad={onContentResize} />
        </button>
      ) : null}
      <div className="hc-attachment-row">
        <button
          type="button"
          className={`hc-attachment-open ${canPreview ? 'previewable' : ''}`}
          disabled={attachment?.is_expired}
          onClick={handleOpen}
          title={canPreview ? '미리보기 열기' : '다운로드'}
        >
          <span className="hc-file-icon">{getAttachmentIcon(attachment)}</span>
          <span className="hc-file-main">
            <strong title={attachment.original_name || '첨부파일'}>{attachment.original_name || '첨부파일'}</strong>
            <em>{attachment?.is_expired ? '만료된 파일' : [formatFileSize(attachment.size_bytes), previewHint].filter(Boolean).join(' · ')}</em>
          </span>
        </button>
        {!attachment?.is_expired ? (
          <button
            type="button"
            className="hc-attachment-download-action"
            onClick={(e) => {
              e.stopPropagation();
              onDownload(attachment);
            }}
            title="다운로드"
          >
            다운로드
          </button>
        ) : null}
      </div>
    </div>
  );
}


function PendingRoomItemFiles({ files = [], onRemove }) {
  const list = Array.isArray(files) ? files : [];
  if (!list.length) return null;
  return (
    <div className="hc-room-item-pending-files">
      {list.map((item) => {
        const isImage = String(item?.type || '').toLowerCase().startsWith('image/');
        return (
          <div key={item.id} className="hc-room-item-pending-file">
            {isImage && item.previewUrl ? (
              <img src={item.previewUrl} alt={item.name || '첨부 이미지'} />
            ) : (
              <span className="hc-room-item-pending-icon">📎</span>
            )}
            <div>
              <strong title={item.name || '첨부파일'}>{item.name || '첨부파일'}</strong>
              <em>{formatFileSize(item.size)}</em>
            </div>
            <button type="button" onClick={() => onRemove?.(item.id)} aria-label="첨부 제거">×</button>
          </div>
        );
      })}
    </div>
  );
}

function RoomItemAttachmentList({ attachments = [], onPreview, onDownload }) {
  const list = Array.isArray(attachments) ? attachments : [];
  if (!list.length) return null;
  return (
    <div className="hc-room-item-attachments">
      {list.map((attachment) => {
        const kind = getAttachmentKind(attachment);
        const previewable = canPreviewAttachment(attachment);
        const expired = Boolean(attachment?.is_expired);
        return (
          <button
            key={attachment.id || `${attachment.original_name}-${attachment.size_bytes}`}
            type="button"
            className={`hc-room-item-attachment ${kind} ${expired ? 'expired' : ''}`}
            disabled={expired}
            onClick={() => {
              if (expired) return;
              if (previewable) onPreview?.(attachment);
              else onDownload?.(attachment);
            }}
            title={expired ? '만료된 첨부파일' : previewable ? '미리보기' : '다운로드'}
          >
            {kind === 'image' ? <span className="hc-room-item-attachment-thumb">🖼️</span> : <span className="hc-room-item-attachment-icon">{getAttachmentIcon(attachment)}</span>}
            <span className="hc-room-item-attachment-info">
              <strong>{attachment.original_name || '첨부파일'}</strong>
              <em>{expired ? '만료된 파일' : [formatFileSize(attachment.size_bytes), previewable ? '미리보기' : '다운로드'].filter(Boolean).join(' · ')}</em>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function getRoomItemFileName(value = '') {
  return String(value || '').trim();
}

function getRoomItemAttachmentName(attachment = {}) {
  return getRoomItemFileName(attachment.original_name || attachment.file_name || attachment.filename || attachment.name || '');
}

function getRoomItemImageMarkers(body = '') {
  const names = [];
  const re = /\[이미지:\s*([^\]]+?)\]/g;
  let match;
  while ((match = re.exec(String(body || ''))) !== null) {
    const name = getRoomItemFileName(match[1]);
    if (name) names.push(name);
  }
  return names;
}

function findRoomItemAttachmentByName(attachments = [], name = '') {
  const target = getRoomItemFileName(name);
  if (!target) return null;
  return (Array.isArray(attachments) ? attachments : []).find((attachment) => getRoomItemAttachmentName(attachment) === target) || null;
}

function RoomItemInlineImage({ attachment, onPreview }) {
  const [url, setUrl] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked = '';
    let cancelled = false;
    if (!attachment?.id || attachment?.is_expired) {
      setUrl('');
      setFailed(Boolean(attachment?.is_expired));
      return () => {};
    }
    setFailed(false);
    chatApi.get(`/room-item-attachments/${attachment.id}/preview`, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        const blob = new Blob([res.data], { type: attachment.mime_type || res.headers?.['content-type'] || 'image/*' });
        const objectUrl = window.URL.createObjectURL(blob);
        revoked = objectUrl;
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (revoked) window.URL.revokeObjectURL(revoked);
    };
  }, [attachment?.id]);

  if (!attachment) {
    return <span className="hc-room-item-inline-missing">이미지를 찾을 수 없습니다.</span>;
  }

  return (
    <button
      type="button"
      className="hc-room-item-inline-image"
      onClick={() => onPreview?.(attachment)}
      title="이미지 미리보기"
    >
      {url && !failed ? <img src={url} alt={getRoomItemAttachmentName(attachment) || '첨부 이미지'} /> : <span>{failed ? '이미지를 불러오지 못했습니다.' : '이미지 불러오는 중...'}</span>}
      <em>{getRoomItemAttachmentName(attachment) || '이미지'}</em>
    </button>
  );
}

function RoomItemContent({ body = '', attachments = [], onPreview, onDownload }) {
  const text = String(body || '');
  const parts = [];
  const inlineNames = [];
  const re = /\[이미지:\s*([^\]]+?)\]/g;
  let lastIndex = 0;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    const name = getRoomItemFileName(match[1]);
    if (name) inlineNames.push(name);
    parts.push({ type: 'image', name });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push({ type: 'text', value: text.slice(lastIndex) });

  const inlineNameSet = new Set(inlineNames);
  const restAttachments = (Array.isArray(attachments) ? attachments : []).filter((attachment) => {
    const attachmentName = getRoomItemAttachmentName(attachment);
    if (inlineNameSet.has(attachmentName)) return false;
    // 공지/노트의 이미지는 본문 인라인 표시가 원칙입니다.
    // 혹시 서버 첨부 목록에 이미지가 같이 내려오더라도 하단 첨부파일 카드에 중복 노출하지 않습니다.
    if (getAttachmentKind(attachment) === 'image') return false;
    return true;
  });

  return (
    <>
      <div className="hc-room-item-content">
        {parts.length ? parts.map((part, index) => {
          if (part.type === 'image') {
            const attachment = findRoomItemAttachmentByName(attachments, part.name);
            return <RoomItemInlineImage key={`image-${index}-${part.name}`} attachment={attachment} onPreview={onPreview} />;
          }
          return <span key={`text-${index}`} className="hc-room-item-content-text">{part.value}</span>;
        }) : null}
      </div>
      <RoomItemAttachmentList attachments={restAttachments} onPreview={onPreview} onDownload={onDownload} />
    </>
  );
}

function getDirectRoomDefaultTitle(room = {}) {
  if (room.title === '나와의 채팅' || Number(room.direct_employee_id || 0) === Number(getCurrentEmployeeId() || 0)) {
    return '나와의 채팅';
  }
  return [room.direct_department_name, room.direct_employee_name, room.direct_rank_name]
    .map((v) => String(v || '').trim())
    .filter(Boolean)
    .join(' ') || '1:1 채팅';
}

function getRoomTitle(room) {
  if (!room) return '채팅';
  if (room.room_type === 'DIRECT') return room.title || getDirectRoomDefaultTitle(room);
  if (room.room_type === 'DEPARTMENT') return room.title || room.department_name || '부서 채팅';
  if (room.room_type === 'PROJECT') {
    const code = room.project_code ? `[${room.project_code}] ` : '';
    return room.title || `${code}${room.project_name || '프로젝트 채팅'}`;
  }
  return room.title || room.member_names || '그룹 채팅';
}

function getRoomTypeInfo(room = {}) {
  const type = String(room.room_type || '').toUpperCase();
  if (type === 'DIRECT') {
    const isSelf = room.title === '나와의 채팅' || Number(room.direct_employee_id || 0) === Number(getCurrentEmployeeId() || 0);
    return { label: isSelf ? '나' : '1:1', className: isSelf ? 'self' : 'direct' };
  }
  if (type === 'DEPARTMENT') return { label: '부서', className: 'department' };
  if (type === 'PROJECT') return { label: '프로젝트', className: 'project' };
  if (type === 'ANNOUNCEMENT') return { label: '공지', className: 'notice' };
  return { label: '그룹', className: 'group' };
}

function isSelfDirectRoom(room = {}) {
  if (!room) return false;
  if (String(room.room_type || '').toUpperCase() !== 'DIRECT') return false;
  return room.title === '나와의 채팅'
    || Number(room.direct_employee_id || 0) === Number(getCurrentEmployeeId() || 0)
    || Number(room.member_count || 0) <= 1;
}

function getRoomSub(room) {
  // 목록을 단순하게 유지하기 위해 방 설명 문구는 표시하지 않는다.
  return '';
}

function getRoomMemberCountChip(room) {
  if (!room) return '';
  const type = String(room.room_type || '').toUpperCase();
  if (type === 'DIRECT') return '';
  const memberCount = Number(room.member_count || 0);
  return memberCount > 0 ? `${memberCount}명` : '';
}

function getRoomTypeLabel(room = {}) {
  const type = String(room.room_type || '').toUpperCase();
  const memberCount = Number(room.member_count || 0);
  if (type === 'DIRECT') {
    return isSelfDirectRoom(room) ? '나' : '1:1';
  }
  const countText = memberCount > 0 ? `(${memberCount}명)` : '';
  if (type === 'DEPARTMENT') return `부서${countText}`;
  if (type === 'PROJECT') return `프로젝트${countText}`;
  if (type === 'ANNOUNCEMENT') return `공지${countText}`;
  return `그룹${countText}`;
}

function getRoomHeaderMeta(room = {}) {
  if (!room) return '';
  return getRoomTypeLabel(room);
}

function canRenameRoom(room) {
  if (!room) return false;
  const type = String(room.room_type || '').toUpperCase();
  // 1:1 대화는 각 사용자에게 상대방 이름으로 보여주는 것이 기본이므로 공용 방 제목 수정 대상에서 제외한다.
  return type !== 'DIRECT';
}



function getRoomUnreadCount(room = {}) {
  const candidates = [
    room.unread_count,
    room.unreadCount,
    room.unread_member_count,
    room.unreadMemberCount,
    room.unread_messages,
    room.unreadMessages,
    room.unread,
  ];

  for (const value of candidates) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return Math.floor(n);
  }
  return 0;
}

function normalizeRoom(raw = {}) {
  const room = raw && typeof raw === 'object' ? raw : {};
  const unread = getRoomUnreadCount(room);
  return {
    ...room,
    unread_count: unread,
    unread_member_count: unread,
  };
}

function normalizeRooms(items) {
  return (Array.isArray(items) ? items : []).map(normalizeRoom);
}

function isChatPageVisible() {
  if (typeof document === 'undefined') return true;
  return document.visibilityState !== 'hidden';
}

function normalizeMemberRole(role) {
  const text = String(role || 'MEMBER').toUpperCase();
  if (text === 'OWNER' || text === 'MANAGER' || text === 'MEMBER') return text;
  return 'MEMBER';
}

function getMemberRoleLabel(role) {
  const normalized = normalizeMemberRole(role);
  if (normalized === 'OWNER') return '방장';
  if (normalized === 'MANAGER') return '관리자';
  return '참여자';
}

function isManagerRole(role) {
  const normalized = normalizeMemberRole(role);
  return normalized === 'OWNER' || normalized === 'MANAGER';
}

function normalizeMessage(raw) {
  if (!raw) return null;
  return raw.message || raw;
}

function getMessageMetadata(msg = {}) {
  const raw = msg.metadata_json ?? msg.metadata ?? null;
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(String(raw));
  } catch {
    return {};
  }
}

function getMessagePreviewText(msg = {}) {
  if (!msg) return '';
  if (msg.message_type === 'FILE') return msg.body || '첨부파일';
  if (msg.message_type === 'SYSTEM') return '시스템 메시지';
  return String(msg.body || '').replace(/\s+/g, ' ').trim().slice(0, 80);
}

const MESSAGE_URL_RE = /(https?:\/\/[^\s<>()]+|www\.[^\s<>()]+)/gi;

function stripTrailingUrlPunctuation(value = '') {
  let text = String(value || '');
  let tail = '';
  while (/[.,!?;:\)\]]$/.test(text)) {
    tail = text.slice(-1) + tail;
    text = text.slice(0, -1);
  }
  return { urlText: text, tail };
}

function escapeRegExp(value = '') {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function renderTextWithSearchHighlight(text = '', keyword = '') {
  const source = String(text || '');
  const q = String(keyword || '').trim();
  if (!source) return null;
  if (!q) return source;
  const re = new RegExp(`(${escapeRegExp(q)})`, 'gi');
  const parts = source.split(re);
  return parts.map((part, index) => (
    part.toLowerCase() === q.toLowerCase()
      ? <mark key={index} className="hc-search-highlight">{part}</mark>
      : part
  ));
}

function renderMessageTextWithLinksAndHighlight(text = '', keyword = '') {
  const source = String(text || '');
  if (!source) return null;
  const nodes = [];
  let lastIndex = 0;
  const re = new RegExp(MESSAGE_URL_RE.source, 'gi');
  let match;
  while ((match = re.exec(source)) !== null) {
    const raw = match[0];
    if (match.index > lastIndex) {
      nodes.push(renderTextWithSearchHighlight(source.slice(lastIndex, match.index), keyword));
    }
    const { urlText, tail } = stripTrailingUrlPunctuation(raw);
    const href = urlText.startsWith('http') ? urlText : `https://${urlText}`;
    nodes.push(
      <a key={`link-${match.index}`} className="hc-message-link" href={href} target="_blank" rel="noreferrer">
        {renderTextWithSearchHighlight(urlText, keyword)}
      </a>
    );
    if (tail) nodes.push(renderTextWithSearchHighlight(tail, keyword));
    lastIndex = match.index + raw.length;
  }
  if (lastIndex < source.length) nodes.push(renderTextWithSearchHighlight(source.slice(lastIndex), keyword));
  return nodes;
}

function renderMessageTextWithLinks(text = '') {
  const source = String(text || '');
  if (!source) return null;
  const nodes = [];
  let lastIndex = 0;
  source.replace(MESSAGE_URL_RE, (match, _url, offset) => {
    if (offset > lastIndex) nodes.push(source.slice(lastIndex, offset));
    const { urlText, tail } = stripTrailingUrlPunctuation(match);
    const href = urlText.startsWith('www.') ? `https://${urlText}` : urlText;
    nodes.push(
      <a
        key={`url-${offset}-${urlText}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="hc-message-link"
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      >
        {urlText}
      </a>
    );
    if (tail) nodes.push(tail);
    lastIndex = offset + match.length;
    return match;
  });
  if (lastIndex < source.length) nodes.push(source.slice(lastIndex));
  return nodes;
}


function extractImportedOriginalSenderFromBody(value = '') {
  const text = String(value || '').trim();
  const match = text.match(/^\[원본 발신자:\s*([^\]]+)\]\s*\n?/);
  return match ? String(match[1] || '').trim() : '';
}

function getSharedCopyInfo(msg = {}, metadata = {}) {
  const shared = metadata?.shared_from || null;
  if (!shared) return null;

  const originalMetadata = metadata?.original_metadata || {};
  const senderCandidates = [
    shared.sender_display,
    shared.sender_name,
    shared.original_sender,
    shared.matched_sender_name,
    originalMetadata.original_sender,
    originalMetadata.matched_sender_name,
    extractImportedOriginalSenderFromBody(shared.body),
    extractImportedOriginalSenderFromBody(msg.body),
  ];
  const sender = senderCandidates.map((v) => String(v || '').trim()).find(Boolean) || '알 수 없음';
  const createdAt = shared.created_at || originalMetadata.original_created_at || null;
  let body = String(shared.body || shared.body_text || msg.body || shared.body_preview || '').trim();

  const importedSender = extractImportedOriginalSenderFromBody(body);
  if (importedSender) {
    body = body.replace(/^\[원본 발신자:\s*[^\]]+\]\s*\n?/, '').trim();
  }

  // 이전 버전에서 "원 발신자: 내용" 또는 "부서 이름 직급: 내용" 형태로 저장된 공유 메시지는 화면에서는 본문만 분리해서 보여줍니다.
  if (sender && body.startsWith(`${sender}:`)) {
    body = body.slice(sender.length + 1).trim();
  }
  if (!body) body = String(shared.body_preview || '').trim() || '공유한 메시지';

  return {
    sender,
    body,
    createdAt,
    sourceRoomId: shared.room_id,
    sourceMessageId: shared.message_id,
    messageType: String(shared.message_type || msg.message_type || 'TEXT').toUpperCase(),
  };
}

function SharedCopyContent({ info }) {
  if (!info) return null;
  return (
    <div className="hc-shared-copy">
      <div className="hc-shared-copy-label">전달된 대화</div>
      <div className="hc-shared-copy-card">
        <div className="hc-shared-copy-sender">{info.sender}</div>
        {info.createdAt ? <div className="hc-shared-copy-time">{formatMessageDate(info.createdAt)} {formatMessageTime(info.createdAt)}</div> : null}
        <div className="hc-shared-copy-body">{renderMessageTextWithLinks(info.body)}</div>
      </div>
    </div>
  );
}

function getSharePreviewLine(msg = {}, roomMembers = []) {
  const sender = formatEmployeeDisplayFromMessage(msg, roomMembers);
  const preview = getMessagePreviewText(msg) || (String(msg.message_type || '').toUpperCase() === 'FILE' ? '첨부파일' : '내용 없음');
  return sender ? `${sender}: ${preview}` : preview;
}

function uniqueById(items) {
  const map = new Map();
  const noIdItems = [];

  (Array.isArray(items) ? items : []).forEach((item) => {
    if (!item) return;
    const id = Number(item.id);
    if (!Number.isFinite(id) || id <= 0) {
      noIdItems.push(item);
      return;
    }

    const prev = map.get(id);
    // 같은 message_id가 REST 응답과 Socket.IO로 동시에 들어올 수 있으므로
    // id 기준으로 1건만 남기고, 나중에 들어온 값으로 보강합니다.
    map.set(id, prev ? { ...prev, ...item } : item);
  });

  // 누락된 LINE 과거 대화는 나중에 DB에 입력되므로 ID가 아닌 실제 대화 날짜로 정렬한다.
  const time = (item) => toDate(String(item?.created_at || '').replace(/^(\d{4}-\d{2}-\d{2}) /, '$1T'))?.getTime() || 0;
  return [...noIdItems, ...map.values()].sort((a, b) =>
    time(a) - time(b)
    || Number(a.id || 0) - Number(b.id || 0));
}

export default function ChatPage({ user, onLogout }) {
  const initialRoomTarget = useRef(roomTargetFromUrl(window.location.href, getCurrentEmployeeId()));
  const [rooms, setRooms] = useState([]);
  const [activeRoomId, setActiveRoomId] = useState(initialRoomTarget.current?.roomId || null);
  const [roomRefreshVersion, setRoomRefreshVersion] = useState(0);
  const [roomOpenError, setRoomOpenError] = useState('');
  const [messages, setMessages] = useState([]);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [hasNewerMessages, setHasNewerMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [loadingNewerMessages, setLoadingNewerMessages] = useState(false);
  const [jumpDate, setJumpDate] = useState('');
  const [dateJumping, setDateJumping] = useState(false);
  const [dateJumpNotice, setDateJumpNotice] = useState('');
  const [text, setText] = useState('');
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadTasks, setUploadTasks] = useState([]);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [socketState, setSocketState] = useState('offline');
  const [socketHint, setSocketHint] = useState('');
  const [userQuery, setUserQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [createMode, setCreateMode] = useState(null); // DIRECT | GROUP | ADD_MEMBERS
  const [groupTitle, setGroupTitle] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [historyAll, setHistoryAll] = useState(true);
  const [addMembersHistoryOpen, setAddMembersHistoryOpen] = useState(false);
  const [creatingRoom, setCreatingRoom] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobileLayout, setIsMobileLayout] = useState(() => isMobileChatViewport());
  const [preview, setPreview] = useState(null);
  const [messageSearchText, setMessageSearchText] = useState('');
  const [messageSearchApplied, setMessageSearchApplied] = useState('');
  const [searchingMessages, setSearchingMessages] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [roomMembers, setRoomMembers] = useState([]);
  const [loadingRoomMembers, setLoadingRoomMembers] = useState(false);
  const [readMarkerMessageId, setReadMarkerMessageId] = useState(null);
  const [replyTarget, setReplyTarget] = useState(null);
  const [deletingMessageId, setDeletingMessageId] = useState(null);
  const [messageMenu, setMessageMenu] = useState(null);
  const [shareMessage, setShareMessage] = useState(null);
  const [shareTargetRoomIds, setShareTargetRoomIds] = useState([]);
  const [sharingMessage, setSharingMessage] = useState(false);
  const [dragShareMessage, setDragShareMessage] = useState(null);
  const [dragShareTargetRoomId, setDragShareTargetRoomId] = useState(null);
  const [removingMemberId, setRemovingMemberId] = useState(null);
  const [roleChangingMemberId, setRoleChangingMemberId] = useState(null);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTitle, setRenameTitle] = useState('');
  const [renamingRoom, setRenamingRoom] = useState(false);
  const [leavingRoom, setLeavingRoom] = useState(false);
  const [deletingRoom, setDeletingRoom] = useState(false);
  const [lineHistoryImportOpen, setLineHistoryImportOpen] = useState(false);
  useEffect(() => { setLineHistoryImportOpen(false); }, [activeRoomId]);
  const [roomMenuOpen, setRoomMenuOpen] = useState(false);
  const [searchToolsOpen, setSearchToolsOpen] = useState(false);
  const [pushSettingsOpen, setPushSettingsOpen] = useState(false);
  const [testingPush, setTestingPush] = useState(false);
  const [repairingPush, setRepairingPush] = useState(false);
  const [sharedOpen, setSharedOpen] = useState(false);
  const [sharedScope, setSharedScope] = useState('room');
  const [sharedType, setSharedType] = useState('all');
  const [sharedItems, setSharedItems] = useState([]);
  const [loadingSharedItems, setLoadingSharedItems] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exportAllDates, setExportAllDates] = useState(false);
  const [exportIncludeAttachments, setExportIncludeAttachments] = useState(true);
  const [exportingRoom, setExportingRoom] = useState(false);
  const [exportJobId, setExportJobId] = useState(null);
  const [exportProgress, setExportProgress] = useState(null);
  const [exportRoomId, setExportRoomId] = useState(null);
  const [exportRoomTitle, setExportRoomTitle] = useState('');
  const [noticesOpen, setNoticesOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [roomNotices, setRoomNotices] = useState([]);
  const [roomNotes, setRoomNotes] = useState([]);
  const [loadingNotices, setLoadingNotices] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [noticeText, setNoticeText] = useState('');
  const [noticeEditMode, setNoticeEditMode] = useState(false);
  const [savingNotice, setSavingNotice] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [noticeFiles, setNoticeFiles] = useState([]);
  const [noteFiles, setNoteFiles] = useState([]);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [savingNote, setSavingNote] = useState(false);
  const [collapsedRoomSections, setCollapsedRoomSections] = useState(() => {
    try {
      const saved = window.localStorage.getItem('hansero-chat-collapsed-room-sections');
      const parsed = saved ? JSON.parse(saved) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (err) {
      return {};
    }
  });

  const socketRef = useRef(null);
  const socketConnectErrorTimerRef = useRef(null);
  const socketHealthyRef = useRef(false);
  const roomListRefreshTimerRef = useRef(null);
  const readEventSeenRef = useRef(new Set());
  const roomsRef = useRef([]);
  const messagesRef = useRef(null);
  const messageEndRef = useRef(null);
  const activeRoomIdRef = useRef(activeRoomId);
  const pendingRoomOpenRef = useRef(initialRoomTarget.current?.roomId || null);
  const confirmedReadRef = useRef(new Map());
  const readRequestsRef = useRef(new Map());
  const roomListRequestRef = useRef(null);
  const roomRequests = useRef(null);
  if (!roomRequests.current) roomRequests.current = createRoomRequests(() => activeRoomIdRef.current);
  const mobileRoomHistoryPushedRef = useRef(false);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const noticeFileInputRef = useRef(null);
  const noticeEditorRef = useRef(null);
  const noteFileInputRef = useRef(null);
  const dateJumpInputRef = useRef(null);
  const keepDateJumpTopRef = useRef(false);
  const messageLongPressTimerRef = useRef(null);
  const suppressMessageMenuCloseRef = useRef(false);
  const currentEmployeeId = useMemo(() => getCurrentEmployeeId(), []);
  const uploading = useMemo(() => uploadTasks.some((task) => task.status === 'uploading'), [uploadTasks]);
  const totalUnreadCount = useMemo(() => rooms.reduce((sum, room) => sum + getRoomUnreadCount(room), 0), [rooms]);

  const selfRooms = useMemo(() => rooms.filter((room) => isSelfDirectRoom(room)), [rooms]);

  const roomSections = useMemo(() => {
    const sections = [
      { key: 'direct', title: '1:1 대화', rooms: [] },
      { key: 'group', title: '그룹대화', rooms: [] },
    ];
    const sectionMap = sections.reduce((acc, section) => {
      acc[section.key] = section;
      return acc;
    }, {});

    rooms.forEach((room) => {
      const type = String(room.room_type || '').toUpperCase();
      if (isSelfDirectRoom(room)) return;
      if (type === 'DIRECT') {
        sectionMap.direct.rooms.push(room);
      } else {
        sectionMap.group.rooms.push(room);
      }
    });

    return sections
      .filter((section) => section.rooms.length > 0)
      .map((section) => ({
        ...section,
        unreadTotal: section.rooms.reduce((sum, room) => sum + getRoomUnreadCount(room), 0),
      }));
  }, [rooms]);

  const getTotalUnreadAfterRoomRead = useCallback((roomId) => {
    const targetRoomId = Number(roomId);
    return roomsRef.current.reduce((sum, room) => {
      const unread = getRoomUnreadCount(room);
      return sum + (Number(room.id) === targetRoomId ? 0 : Math.max(0, unread));
    }, 0);
  }, []);

  const toggleRoomSectionCollapsed = useCallback((sectionKey) => {
    setCollapsedRoomSections((prev) => {
      const next = { ...(prev || {}), [sectionKey]: !prev?.[sectionKey] };
      try {
        window.localStorage.setItem('hansero-chat-collapsed-room-sections', JSON.stringify(next));
      } catch (err) {
        // localStorage가 막혀 있어도 화면 동작은 유지한다.
      }
      return next;
    });
  }, []);

  const activeRoom = useMemo(
    () => rooms.find((room) => Number(room.id) === Number(activeRoomId)) || null,
    [rooms, activeRoomId]
  );

  const currentRoomMember = useMemo(
    () => roomMembers.find((member) => Number(member.employee_id) === Number(currentEmployeeId)) || null,
    [roomMembers, currentEmployeeId]
  );
  const currentRoomRole = normalizeMemberRole(currentRoomMember?.member_role || activeRoom?.my_member_role);
  const isCurrentRoomOwner = currentRoomRole === 'OWNER';
  const canManageCurrentRoom = activeRoom && activeRoom.room_type !== 'DIRECT' && isManagerRole(currentRoomRole);
  const canAddMembers = Boolean(canManageCurrentRoom);
  const canViewMembers = Boolean(activeRoom && String(activeRoom.room_type || '').toUpperCase() !== 'DIRECT');
  const isSelfRoom = Boolean(activeRoom && isSelfDirectRoom(activeRoom));

  const activeRoomMemberPreview = useMemo(() => {
    if (!canViewMembers) return [];
    const source = Array.isArray(roomMembers) ? roomMembers : [];
    return source
      .filter((member) => String(member.member_status || 'ACTIVE').toUpperCase() === 'ACTIVE' && !member.deleted_at)
      .slice(0, 8);
  }, [canViewMembers, roomMembers]);

  const activeRoomMemberIdSet = useMemo(() => {
    const ids = new Set();
    (Array.isArray(roomMembers) ? roomMembers : []).forEach((member) => {
      const status = String(member.member_status || 'ACTIVE').toUpperCase();
      if (status !== 'ACTIVE' || member.deleted_at) return;
      const id = Number(member.employee_id || member.id);
      if (Number.isFinite(id) && id > 0) ids.add(id);
    });
    return ids;
  }, [roomMembers]);

  const isAlreadyRoomMember = useCallback((employee) => {
    if (createMode !== 'ADD_MEMBERS') return false;
    const id = Number(employee?.id || employee?.employee_id);
    return Number.isFinite(id) && activeRoomMemberIdSet.has(id);
  }, [activeRoomMemberIdSet, createMode]);

  const activeNotice = useMemo(
    () => roomNotices.find((notice) => !notice.archived_at) || null,
    [roomNotices]
  );

  useEffect(() => {
    setRoomMenuOpen(false);
    setSearchToolsOpen(false);
    setNoticesOpen(false);
    setNotesOpen(false);
    setNoticeText('');
    setNoticeEditMode(false);
    setNoteTitle('');
    setNoteBody('');
    setEditingNoteId(null);
  }, [activeRoomId]);

  useEffect(() => {
    if (createMode !== 'ADD_MEMBERS') return;
    setSelectedMembers((prev) => prev.filter((member) => !activeRoomMemberIdSet.has(Number(member.id || member.employee_id))));
  }, [activeRoomMemberIdSet, createMode]);

  const getUnreadFallbackForOutgoingMessage = useCallback((message = {}) => {
    if (!message?.id) return 0;
    if (Number(message.sender_employee_id) !== Number(currentEmployeeId)) return 0;
    if (message.message_type === 'SYSTEM' || message.deleted_at) return 0;

    const roomId = Number(message.room_id || activeRoomIdRef.current || 0);
    const room = roomsRef.current.find((item) => Number(item.id) === roomId) || activeRoom;

    if (Number(activeRoomIdRef.current) === roomId && Array.isArray(roomMembers) && roomMembers.length > 0) {
      const activeMemberCount = roomMembers.filter((member) => {
        const status = String(member.member_status || 'ACTIVE').toUpperCase();
        return status === 'ACTIVE' && !member.deleted_at && Number(member.employee_id) !== Number(currentEmployeeId);
      }).length;
      return Math.max(0, activeMemberCount);
    }

    const memberCount = Number(room?.member_count || 0);
    return Math.max(0, memberCount - 1);
  }, [activeRoom, currentEmployeeId, roomMembers]);

  const normalizeOutgoingReadCount = useCallback((message = null) => {
    if (!message?.id) return message;
    const hasServerCount = message.unread_count !== undefined && message.unread_count !== null;
    const hasLegacyCount = message.unread_member_count !== undefined && message.unread_member_count !== null;
    if (hasServerCount || hasLegacyCount) return message;
    const fallback = getUnreadFallbackForOutgoingMessage(message);
    return { ...message, unread_count: fallback };
  }, [getUnreadFallbackForOutgoingMessage]);

  useEffect(() => {
    activeRoomIdRef.current = activeRoomId;
  }, [activeRoomId]);

  useEffect(() => () => {
    roomRequests.current.cancelAll();
    roomListRequestRef.current?.abort();
  }, []);

  useEffect(() => {
    const media = window.matchMedia?.(CHAT_MOBILE_MEDIA_QUERY);
    if (!media) return undefined;

    const syncMobileLayout = () => setIsMobileLayout(media.matches);
    syncMobileLayout();

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', syncMobileLayout);
      return () => media.removeEventListener('change', syncMobileLayout);
    }

    media.addListener?.(syncMobileLayout);
    return () => media.removeListener?.(syncMobileLayout);
  }, []);

  useEffect(() => {
    roomsRef.current = rooms;
  }, [rooms]);


  useEffect(() => {
    const close = () => {
      if (suppressMessageMenuCloseRef.current) {
        suppressMessageMenuCloseRef.current = false;
        return;
      }
      setMessageMenu(null);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMessageMenu(null);
    };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onKeyDown);
      if (messageLongPressTimerRef.current) {
        window.clearTimeout(messageLongPressTimerRef.current);
        messageLongPressTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const baseTitle = 'Hansero Chat';
    const count = clampBadgeCount(totalUnreadCount);
    document.title = count > 0 ? `(${count > 99 ? '99+' : count}) ${baseTitle}` : baseTitle;
    updateBrowserBadge(count);
    setChatAppBadge(count).catch(() => {});
    return () => {
      document.title = baseTitle;
      updateBrowserBadge(0);
      setChatAppBadge(0).catch(() => {});
    };
  }, [totalUnreadCount]);

  const scrollToBottom = useCallback((isCurrent) => {
    const roomId = activeRoomIdRef.current;
    const run = () => {
      if (roomId !== activeRoomIdRef.current || (typeof isCurrent === 'function' && !isCurrent())) return;
      const box = messagesRef.current;
      if (box) {
        box.scrollTop = box.scrollHeight;
        return;
      }
      messageEndRef.current?.scrollIntoView({ block: 'end' });
    };

    // 메시지 추가 직후, 말풍선/첨부 썸네일 렌더 직후 모두 맨 아래로 맞춘다.
    // 기존 scrollIntoView 1회만 쓰면 입력/전송 직후 레이아웃이 늦게 늘어나면서
    // 스크롤이 살짝 위에 남는 경우가 있었다.
    requestAnimationFrame(run);
    window.setTimeout(run, 30);
    window.setTimeout(run, 180);
  }, []);

  const scrollToTopStable = useCallback((isCurrent) => {
    const roomId = activeRoomIdRef.current;
    const current = () => roomId === activeRoomIdRef.current && (typeof isCurrent !== 'function' || isCurrent());
    const run = () => {
      if (!current()) return;
      const box = messagesRef.current;
      if (!box) return;
      box.scrollTop = 0;
    };

    // 날짜 이동은 선택 날짜의 첫 대화를 보여줘야 한다.
    // 모바일/PWA와 이미지 썸네일 렌더링에서는 레이아웃이 늦게 바뀌면서
    // 한 번의 scrollTop=0이 무시될 수 있어 짧게 여러 번 보정한다.
    keepDateJumpTopRef.current = true;
    requestAnimationFrame(run);
    window.setTimeout(run, 30);
    window.setTimeout(run, 120);
    window.setTimeout(run, 300);
    window.setTimeout(() => {
      if (current()) keepDateJumpTopRef.current = false;
    }, 700);
  }, []);

  const resetCreatePanel = useCallback(() => {
    setCreateMode(null);
    setUserQuery('');
    setUsers([]);
    setGroupTitle('');
    setSelectedMembers([]);
    setHistoryAll(true);
    setAddMembersHistoryOpen(false);
  }, []);

  const closeMobileRoom = useCallback(() => {
    setSidebarOpen(false);
    setRoomMenuOpen(false);
    setMessageMenu(null);

    if (isMobileChatViewport() && activeRoomIdRef.current && mobileRoomHistoryPushedRef.current && window.history?.back) {
      mobileRoomHistoryPushedRef.current = false;
      window.history.back();
      return;
    }

    roomRequests.current.cancelAll();
    activeRoomIdRef.current = null;
    pendingRoomOpenRef.current = null;
    rememberRoomTarget(null);
    setActiveRoomId(null);
  }, []);

  const openRoom = useCallback((roomId, options = {}) => {
    const normalizedRoomId = positiveId(roomId);
    if (!normalizedRoomId) return;

    const isMobile = isMobileChatViewport();
    if (isMobile && !activeRoomIdRef.current && window.history?.pushState) {
      try {
        window.history.pushState({ hanseroChatRoomOpen: true, roomId: normalizedRoomId }, '', window.location.href);
        mobileRoomHistoryPushedRef.current = true;
      } catch (err) {
        mobileRoomHistoryPushedRef.current = false;
      }
    }

    const sameRoom = Number(activeRoomIdRef.current) === normalizedRoomId;
    if (!sameRoom) {
      roomRequests.current.cancelAll();
      setMessages([]);
      setLoadingMessages(true);
      setRoomMembers([]);
      setRoomNotices([]);
      setRoomNotes([]);
    }
    activeRoomIdRef.current = normalizedRoomId;
    pendingRoomOpenRef.current = options.notification ? normalizedRoomId : null;
    setRoomOpenError('');
    setActiveRoomId(normalizedRoomId);
    if (sameRoom) setRoomRefreshVersion(value => value + 1);
    rememberRoomTarget(normalizedRoomId, {messageId:options.messageId,employeeId:getCurrentEmployeeId()});
    setSidebarOpen(false);
    setCreateMenuOpen(false);
    setRoomMenuOpen(false);

    // 모바일에서는 방 진입 시 입력창에 자동 포커스를 주지 않는다.
    // 자동으로 키보드가 올라오면 카카오톡/라인과 달리 대화 영역이 바로 줄어든다.
    const shouldFocusComposer = options.focus === true || (!isMobile && options.focus !== false);
    if (shouldFocusComposer) {
      window.setTimeout(() => inputRef.current?.focus?.(), 100);
    }
  }, []);

  useEffect(() => {
    const handleBrowserBack = () => {
      if (!isMobileChatViewport() || !activeRoomIdRef.current) return;
      mobileRoomHistoryPushedRef.current = false;
      roomRequests.current.cancelAll();
      activeRoomIdRef.current = null;
      pendingRoomOpenRef.current = null;
      setActiveRoomId(null);
      setSidebarOpen(false);
      setRoomMenuOpen(false);
      setMessageMenu(null);
    };

    window.addEventListener('popstate', handleBrowserBack);
    return () => window.removeEventListener('popstate', handleBrowserBack);
  }, []);

  const openCreatePanel = useCallback((mode) => {
    setCreateMenuOpen(false);
    setCreateMode(mode);
    setUserQuery('');
    setUsers([]);
    setGroupTitle('');
    setSelectedMembers([]);
    setHistoryAll(true);
    if (mode !== 'ADD_MEMBERS') {
      setSidebarOpen(true);
    }
  }, []);

  // 새 채팅 생성은 사이드바에서 유지하고, 기존 방의 참여자 추가는 팝업으로 연다.
  useEffect(() => {
    if (createMode && createMode !== 'ADD_MEMBERS') {
      setSidebarOpen(true);
    }
  }, [createMode]);

  const loadRooms = useCallback(async () => {
    roomListRequestRef.current?.abort();
    const controller = new AbortController();
    roomListRequestRef.current = controller;
    setLoadingRooms(true);
    try {
      const res = await chatApi.get('/rooms', {signal:controller.signal});
      if (controller.signal.aborted || roomListRequestRef.current !== controller) return;
      const activeRoomIdForView = Number(activeRoomIdRef.current || 0);
      const shouldKeepActiveRoomRead = Boolean(
        activeRoomIdForView
        && isChatPageVisible()
        && !jumpDateRef.current
        && !messageSearchAppliedRef.current
      );
      const items = normalizeRooms(res.data?.items).map((room) => {
        const key = Number(room.id);
        confirmedReadRef.current.set(key, Math.max(confirmedReadRef.current.get(key) || 0, Number(room.last_read_message_id || 0)));
        if (shouldKeepActiveRoomRead && Number(room.id) === activeRoomIdForView) {
          // 모바일 앱이 켜진 상태에서 현재 보고 있는 방은 이미 화면에 보이는 대화이므로
          // 서버 방 목록 재조회가 잠깐 늦게 반영되더라도 앱 내부 미확인 숫자는 0으로 유지한다.
          return {
            ...room,
            unread_count: 0,
            unread_member_count: 0,
          };
        }
        return room;
      });
      const pending = pendingRoomOpenRef.current;
      const missingTarget = pending && !items.some(room => Number(room.id) === Number(pending));
      if (missingTarget) setRoomOpenError('참여 중인 채팅방을 찾을 수 없습니다. 방 참여 상태를 확인해 주세요.');
      pendingRoomOpenRef.current = null;
      roomsRef.current = items;
      setRooms(items);
      setActiveRoomId((prev) => {
        if (prev && items.some((room) => Number(room.id) === Number(prev))) return prev;
        if (missingTarget) return null;
        // 모바일은 카카오톡/라인처럼 채팅방 목록을 첫 화면으로 보여주고,
        // 사용자가 방을 누른 뒤에만 대화 화면으로 들어간다.
        return isMobileChatViewport() ? null : (items[0]?.id || null);
      });
    } catch (err) {
      if (controller.signal.aborted || roomListRequestRef.current !== controller) return;
      console.error('[chat] rooms failed', err);
    } finally {
      if (roomListRequestRef.current === controller) setLoadingRooms(false);
    }
  }, []);



  const cycleRoomNotificationMode = useCallback(async (event, room) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const roomId = Number(room?.id || 0);
    if (!roomId) return;
    const payload = hcNotifyNextPayload(room);
    try {
      const res = await chatApi.patch(`/rooms/${roomId}/notification-settings`, payload);
      const next = {
        notification_mode: res.data?.notification_mode || payload.notification_mode,
        vibration_enabled: res.data?.vibration_enabled !== undefined ? res.data.vibration_enabled : payload.vibration_enabled,
        muted_until: res.data?.muted_until || null,
      };
      setRooms((prevRooms) => prevRooms.map((item) => Number(item.id) === roomId ? { ...item, ...next } : item));

      // 방 알림을 켜는 동작은 OS/PWA 푸시 구독까지 같이 보장한다.
      // 기존에는 방 알림 값만 바뀌고 기기 구독이 죽어 있으면 알림이 오지 않았다.
      if (String(next.notification_mode || '').toUpperCase() !== 'OFF') {
        const token = getToken();
        requestAndEnsureDevicePushSubscription({ token }).catch((pushErr) => {
          console.warn('[chat/push] ensure after room notification change failed:', pushErr?.message || pushErr);
        });
      }
    } catch (err) {
      console.error('[chat] notification cycle failed', err);
      alert('알림 설정 저장에 실패했습니다.');
    }
  }, []);


  const testPushNotification = useCallback(async () => {
    const token = getToken();
    if (!token) {
      alert('로그인 정보가 없어 테스트 알림을 보낼 수 없습니다. 다시 로그인 후 시도해 주세요.');
      return;
    }
    if (testingPush) return;
    setTestingPush(true);
    try {
      await requestAndEnsureDevicePushSubscription({ token });
      const test = await sendPushTest({ token });
      if (test?.ok) {
        alert('테스트 알림을 발송했습니다. 휴대폰 알림이 표시되는지 확인해 주세요.');
      } else {
        alert(`테스트 알림 발송에 실패했습니다. (${test?.reason || test?.error || 'UNKNOWN'})`);
      }
    } catch (err) {
      console.error('[chat/push] test failed', err);
      alert(`테스트 알림 발송 중 오류가 발생했습니다. ${err?.message || err}`);
    } finally {
      setTestingPush(false);
    }
  }, [testingPush]);

  const repairPushSubscription = useCallback(async () => {
    const token = getToken();
    if (!token) {
      alert('로그인 정보가 없어 알림을 복구할 수 없습니다. 다시 로그인 후 시도해 주세요.');
      return;
    }
    if (repairingPush) return;
    setRepairingPush(true);
    try {
      const result = await resetDevicePushSubscription({ token });
      if (!result?.ok) {
        const reason = result?.reason || 'UNKNOWN';
        alert(`알림 복구에 실패했습니다. (${reason})\n휴대폰/브라우저 알림 권한을 허용한 뒤 다시 시도해 주세요.`);
        return;
      }
      const test = await sendPushTest({ token });
      if (test?.ok) {
        alert('알림 구독을 새로 등록했고, 테스트 알림을 발송했습니다. 휴대폰 알림이 표시되는지 확인해 주세요.');
      } else {
        alert(`알림 구독은 등록됐지만 테스트 발송 확인에 실패했습니다. (${test?.reason || test?.error || 'UNKNOWN'})`);
      }
    } catch (err) {
      console.error('[chat/push] repair failed', err);
      alert(`알림 복구 중 오류가 발생했습니다. ${err?.message || err}`);
    } finally {
      setRepairingPush(false);
    }
  }, [repairingPush]);

  const markRoomRead = useCallback(async (roomId, items) => {
    if (!roomId || !Array.isArray(items) || !items.length) return;

    const lastMessageId = Number(items[items.length - 1]?.id || 0);
    if (!lastMessageId) return;

    // 읽음 처리는 화면 리프레시 없이 로컬 상태만 먼저 보정합니다.
    // 여기서 loadMessages/loadRooms를 호출하면 스크롤이 튀거나 날짜 이동 위치가 깨집니다.
    setRooms((prev) => prev.map((room) => (
      Number(room.id) === Number(roomId)
        ? {
            ...room,
            unread_count: 0,
            unread_member_count: 0,
            last_read_message_id: Math.max(Number(room.last_read_message_id || 0), lastMessageId),
            last_read_at: new Date().toISOString(),
          }
        : room
    )));

    // 모바일/PWA OS 알림과 앱 아이콘 배지는 DB 읽음 처리와 별개로 남을 수 있으므로
    // 현재 방을 읽음 처리하는 즉시 해당 방 알림을 닫고 배지를 정리한다.
    clearChatNotifications({ roomId, badgeCount: getTotalUnreadAfterRoomRead(roomId) }).catch(() => {});

    let job;
    try {
      const key = Number(roomId);
      if ((confirmedReadRef.current.get(key) || 0) >= lastMessageId) return;
      const pending = readRequestsRef.current.get(key);
      if (pending && pending.lastId >= lastMessageId) { await pending.promise; return; }
      job = {lastId:lastMessageId,promise:chatApi.patch(`/rooms/${roomId}/read`, { message_id: lastMessageId })};
      readRequestsRef.current.set(key, job);
      await job.promise;
      confirmedReadRef.current.set(key, Math.max(confirmedReadRef.current.get(key) || 0, lastMessageId));
      clearChatNotifications({ roomId, badgeCount: getTotalUnreadAfterRoomRead(roomId) }).catch(() => {});
    } catch (err) {
      console.warn('[chat] mark read failed', err?.message || err);
    } finally {
      if (job && readRequestsRef.current.get(Number(roomId)) === job) readRequestsRef.current.delete(Number(roomId));
    }
  }, [getTotalUnreadAfterRoomRead]);


  // Socket.IO 이벤트 핸들러가 방 목록/멤버/읽음 상태 변경 때마다 재등록되면
  // 모바일에서 순간적으로 "io client disconnect" 같은 내부 메시지가 화면에 노출될 수 있다.
  // 변동되는 함수/상태는 ref로 최신값만 보관하고, 소켓 연결 effect는 재실행하지 않게 고정한다.
  const loadRoomsRef = useRef(loadRooms);
  const markRoomReadRef = useRef(markRoomRead);
  const normalizeOutgoingReadCountRef = useRef(normalizeOutgoingReadCount);
  const scrollToBottomRef = useRef(scrollToBottom);
  const jumpDateRef = useRef(jumpDate);
  const messageSearchAppliedRef = useRef(messageSearchApplied);

  useEffect(() => { loadRoomsRef.current = loadRooms; }, [loadRooms]);
  useEffect(() => { markRoomReadRef.current = markRoomRead; }, [markRoomRead]);
  useEffect(() => { normalizeOutgoingReadCountRef.current = normalizeOutgoingReadCount; }, [normalizeOutgoingReadCount]);
  useEffect(() => { scrollToBottomRef.current = scrollToBottom; }, [scrollToBottom]);
  useEffect(() => { jumpDateRef.current = jumpDate; }, [jumpDate]);
  useEffect(() => { messageSearchAppliedRef.current = messageSearchApplied; }, [messageSearchApplied]);

  const scheduleRoomListRefresh = useCallback((delay = 500) => {
    if (roomListRefreshTimerRef.current) {
      window.clearTimeout(roomListRefreshTimerRef.current);
      roomListRefreshTimerRef.current = null;
    }
    roomListRefreshTimerRef.current = window.setTimeout(() => {
      roomListRefreshTimerRef.current = null;
      loadRoomsRef.current?.();
    }, Math.max(0, Number(delay) || 0));
  }, []);

  useEffect(() => () => {
    if (roomListRefreshTimerRef.current) {
      window.clearTimeout(roomListRefreshTimerRef.current);
      roomListRefreshTimerRef.current = null;
    }
  }, []);

  // 모바일 PWA가 백그라운드에서 다시 열릴 때는 OS 뱃지와 앱 내부 방 목록이
  // 어긋날 수 있으므로 최신 미확인 수를 다시 조회합니다.
  useEffect(() => {
    const refreshRoomsWhenVisible = () => {
      if (isChatPageVisible()) loadRoomsRef.current?.();
    };

    document.addEventListener('visibilitychange', refreshRoomsWhenVisible);
    window.addEventListener('focus', refreshRoomsWhenVisible);
    return () => {
      document.removeEventListener('visibilitychange', refreshRoomsWhenVisible);
      window.removeEventListener('focus', refreshRoomsWhenVisible);
    };
  }, []);

  // 휴대폰에서 앱이 켜져 있는 동안에는 Service Worker 푸시가 OS 뱃지만 갱신하고
  // React 방 목록은 그대로 남을 수 있다. 푸시/알림 클릭 메시지를 받으면 방 목록을
  // 즉시 재조회해 앱 내부 미확인 숫자를 OS 뱃지와 맞춘다.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) return undefined;

    const handleServiceWorkerMessage = (event) => {
      const data = event?.data || {};
      const type = String(data.type || '');
      if (type === 'CHAT_PUSH_OPEN') {
        const reply = payload => { try { event.ports?.[0]?.postMessage({open_id:data.open_id,...payload}); } catch (_) {} };
        const employeeId = getCurrentEmployeeId();
        if (employeeId !== currentEmployeeId) { reply({ok:false,reason:'RELOAD_REQUIRED'}); return; }
        const target = roomOpenTarget(data, employeeId);
        if (!target) { reply({ok:false,reason:'ACCOUNT_MISMATCH'}); return; }
        openRoom(target.roomId, {focus:false,notification:true,refresh:true,messageId:target.messageId});
        reply({ok:true,room_id:target.roomId});
      }
      if (type === 'CHAT_PUSH_MESSAGE' || type === 'CHAT_PUSH_OPEN' || type === 'CHAT_BADGE_UPDATED') {
        scheduleRoomListRefresh(type === 'CHAT_PUSH_MESSAGE' ? 200 : 0);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
  }, [currentEmployeeId, openRoom, scheduleRoomListRefresh]);

  const loadMessages = useCallback(async (roomId, options = {}) => {
    const targetDate = String(options.date || '').trim();
    const searchText = String(options.q || '').trim();
    if (!roomId) {
      setMessages([]);
      setLoadingMessages(false);
      setHasOlderMessages(false);
      setHasNewerMessages(false);
      setDateJumpNotice('');
      return;
    }
    const request = roomRequests.current.begin('messages', roomId);
    setLoadingMessages(true);
    setLoadingOlderMessages(false);
    setLoadingNewerMessages(false);
    try {
      const params = { limit: MESSAGE_PAGE_SIZE };
      if (targetDate) params.date = targetDate;
      if (searchText) params.q = searchText;

      const res = await chatApi.get(`/rooms/${roomId}/messages`, { params, signal:request.signal });
      if (!request.current()) return;
      setRoomOpenError('');
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      const roomSnapshot = roomsRef.current.find((room) => Number(room.id) === Number(roomId));
      const previousLastReadId = Number(roomSnapshot?.last_read_message_id || 0);
      const hasUnreadInView = previousLastReadId > 0 && items.some((item) =>
        Number(item.id) > previousLastReadId && getMessageMetadata(item).source !== 'LINE_HISTORY_BACKFILL');
      setReadMarkerMessageId(!targetDate && !searchText && hasUnreadInView ? previousLastReadId : null);
      setMessages(items);
      setHasOlderMessages(Boolean(res.data?.has_older ?? res.data?.has_more));
      setHasNewerMessages(Boolean(res.data?.has_newer));
      if (searchText && targetDate) {
        setDateJumpNotice(`${formatDateInputLabel(targetDate)} 이후 대화 중 “${searchText}” 검색 결과입니다.`);
      } else if (searchText) {
        setDateJumpNotice(`“${searchText}” 검색 결과입니다.`);
      } else {
        setDateJumpNotice(targetDate ? `${formatDateInputLabel(targetDate)} 첫 대화로 이동했습니다.` : '');
      }
      setLoadingMessages(false);
      // Read receipts must never keep already-loaded messages behind a spinner.
      // Start them after React has committed the conversation to the screen.
      requestAnimationFrame(() => {
        if (!request.current()) return;
        if (targetDate) scrollToTopStable(request.current); else scrollToBottom(request.current);
        if (isChatPageVisible()) markRoomRead(roomId, items);
      });
    } catch (err) {
      if (!request.current()) return;
      console.error('[chat] messages failed', err);
      setMessages([]);
      setHasOlderMessages(false);
      setHasNewerMessages(false);
      setDateJumpNotice('');
      setRoomOpenError(err?.response?.status === 403 ? '이 채팅방에 참여할 수 없습니다.' : '대화를 불러오지 못했습니다. 방을 다시 열어 주세요.');
    } finally {
      if (request.current()) setLoadingMessages(false);
    }
  }, [markRoomRead, scrollToBottom, scrollToTopStable]);

  const loadOlderMessages = useCallback(async () => {
    if (!activeRoomId || loadingOlderMessages || loadingMessages || !hasOlderMessages || !messages.length) return;
    const box = messagesRef.current;
    const prevHeight = box?.scrollHeight || 0;
    const beforeId = messages[0]?.id;
    if (!beforeId) return;

    const request = roomRequests.current.begin('older', activeRoomId);
    setLoadingOlderMessages(true);
    try {
      const res = await chatApi.get(`/rooms/${activeRoomId}/messages`, {
        signal:request.signal,
        params: {
          limit: MESSAGE_PAGE_SIZE,
          before_id: beforeId,
          ...(messageSearchApplied ? { q: messageSearchApplied } : {}),
        },
      });
      if (!request.current()) return;
      const older = Array.isArray(res.data?.items) ? res.data.items : [];
      setHasOlderMessages(Boolean(res.data?.has_older ?? res.data?.has_more) || older.length >= MESSAGE_PAGE_SIZE);
      setMessages((prev) => uniqueById([...older, ...prev]));
      requestAnimationFrame(() => {
        if (!box || !request.current()) return;
        const nextHeight = box.scrollHeight || 0;
        box.scrollTop = nextHeight - prevHeight;
      });
    } catch (err) {
      if (!request.current()) return;
      console.error('[chat] older messages failed', err);
    } finally {
      if (request.current()) setLoadingOlderMessages(false);
    }
  }, [activeRoomId, hasOlderMessages, jumpDate, loadingMessages, loadingOlderMessages, messageSearchApplied, messages]);


  const loadNewerMessages = useCallback(async () => {
    if (!activeRoomId || loadingNewerMessages || loadingMessages || !hasNewerMessages || !messages.length) return;
    const afterId = messages[messages.length - 1]?.id;
    if (!afterId) return;

    const request = roomRequests.current.begin('newer', activeRoomId);
    setLoadingNewerMessages(true);
    try {
      const res = await chatApi.get(`/rooms/${activeRoomId}/messages`, {
        signal:request.signal,
        params: {
          limit: MESSAGE_PAGE_SIZE,
          after_id: afterId,
          ...(messageSearchApplied ? { q: messageSearchApplied } : {}),
        },
      });
      if (!request.current()) return;
      const newer = Array.isArray(res.data?.items) ? res.data.items : [];
      setHasNewerMessages(Boolean(res.data?.has_newer) || newer.length >= MESSAGE_PAGE_SIZE);
      setMessages((prev) => uniqueById([...prev, ...newer]));
      setReplyTarget(null);
      scrollToBottom(request.current);
    } catch (err) {
      if (!request.current()) return;
      console.error('[chat] newer messages failed', err);
    } finally {
      if (request.current()) setLoadingNewerMessages(false);
    }
  }, [activeRoomId, hasNewerMessages, jumpDate, loadingMessages, loadingNewerMessages, messageSearchApplied, messages, scrollToBottom]);

  const getCurrentJumpDate = useCallback(() => {
    const domValue = dateJumpInputRef.current?.value || '';
    return getSafeJumpDate(domValue || jumpDate);
  }, [jumpDate]);

  const jumpToDate = useCallback(async () => {
    if (!activeRoomId) return;

    const safeDate = getCurrentJumpDate();
    if (!safeDate) {
      alert('이동할 날짜를 선택하세요.');
      return;
    }

    setJumpDate(safeDate);
    setDateJumping(true);
    try {
      await loadMessages(activeRoomId, { date: safeDate, q: messageSearchApplied });
    } finally {
      setDateJumping(false);
    }
  }, [activeRoomId, getCurrentJumpDate, loadMessages, messageSearchApplied]);

  const backToLatestMessages = useCallback(async () => {
    if (!activeRoomId) return;
    setJumpDate('');
    setDateJumpNotice('');
    setMessageSearchApplied('');
    setMessageSearchText('');
    await loadMessages(activeRoomId);
  }, [activeRoomId, loadMessages]);

  const searchMessages = useCallback(async () => {
    if (!activeRoomId) return;
    const q = messageSearchText.trim();
    const currentDate = getCurrentJumpDate();
    if (!q) {
      setMessageSearchApplied('');
      await loadMessages(activeRoomId, currentDate ? { date: currentDate } : {});
      setSearchToolsOpen(false);
      return;
    }
    setSearchingMessages(true);
    try {
      setMessageSearchApplied(q);
      await loadMessages(activeRoomId, { q, date: currentDate || '' });
      setSearchToolsOpen(false);
    } finally {
      setSearchingMessages(false);
    }
  }, [activeRoomId, getCurrentJumpDate, loadMessages, messageSearchText]);

  const clearMessageSearch = useCallback(async () => {
    if (!activeRoomId) return;
    const currentDate = getCurrentJumpDate();
    setMessageSearchText('');
    setMessageSearchApplied('');
    await loadMessages(activeRoomId, currentDate ? { date: currentDate } : {});
  }, [activeRoomId, getCurrentJumpDate, loadMessages]);

  const loadRoomMembers = useCallback(async () => {
    if (!activeRoomId) return;
    const request = roomRequests.current.begin('members', activeRoomId);
    setLoadingRoomMembers(true);
    try {
      const res = await chatApi.get(`/rooms/${activeRoomId}/members`, {signal:request.signal});
      if (!request.current()) return;
      setRoomMembers(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (err) {
      if (!request.current()) return;
      console.error('[chat] room members failed', err);
      setRoomMembers([]);
    } finally {
      if (request.current()) setLoadingRoomMembers(false);
    }
  }, [activeRoomId]);


  const openExportPanel = useCallback(() => {
    if (!activeRoomId) return;

    // 백그라운드 내보내기 작업이 살아 있으면 패널을 다시 여는 동작만 한다.
    // 닫기/숨기기를 눌러도 작업 상태는 유지되어야 한다.
    if (exportJobId || exportingRoom || exportProgress) {
      setExportOpen(true);
      return;
    }

    const today = new Date();
    const defaultDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    setExportStartDate((prev) => prev || getSafeJumpDate(jumpDate) || defaultDate);
    setExportEndDate((prev) => prev || getSafeJumpDate(jumpDate) || defaultDate);
    setExportAllDates(false);
    setExportIncludeAttachments(true);
    setExportRoomId(activeRoomId);
    setExportRoomTitle(activeRoom ? getRoomTitle(activeRoom) : '');
    setExportOpen(true);
  }, [activeRoom, activeRoomId, exportJobId, exportProgress, exportingRoom, jumpDate]);

  const triggerExportDownload = useCallback((job) => {
    const jobId = job?.job_id || exportJobId;
    if (!jobId) return false;
    const token = getToken();
    if (!token) return false;
    const baseUrl = String(chatApi?.defaults?.baseURL || '/chat-api/chat').replace(/\/$/, '');
    const url = `${baseUrl}/export-jobs/${encodeURIComponent(jobId)}/download?download_token=${encodeURIComponent(token)}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = job?.file_name || 'HanseroChat_backup.zip';
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    return true;
  }, [exportJobId]);

  const downloadRoomExport = useCallback(async () => {
    if (!activeRoomId || exportingRoom) return;
    const start = getSafeJumpDate(exportStartDate);
    const end = getSafeJumpDate(exportEndDate);
    if (!exportAllDates) {
      if (!start || !end) {
        alert('내보낼 시작일과 종료일을 선택하세요.');
        return;
      }
      if (start > end) {
        alert('종료일은 시작일보다 빠를 수 없습니다.');
        return;
      }
    }

    setExportingRoom(true);
    setExportRoomId(activeRoomId);
    setExportRoomTitle(activeRoom ? getRoomTitle(activeRoom) : '');
    setExportProgress({ status: 'QUEUED', phase: '준비', percent: 0, message: '작업을 준비 중입니다.' });
    setExportJobId(null);
    try {
      const res = await chatApi.post(`/rooms/${activeRoomId}/export-jobs`, {
        all: exportAllDates ? 1 : 0,
        start_date: exportAllDates ? undefined : start,
        end_date: exportAllDates ? undefined : end,
        include_attachments: exportIncludeAttachments ? 1 : 0,
      });
      const job = res.data?.job || null;
      if (!job?.job_id) throw new Error('EXPORT_JOB_ID_MISSING');
      setExportJobId(job.job_id);
      setExportProgress(job);
    } catch (err) {
      console.error('[chat] export job create failed', err);
      setExportingRoom(false);
      setExportJobId(null);
      setExportProgress({
        status: 'FAILED',
        phase: '실패',
        percent: 100,
        message: '백업 작업을 시작하지 못했습니다.',
      });
    }
  }, [activeRoom, activeRoomId, exportAllDates, exportEndDate, exportIncludeAttachments, exportStartDate, exportingRoom]);

  useEffect(() => {
    if (!exportJobId) return undefined;
    let stopped = false;
    let timer = null;

    const poll = async () => {
      try {
        const res = await chatApi.get(`/export-jobs/${exportJobId}`);
        if (stopped) return;
        const job = res.data?.job || null;
        if (job) setExportProgress(job);

        if (job?.status === 'COMPLETED') {
          if (!stopped) {
            const started = triggerExportDownload(job);
            setExportProgress({
              ...job,
              message: started
                ? '완료되었습니다. 다운로드가 시작됩니다.'
                : '완료되었습니다. 다시 다운로드를 눌러 저장하세요.',
            });
            setExportingRoom(false);
            // 완료된 작업은 닫기/숨기기 후에도 다시 열어 상태 확인 및 재다운로드할 수 있게 보존한다.
            setExportJobId(job.job_id || exportJobId);
          }
          return;
        }

        if (job?.status === 'FAILED') {
          if (!stopped) {
            setExportingRoom(false);
            setExportProgress({
              ...job,
              status: 'FAILED',
              message: job.error ? `실패: ${job.error}` : '백업에 실패했습니다.',
            });
            setExportJobId(job.job_id || exportJobId);
          }
          return;
        }

        if (job?.status === 'CANCELLED') {
          if (!stopped) {
            setExportingRoom(false);
            setExportProgress({
              ...job,
              status: 'CANCELLED',
              phase: job.phase || '중지됨',
              message: job.message || '백업을 중지했습니다.',
            });
            setExportJobId(job.job_id || exportJobId);
          }
          return;
        }

        timer = window.setTimeout(poll, 1000);
      } catch (err) {
        console.error('[chat] export status failed', err);
        timer = window.setTimeout(poll, 2000);
      }
    };

    poll();
    return () => {
      stopped = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [exportJobId, triggerExportDownload]);

  const hideExportPanel = useCallback(() => {
    setExportOpen(false);
  }, []);

  const resetExportPanel = useCallback(() => {
    setExportingRoom(false);
    setExportJobId(null);
    setExportProgress(null);
    setExportRoomId(null);
    setExportRoomTitle('');
  }, []);

  const cancelExportJob = useCallback(async () => {
    if (!exportJobId) return;
    try {
      const res = await chatApi.post(`/export-jobs/${exportJobId}/cancel`);
      const job = res.data?.job || null;
      setExportingRoom(false);
      if (job) {
        setExportProgress({
          ...job,
          status: 'CANCELLED',
          phase: job.phase || '중지됨',
          message: job.message || '백업을 중지했습니다.',
        });
        setExportJobId(job.job_id || exportJobId);
      } else {
        setExportProgress((prev) => ({
          ...(prev || {}),
          status: 'CANCELLED',
          phase: '중지됨',
          message: '백업을 중지했습니다.',
        }));
      }
    } catch (err) {
      console.error('[chat] export cancel failed', err);
      setExportProgress((prev) => ({
        ...(prev || {}),
        message: '중지 요청에 실패했습니다. 잠시 후 다시 시도하세요.',
      }));
    }
  }, [exportJobId]);

  const exportStatusText = useMemo(() => {
    if (!exportProgress) return '';
    const status = String(exportProgress.status || '').toUpperCase();
    const percent = Math.round(Number(exportProgress.percent || 0));
    if (status === 'COMPLETED') return '백업 완료';
    if (status === 'FAILED') return '백업 실패';
    if (status === 'CANCELLED') return '백업 중지됨';
    return `백업 ${percent}%`;
  }, [exportProgress]);

  const openMembersPanel = useCallback(async () => {
    setMembersOpen(true);
    await loadRoomMembers();
  }, [loadRoomMembers]);

  useEffect(() => {
    loadRooms();
  }, [loadRooms]);

  useEffect(() => {
    if (!isMobileLayout && !activeRoomIdRef.current && roomsRef.current.length > 0) {
      setActiveRoomId(roomsRef.current[0]?.id || null);
      return;
    }
    if (isMobileLayout && !activeRoomIdRef.current) {
      setSidebarOpen(false);
    }
  }, [isMobileLayout]);

  useEffect(() => {
    keepDateJumpTopRef.current = false;
    setJumpDate('');
    setDateJumpNotice('');
    setMessageSearchText('');
    setMessageSearchApplied('');
    setReplyTarget(null);
    setReadMarkerMessageId(null);
    setMembersOpen(false);
    setRoomMembers([]);
    setLoadingRoomMembers(false);
    setLoadingOlderMessages(false);
    setLoadingNewerMessages(false);
    setExportOpen(false);
    setExportStartDate('');
    setExportEndDate('');
    loadMessages(activeRoomId);
    // Give the conversation request first access to the connection/DB pool.
    const memberTimer = activeRoomId ? window.setTimeout(loadRoomMembers, 80) : null;
    if (activeRoomId && socketRef.current?.connected) {
      socketRef.current.emit('chat:room:join', { room_id: activeRoomId });
    }
    return () => { window.clearTimeout(memberTimer); roomRequests.current.cancelAll(); };
  }, [activeRoomId, roomRefreshVersion, loadMessages, loadRoomMembers]);

  useEffect(() => {
    const token = getToken();
    if (!token) return undefined;

    const socket = io(window.location.origin, {
      path: CHAT_SOCKET_PATH,
      transports: ['websocket', 'polling'],
      auth: { token },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    const clearSocketErrorTimer = () => {
      if (socketConnectErrorTimerRef.current) {
        window.clearTimeout(socketConnectErrorTimerRef.current);
        socketConnectErrorTimerRef.current = null;
      }
    };

    const markSocketOnline = () => {
      socketHealthyRef.current = true;
      clearSocketErrorTimer();
      setSocketState('online');
      setSocketHint('');
    };

    socket.on('connect', markSocketOnline);
    socket.on('disconnect', (reason) => {
      socketHealthyRef.current = false;
      clearSocketErrorTimer();
      const text = String(reason || '').trim();
      if (text && !['io client disconnect', 'transport close', 'ping timeout'].includes(text)) {
        console.warn('[chat] socket disconnected:', text);
      }
      setSocketState('offline');
      setSocketHint('');
    });
    socket.on('connect_error', (err) => {
      const msg = err?.message || String(err || 'socket connect error');
      console.warn('[chat] socket connect_error', msg);

      // 모바일 PWA에서는 WebSocket/Polling 전환 중 일시적인 connect_error가
      // 실제 채팅 송수신과 무관하게 발생할 수 있다. 이미 연결됐거나 곧 재연결될
      // 수 있으므로 즉시 "연결 확인 필요" 상태로 고정하지 않고 잠시 대기한다.
      if (socket.connected || socketHealthyRef.current) {
        setSocketState('online');
        setSocketHint('');
        return;
      }

      clearSocketErrorTimer();
      socketConnectErrorTimerRef.current = window.setTimeout(() => {
        if (!socket.connected && !socketHealthyRef.current) {
          setSocketState('error');
          setSocketHint('실시간 연결을 다시 시도하는 중입니다.');
        }
      }, 7000);
    });
    socket.on('chat:connected', () => {
      markSocketOnline();
      if (activeRoomIdRef.current) socket.emit('chat:room:join', { room_id: activeRoomIdRef.current });
    });
    socket.on('chat:room:updated', () => {
      loadRoomsRef.current?.();
    });
    socket.on('chat:member:removed', (payload = {}) => {
      const roomId = Number(payload.room_id);
      const employeeId = Number(payload.employee_id);
      if (employeeId === Number(currentEmployeeId) && Number(activeRoomIdRef.current) === roomId) {
        setActiveRoomId(null);
        setMembersOpen(false);
        setMessages([]);
        alert('이 채팅방에서 내보내졌습니다.');
      }
      loadRoomsRef.current?.();
    });
    socket.on('chat:message:deleted', (payload = {}) => {
      const roomId = Number(payload.room_id);
      const messageId = Number(payload.message_id);
      if (!messageId) return;
      if (Number(activeRoomIdRef.current) === roomId) {
        const deletedAt = payload.deleted_at || new Date().toISOString();
        setMessages((prev) => prev.map((item) => (Number(item.id) === messageId
          ? { ...item, deleted_at: item.deleted_at || deletedAt, body: '', attachments: [] }
          : updateLineReplyFromEvent(item,payload,true))));
      }
      loadRoomsRef.current?.();
    });
    socket.on('chat:message:edited', (payload = {}) => {
      const roomId = Number(payload.room_id);
      const messageId = Number(payload.message_id);
      if (!messageId) return;
      if (Number(activeRoomIdRef.current) === roomId) {
        setMessages((prev) => prev.map((item) => Number(item.id) === messageId && !item.deleted_at
          ? { ...item, body: payload.body, edited_at: new Date().toISOString() } : updateLineReplyFromEvent(item,payload,false)));
      }
      loadRoomsRef.current?.();
    });

    socket.on('chat:room:read', (payload = {}) => {
      const roomId = Number(payload.room_id);
      const readerId = Number(payload.employee_id);
      const lastReadId = Number(payload.last_read_message_id || 0);
      if (!roomId || !readerId || !lastReadId) return;

      // 같은 read 이벤트가 user room + chat room 양쪽에서 중복으로 올 수 있어 한 번만 처리합니다.
      const eventKey = `${roomId}:${readerId}:${lastReadId}`;
      if (readEventSeenRef.current.has(eventKey)) return;
      readEventSeenRef.current.add(eventKey);
      if (readEventSeenRef.current.size > 500) {
        readEventSeenRef.current = new Set(Array.from(readEventSeenRef.current).slice(-200));
      }

      // 내가 읽은 이벤트: 방 목록의 unread_count만 0으로 보정합니다.
      // 메시지 목록 재조회/스크롤 이동은 하지 않습니다.
      if (readerId === Number(currentEmployeeId)) {
        setRooms((prev) => prev.map((room) => (
          Number(room.id) === roomId
            ? {
                ...room,
                unread_count: 0,
                unread_member_count: 0,
                last_read_message_id: Math.max(Number(room.last_read_message_id || 0), lastReadId),
                last_read_at: payload.read_at || new Date().toISOString(),
              }
            : room
        )));
        return;
      }

      // 다른 사람이 읽은 이벤트: 현재 열려 있는 방에서 내가 보낸 메시지의 숫자만 감소시킵니다.
      if (Number(activeRoomIdRef.current) === roomId) {
        setMessages((prev) => prev.map((item) => {
          if (Number(item.sender_employee_id) !== Number(currentEmployeeId)) return item;
          if (Number(item.id) > lastReadId) return item;

          const currentUnread = Number(item.unread_count || item.unread_member_count || 0);
          if (currentUnread <= 0) return item;

          return {
            ...item,
            unread_count: Math.max(0, currentUnread - 1),
            unread_member_count: Math.max(0, currentUnread - 1),
          };
        }));
      }

      // 방 목록은 전체 reload하지 않고, 현재 방이면 unread_count를 유지/보정만 합니다.
      // 상대방이 읽은 이벤트이므로 내 unread_count에는 영향이 없습니다.
    });

    socket.on('chat:message:new', async (payload) => {
      markSocketOnline();
      const msg = normalizeOutgoingReadCountRef.current(normalizeMessage(payload));
      if (!msg?.id) return;
      const roomId = Number(msg.room_id);
      const isActive = Number(activeRoomIdRef.current) === roomId;
      const isMine = Number(msg.sender_employee_id) === Number(currentEmployeeId);
      const shouldReadImmediately = isActive && isChatPageVisible();

      if (isActive) {
        setMessages((prev) => uniqueById([...prev, msg]));
        if (shouldReadImmediately && !jumpDateRef.current && !messageSearchAppliedRef.current) {
          scrollToBottomRef.current?.();
        }
        if (shouldReadImmediately) {
          await markRoomReadRef.current?.(roomId, [msg]);
        }
      }

      setRooms((prev) => prev.map((rawRoom) => {
        const room = normalizeRoom(rawRoom);
        if (Number(room.id) !== roomId) return room;
        const serverUnread = getRoomUnreadCount(msg);
        const nextUnread = shouldReadImmediately || isMine ? 0 : (serverUnread > 0 ? serverUnread : getRoomUnreadCount(room) + 1);
        return {
          ...room,
          last_message_body: msg.body,
          last_message_type: msg.message_type,
          last_message_at: msg.created_at,
          last_sender_name: msg.sender_name,
          unread_count: nextUnread,
          unread_member_count: nextUnread,
        };
      }));

      // 모바일 앱이 켜져 있을 때는 소켓 이벤트만으로 로컬 unread가 누락되는
      // 경우가 있어, 다른 방의 새 메시지는 짧게 지연 후 서버 기준 방 목록을 재조회한다.
      // 단, 현재 열어보고 있는 방의 새 메시지는 즉시 읽음 처리했으므로 재조회로 unread가 다시 뜨지 않게 제외한다.
      if (!isMine && !(isActive && shouldReadImmediately)) scheduleRoomListRefresh(700);
    });

    return () => {
      clearSocketErrorTimer();
      socketHealthyRef.current = false;
      socket.disconnect();
      socketRef.current = null;
    };
  }, [currentEmployeeId, scheduleRoomListRefresh]);

  const searchUsers = useCallback(async (q) => {
    setUserQuery(q);
    if (!q.trim()) {
      setUsers([]);
      return;
    }
    try {
      const res = await chatApi.get('/users', { params: { q, limit: 50 } });
      setUsers(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (err) {
      console.error('[chat] users failed', err);
      setUsers([]);
    }
  }, []);

  const openDirectRoom = async (employeeId) => {
    try {
      const res = await chatApi.post('/rooms/direct', { employee_id: employeeId });
      const roomId = res.data?.room?.id || res.data?.room_id;
      resetCreatePanel();
      await loadRooms();
      if (roomId) openRoom(roomId);
    } catch (err) {
      console.error('[chat] direct room failed', err);
      alert('1:1 채팅방 생성에 실패했습니다.');
    }
  };

  const openSelfRoom = async () => {
    setCreateMenuOpen(false);
    if (!currentEmployeeId) return;
    await openDirectRoom(currentEmployeeId);
  };

  const openRenameRoom = () => {
    if (!activeRoom || !canRenameRoom(activeRoom) || !canManageCurrentRoom) return;
    setRenameTitle(getRoomTitle(activeRoom));
    setRenameOpen(true);
  };

  const saveRoomTitle = async () => {
    const title = String(renameTitle || '').trim();
    if (!activeRoomId || !activeRoom || !canRenameRoom(activeRoom) || !canManageCurrentRoom) return;
    if (!title) {
      alert('채팅방 이름을 입력하세요.');
      return;
    }
    setRenamingRoom(true);
    try {
      await chatApi.patch(`/rooms/${activeRoomId}/title`, { title });
      setRooms((prev) => prev.map((room) => Number(room.id) === Number(activeRoomId) ? { ...room, title } : room));
      setRenameOpen(false);
      await loadRooms();
    } catch (err) {
      console.error('[chat] rename room failed', err);
      alert('채팅방 이름 수정에 실패했습니다. 방장 또는 관리자만 수정할 수 있습니다.');
    } finally {
      setRenamingRoom(false);
    }
  };

  const leaveCurrentRoom = async () => {
    if (!activeRoomId || !activeRoom) return;
    if (isSelfDirectRoom(activeRoom)) {
      alert('나와의 채팅은 나갈 수 없습니다. 개인 메모용 대화방으로 유지됩니다.');
      return;
    }
    const title = getRoomTitle(activeRoom);
    const isDirect = activeRoom.room_type === 'DIRECT';
    const message = isDirect
      ? `'${title}' 대화를 내 목록에서 숨길까요? 상대방의 대화 내용은 삭제되지 않습니다.`
      : `'${title}' 채팅방에서 나가시겠습니까?`;
    if (!window.confirm(message)) return;

    setLeavingRoom(true);
    try {
      await chatApi.delete(`/rooms/${activeRoomId}/leave`);
      setActiveRoomId(null);
      setMessages([]);
      await loadRooms();
    } catch (err) {
      console.error('[chat] leave room failed', err);
      const code = err?.response?.data?.error;
      if (code === 'OWNER_LEAVE_TRANSFER_REQUIRED') {
        alert('방장은 다른 참여자에게 방장을 위임한 뒤 나갈 수 있습니다. 참여자 화면에서 방장을 위임하세요.');
      } else {
        alert('채팅방 나가기에 실패했습니다.');
      }
    } finally {
      setLeavingRoom(false);
    }
  };

  const deleteCurrentRoom = async () => {
    if (!activeRoomId || !activeRoom) return;
    if (activeRoom.room_type === 'DIRECT') {
      alert('1:1 대화는 삭제 대신 나가기로 내 목록에서 숨길 수 있습니다.');
      return;
    }
    if (!isCurrentRoomOwner) {
      alert('방장만 채팅방을 삭제할 수 있습니다.');
      return;
    }
    const title = getRoomTitle(activeRoom);
    if (!window.confirm(`'${title}' 채팅방을 삭제할까요?\n\n대화는 즉시 목록에서 사라지며, 감사/보관 목적의 데이터는 DB에 보관됩니다.`)) return;

    setDeletingRoom(true);
    try {
      await chatApi.delete(`/rooms/${activeRoomId}`);
      setActiveRoomId(null);
      setMessages([]);
      await loadRooms();
    } catch (err) {
      console.error('[chat] delete room failed', err);
      const code = err?.response?.data?.error;
      if (code === 'CHAT_ROOM_DELETE_OWNER_ONLY') {
        alert('방장만 채팅방을 삭제할 수 있습니다.');
      } else if (code === 'DIRECT_ROOM_DELETE_NOT_ALLOWED') {
        alert('1:1 대화는 삭제할 수 없습니다. 나가기로 내 목록에서 숨겨 주세요.');
      } else {
        alert('채팅방 삭제에 실패했습니다.');
      }
    } finally {
      setDeletingRoom(false);
    }
  };

  const toggleSelectedMember = (u) => {
    if (!u) return;
    if (isAlreadyRoomMember(u)) {
      return;
    }
    setSelectedMembers((prev) => {
      if (prev.some((item) => Number(item.id) === Number(u.id))) {
        return prev.filter((item) => Number(item.id) !== Number(u.id));
      }
      return [...prev, u];
    });
  };

  const createGroupRoom = async () => {
    const title = groupTitle.trim();
    if (!title) {
      alert('그룹 채팅방 이름을 입력하세요.');
      return;
    }
    if (selectedMembers.length < 2) {
      alert('그룹 채팅은 본인을 제외하고 최소 2명을 선택하세요.');
      return;
    }

    setCreatingRoom(true);
    try {
      const res = await chatApi.post('/rooms', {
        room_type: 'GROUP',
        title,
        member_ids: selectedMembers.map((item) => item.id),
      });
      const roomId = res.data?.room_id;
      resetCreatePanel();
      await loadRooms();
      if (roomId) openRoom(roomId);
    } catch (err) {
      console.error('[chat] group room failed', err);
      alert('그룹 채팅방 생성에 실패했습니다.');
    } finally {
      setCreatingRoom(false);
    }
  };

  const openAddMembersHistoryConfirm = () => {
    if (!activeRoomId) return;
    if (!canManageCurrentRoom) {
      alert('방장 또는 관리자만 참여자를 추가할 수 있습니다.');
      return;
    }
    const addableMembers = selectedMembers.filter((member) => !isAlreadyRoomMember(member));
    if (!addableMembers.length) {
      alert('추가할 직원을 선택하세요. 이미 참여중인 직원은 다시 추가할 수 없습니다.');
      return;
    }
    setSelectedMembers(addableMembers);
    setHistoryAll(true);
    setAddMembersHistoryOpen(true);
  };

  const addMembersToRoom = async () => {
    if (!activeRoomId) return;
    if (!canManageCurrentRoom) {
      alert('방장 또는 관리자만 참여자를 추가할 수 있습니다.');
      return;
    }
    const addableMembers = selectedMembers.filter((member) => !isAlreadyRoomMember(member));
    if (!addableMembers.length) {
      alert('추가할 직원을 선택하세요. 이미 참여중인 직원은 다시 추가할 수 없습니다.');
      return;
    }

    setCreatingRoom(true);
    try {
      await chatApi.post(`/rooms/${activeRoomId}/members`, {
        member_ids: addableMembers.map((item) => item.id),
        history_mode: historyAll ? 'ALL' : 'FROM_JOIN',
      });
      setAddMembersHistoryOpen(false);
      resetCreatePanel();
      await loadRooms();
      await loadRoomMembers();
      await loadMessages(activeRoomId);
    } catch (err) {
      console.error('[chat] add members failed', err);
      alert('참여자 추가에 실패했습니다.');
    } finally {
      setCreatingRoom(false);
    }
  };

  const removeMemberFromRoom = async (member) => {
    if (!activeRoomId || !member?.employee_id) return;
    if (!canManageCurrentRoom) {
      alert('방장 또는 관리자만 참여자를 내보낼 수 있습니다.');
      return;
    }
    if (Number(member.employee_id) === Number(currentEmployeeId)) {
      alert('본인은 여기서 내보낼 수 없습니다.');
      return;
    }
    const targetRole = normalizeMemberRole(member.member_role);
    if (targetRole === 'OWNER') {
      alert('방장은 내보낼 수 없습니다.');
      return;
    }
    if (currentRoomRole === 'MANAGER' && targetRole !== 'MEMBER') {
      alert('관리자는 일반 참여자만 내보낼 수 있습니다.');
      return;
    }

    const name = formatEmployeeDisplayFromUser(member);
    if (!window.confirm(`${name} 님을 이 채팅방에서 내보낼까요?`)) return;

    setRemovingMemberId(Number(member.employee_id));
    try {
      await chatApi.delete(`/rooms/${activeRoomId}/members/${member.employee_id}`);
      await loadRoomMembers();
      await loadRooms();
      await loadMessages(activeRoomId);
    } catch (err) {
      console.error('[chat] remove member failed', err);
      const code = err?.response?.data?.error;
      if (code === 'CHAT_ROOM_MANAGE_FORBIDDEN' || code === 'CHAT_MEMBER_REMOVE_FORBIDDEN') {
        alert('참여자를 내보낼 권한이 없습니다.');
      } else if (code === 'OWNER_MEMBER_REMOVE_NOT_ALLOWED') {
        alert('방장은 내보낼 수 없습니다.');
      } else {
        alert('참여자 백업에 실패했습니다.');
      }
    } finally {
      setRemovingMemberId(null);
    }
  };


  const changeMemberRole = async (member, nextRole) => {
    if (!activeRoomId || !member?.employee_id) return;
    if (!isCurrentRoomOwner) {
      alert('방장만 관리자 지정/해제 또는 방장 위임을 할 수 있습니다.');
      return;
    }
    const normalizedNext = normalizeMemberRole(nextRole);
    if (Number(member.employee_id) === Number(currentEmployeeId) && normalizedNext !== 'OWNER') {
      alert('본인 권한은 직접 낮출 수 없습니다. 다른 참여자에게 방장을 위임하세요.');
      return;
    }
    const name = formatEmployeeDisplayFromUser(member);
    const label = getMemberRoleLabel(normalizedNext);
    if (!window.confirm(`${name} 님을 ${label}(으)로 변경할까요?`)) return;

    setRoleChangingMemberId(Number(member.employee_id));
    try {
      await chatApi.patch(`/rooms/${activeRoomId}/members/${member.employee_id}/role`, { member_role: normalizedNext });
      await loadRoomMembers();
      await loadRooms();
      await loadMessages(activeRoomId);
    } catch (err) {
      console.error('[chat] change member role failed', err);
      const code = err?.response?.data?.error;
      if (code === 'CHAT_MEMBER_ROLE_OWNER_ONLY') {
        alert('방장만 관리자 지정/해제 또는 방장 위임을 할 수 있습니다.');
      } else if (code === 'OWNER_ROLE_DOWNGRADE_NOT_ALLOWED' || code === 'OWNER_ROLE_DOWNGRADE_USE_TRANSFER') {
        alert('방장은 일반 참여자로 바로 변경할 수 없습니다. 먼저 방장 위임을 사용하세요.');
      } else {
        alert('참여자 권한 변경에 실패했습니다.');
      }
    } finally {
      setRoleChangingMemberId(null);
    }
  };

  const canRemoveMember = (member) => {
    if (!canManageCurrentRoom || !member) return false;
    if (Number(member.employee_id) === Number(currentEmployeeId)) return false;
    const targetRole = normalizeMemberRole(member.member_role);
    if (targetRole === 'OWNER') return false;
    if (currentRoomRole === 'MANAGER') return targetRole === 'MEMBER';
    return true;
  };

  const getDownloadFileName = (attachment, headers = {}) => {
    const cd = headers?.['content-disposition'] || headers?.['Content-Disposition'] || '';
    const utf8Match = String(cd).match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1]);
      } catch {}
    }
    const plainMatch = String(cd).match(/filename="?([^";]+)"?/i);
    if (plainMatch?.[1]) return plainMatch[1];
    return attachment?.original_name || 'download';
  };

  const downloadAttachment = async (attachment) => {
    if (!attachment?.id || attachment?.is_expired) return;
    try {
      const res = await chatApi.get(`/attachments/${attachment.id}/download`, {
        responseType: 'blob',
        timeout: 0,
      });
      const contentType = res.headers?.['content-type'] || attachment.mime_type || 'application/octet-stream';
      const blob = new Blob([res.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = getDownloadFileName(attachment, res.headers);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('[chat] attachment download failed', err);
      const status = err?.response?.status;
      const code = err?.response?.data?.error;
      if (status === 404) {
        alert('파일이 만료되었거나 서버에 없습니다.');
      } else if (code === 'CHAT_ATTACHMENT_HEADER_FAILED') {
        alert('파일명 처리 중 오류가 발생했습니다. 다시 업로드 후 확인해 주세요.');
      } else {
        alert('파일 다운로드에 실패했습니다.');
      }
    }
  };

  const closePreview = useCallback(() => {
    setPreview((prev) => {
      if (prev?.url) window.URL.revokeObjectURL(prev.url);
      return null;
    });
  }, []);

  const openAttachmentPreview = async (attachment) => {
    if (!attachment?.id || !canPreviewAttachment(attachment)) return;

    closePreview();
    setPreview({ attachment, loading: true, error: '', url: '', text: '', kind: getAttachmentKind(attachment) });

    try {
      const res = await chatApi.get(`/attachments/${attachment.id}/preview`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: attachment.mime_type || res.headers?.['content-type'] || 'application/octet-stream' });
      const kind = getAttachmentKind(attachment);

      if (kind === 'text') {
        const text = await blob.text();
        setPreview({ attachment, loading: false, error: '', url: '', text: text.slice(0, 200000), kind });
        return;
      }

      const url = window.URL.createObjectURL(blob);
      setPreview({ attachment, loading: false, error: '', url, text: '', kind });
    } catch (err) {
      console.error('[chat] attachment preview failed', err);
      setPreview({ attachment, loading: false, error: '미리보기를 불러오지 못했습니다.', url: '', text: '', kind: getAttachmentKind(attachment) });
    }
  };

  const makeAttachmentFromSharedItem = useCallback((item = {}) => ({
    id: item.attachment_id,
    original_name: item.original_name,
    mime_type: item.mime_type,
    size_bytes: item.size_bytes,
    is_expired: false,
  }), []);

  const loadSharedItems = useCallback(async ({ scope = sharedScope, type = sharedType } = {}) => {
    const normalizedScope = scope === 'mine' ? 'mine' : 'room';
    if (normalizedScope === 'room' && !activeRoomId) {
      setSharedItems([]);
      return;
    }
    setLoadingSharedItems(true);
    try {
      const url = normalizedScope === 'mine' ? '/shared' : `/rooms/${activeRoomId}/shared`;
      const res = await chatApi.get(url, { params: { type, limit: 120 } });
      setSharedItems(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (err) {
      console.error('[chat] shared items load failed', err);
      setSharedItems([]);
      alert('공유 자료를 불러오지 못했습니다.');
    } finally {
      setLoadingSharedItems(false);
    }
  }, [activeRoomId, sharedScope, sharedType]);

  const openSharedLibrary = useCallback(() => {
    const nextScope = activeRoomId ? 'room' : 'mine';
    setSharedScope(nextScope);
    setSharedType('all');
    setSharedOpen(true);
    loadSharedItems({ scope: nextScope, type: 'all' });
  }, [activeRoomId, loadSharedItems]);

  const changeSharedScope = useCallback((scope) => {
    const nextScope = scope === 'mine' ? 'mine' : 'room';
    setSharedScope(nextScope);
    loadSharedItems({ scope: nextScope, type: sharedType });
  }, [loadSharedItems, sharedType]);

  const changeSharedType = useCallback((type) => {
    const nextType = type || 'all';
    setSharedType(nextType);
    loadSharedItems({ scope: sharedScope, type: nextType });
  }, [loadSharedItems, sharedScope]);

  const handleSharedItemOpen = useCallback((item) => {
    if (!item) return;
    if (item.item_type === 'LINK') {
      window.open(item.url, '_blank', 'noopener,noreferrer');
      return;
    }
    const attachment = makeAttachmentFromSharedItem(item);
    if (canPreviewAttachment(attachment)) openAttachmentPreview(attachment);
    else downloadAttachment(attachment);
  }, [downloadAttachment, makeAttachmentFromSharedItem, openAttachmentPreview]);

  const handleSharedItemDownload = useCallback((item) => {
    if (!item?.attachment_id) return;
    downloadAttachment(makeAttachmentFromSharedItem(item));
  }, [downloadAttachment, makeAttachmentFromSharedItem]);

  useEffect(() => () => {
    setPreview((prev) => {
      if (prev?.url) window.URL.revokeObjectURL(prev.url);
      return null;
    });
  }, []);

  const updateUploadTask = useCallback((taskId, patch) => {
    setUploadTasks((prev) => prev.map((task) => (
      task.id === taskId ? { ...task, ...patch } : task
    )));
  }, []);

  const removeFinishedUploadTasksLater = useCallback(() => {
    window.setTimeout(() => {
      setUploadTasks((prev) => prev.filter((task) => task.status === 'uploading'));
    }, 4500);
  }, []);

  const appendFileMessage = useCallback((msg) => {
    if (!msg?.id) return;
    setMessages((prev) => uniqueById([...prev, msg]));
    setRooms((prev) => prev.map((room) => (
      Number(room.id) === Number(activeRoomIdRef.current)
        ? {
            ...room,
            last_message_body: msg.body,
            last_message_type: msg.message_type,
            last_message_at: msg.created_at,
            last_sender_name: msg.sender_name,
            unread_count: 0,
          }
        : room
    )));
    if (!jumpDate && !messageSearchApplied) scrollToBottom();
  }, [jumpDate, messageSearchApplied, scrollToBottom]);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!activeRoomId || !files.length) return;

    const roomIdAtStart = activeRoomId;
    const startedAt = Date.now();
    const tasks = files.map((file, index) => ({
      id: `${startedAt}-${index}-${file.name}`,
      name: file.name,
      size: file.size,
      progress: 0,
      status: 'waiting',
      error: '',
    }));

    setUploadTasks((prev) => [...prev, ...tasks]);

    // 한 번에 여러 파일을 하나의 요청으로 묶으면 큰 파일에서 브라우저/프록시가 오래 멈춘 것처럼 보일 수 있습니다.
    // 그래서 1개 파일씩 순차 업로드하고 각 파일별 진행률을 표시합니다.
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      const task = tasks[i];
      const formData = new FormData();
      formData.append('files', file);

      updateUploadTask(task.id, { status: 'uploading', progress: 0, error: '' });

      try {
        // FormData 업로드는 브라우저/axios가 boundary를 포함한 Content-Type을 자동으로 만들어야 합니다.
        // 수동으로 multipart/form-data를 지정하면 일부 환경에서 boundary가 빠져 multer가 실패합니다.
        const res = await chatApi.post(`/rooms/${roomIdAtStart}/attachments`, formData, {
          timeout: 0,
          onUploadProgress: (event) => {
            if (!event.total) return;
            const progress = Math.min(99, Math.round((event.loaded / event.total) * 100));
            updateUploadTask(task.id, { progress });
          },
        });

        updateUploadTask(task.id, { status: 'done', progress: 100 });
        appendFileMessage(res.data?.message);
      } catch (err) {
        console.error('[chat] attachment upload failed', err);
        const code = err?.response?.data?.error;
        const message = code === 'CHAT_FILE_TOO_LARGE'
          ? '파일 용량 제한 초과'
          : code === 'CHAT_FILE_COUNT_EXCEEDED'
            ? '파일 개수 제한 초과'
            : err?.response?.data?.message || err?.message || '업로드 실패';
        updateUploadTask(task.id, { status: 'error', error: message });
      }
    }

    removeFinishedUploadTasksLater();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const addPendingAttachments = useCallback((fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!activeRoom || !files.length) return;

    const addedAt = Date.now();
    const items = files.map((file, index) => {
      const type = String(file.type || '').toLowerCase();
      const previewUrl = type.startsWith('image/') ? window.URL.createObjectURL(file) : '';
      return {
        id: `${addedAt}-${index}-${file.name}-${file.size}`,
        file,
        name: file.name || `clipboard_${addedAt}_${index + 1}.png`,
        size: file.size || 0,
        type,
        previewUrl,
      };
    });

    setPendingAttachments((prev) => [...prev, ...items]);
    setTimeout(() => inputRef.current?.focus?.(), 0);
  }, [activeRoom]);

  const removePendingAttachment = useCallback((pendingId) => {
    setPendingAttachments((prev) => {
      const target = prev.find((item) => item.id === pendingId);
      if (target?.previewUrl) window.URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.id !== pendingId);
    });
  }, []);

  const clearPendingAttachments = useCallback((items = pendingAttachments) => {
    items.forEach((item) => {
      if (item?.previewUrl) window.URL.revokeObjectURL(item.previewUrl);
    });
    setPendingAttachments([]);
  }, [pendingAttachments]);

  useEffect(() => () => {
    pendingAttachments.forEach((item) => {
      if (item?.previewUrl) window.URL.revokeObjectURL(item.previewUrl);
    });
  }, [pendingAttachments]);

  const isMessageDeleted = useCallback((msg) => Boolean(msg?.deleted_at), []);

  const isInternalMessageDrag = useCallback((dataTransfer) => {
    const types = Array.from(dataTransfer?.types || []);
    return types.includes(CHAT_MESSAGE_DRAG_MIME) || Boolean(dragShareMessage?.id);
  }, [dragShareMessage]);

  const handleMessageDragStart = useCallback((msg, event) => {
    if (!msg || isMessageDeleted(msg) || msg.message_type === 'SYSTEM') return;
    setDragShareMessage(msg);
    setDragShareTargetRoomId(null);
    const payload = {
      source_room_id: Number(activeRoomId),
      message_id: Number(msg.id),
      preview: getSharePreviewLine(msg, roomMembers),
    };
    const json = JSON.stringify(payload);
    try {
      event.dataTransfer.effectAllowed = 'copy';
      event.dataTransfer.setData(CHAT_MESSAGE_DRAG_MIME, json);
      event.dataTransfer.setData('text/plain', payload.preview);
    } catch (err) {
      console.warn('[chat] message drag data failed', err);
    }
  }, [activeRoomId, isMessageDeleted, roomMembers]);

  const handleMessageDragEnd = useCallback(() => {
    setDragShareMessage(null);
    setDragShareTargetRoomId(null);
  }, []);

  const postShareMessage = useCallback(async (sourceMessage, targetRoomIds, { confirm = false } = {}) => {
    const targets = uniquePositiveIds(targetRoomIds).filter((id) => Number(id) !== Number(activeRoomId));
    if (!activeRoomId || !sourceMessage?.id || !targets.length || sharingMessage) return false;
    const targetNames = rooms
      .filter((room) => targets.includes(Number(room.id)))
      .map((room) => getRoomTitle(room))
      .join(', ');
    if (confirm) {
      const ok = window.confirm(`다음 내용을 ${targetNames || '선택한 채팅방'}에 공유할까요?\n\n${getSharePreviewLine(sourceMessage, roomMembers)}`);
      if (!ok) return false;
    }
    setSharingMessage(true);
    try {
      await chatApi.post(`/rooms/${activeRoomId}/messages/${sourceMessage.id}/share`, {
        target_room_ids: targets,
      });
      await loadRooms();
      alert(`${targetNames || '선택한 채팅방'}에 공유했습니다.`);
      return true;
    } catch (err) {
      console.error('[chat] share message failed', err);
      const code = err?.response?.data?.error;
      if (code === 'TARGET_ROOM_REQUIRED') alert('공유할 채팅방을 선택해 주세요.');
      else if (code === 'SOURCE_MESSAGE_NOT_FOUND') alert('공유할 수 없는 메시지입니다.');
      else if (code === 'TARGET_ROOM_FORBIDDEN') alert('공유 대상 채팅방에 접근 권한이 없습니다.');
      else alert('메시지 공유에 실패했습니다.');
      return false;
    } finally {
      setSharingMessage(false);
    }
  }, [activeRoomId, loadRooms, roomMembers, rooms, sharingMessage]);

  const handleRoomDragOver = useCallback((room, event) => {
    if (!isInternalMessageDrag(event.dataTransfer)) return;
    event.preventDefault();
    event.stopPropagation();
    const roomId = Number(room?.id || 0);
    const sameRoom = roomId === Number(activeRoomId);
    try { event.dataTransfer.dropEffect = sameRoom ? 'none' : 'copy'; } catch {}
    setDragShareTargetRoomId(sameRoom ? null : roomId);
  }, [activeRoomId, isInternalMessageDrag]);

  const handleRoomDragLeave = useCallback((room, event) => {
    if (!isInternalMessageDrag(event.dataTransfer)) return;
    const current = event.currentTarget;
    const related = event.relatedTarget;
    if (current && related && current.contains(related)) return;
    const roomId = Number(room?.id || 0);
    setDragShareTargetRoomId((prev) => (Number(prev) === roomId ? null : prev));
  }, [isInternalMessageDrag]);

  const handleRoomDropShare = useCallback(async (room, event) => {
    if (!isInternalMessageDrag(event.dataTransfer)) return;
    event.preventDefault();
    event.stopPropagation();
    const roomId = Number(room?.id || 0);
    if (!roomId) return;
    if (roomId === Number(activeRoomId)) {
      alert('같은 채팅방에는 다시 공유하지 않습니다. 다른 채팅방으로만 공유할 수 있습니다.');
      handleMessageDragEnd();
      return;
    }
    let sourceMessage = dragShareMessage;
    if (!sourceMessage?.id) {
      try {
        const payload = JSON.parse(event.dataTransfer.getData(CHAT_MESSAGE_DRAG_MIME) || '{}');
        const messageId = Number(payload.message_id);
        sourceMessage = messages.find((item) => Number(item.id) === messageId) || null;
      } catch {
        sourceMessage = null;
      }
    }
    if (!sourceMessage?.id) {
      alert('공유할 메시지를 확인할 수 없습니다. 다시 시도해 주세요.');
      handleMessageDragEnd();
      return;
    }
    await postShareMessage(sourceMessage, [roomId], { confirm: true });
    handleMessageDragEnd();
  }, [activeRoomId, dragShareMessage, handleMessageDragEnd, isInternalMessageDrag, messages, postShareMessage]);

  const handleDragOver = (e) => {
    if (roomItemModalOpen) {
      setDragActive(false);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (!activeRoom) return;
    if (isInternalMessageDrag(e.dataTransfer)) {
      setDragActive(false);
      try { e.dataTransfer.dropEffect = 'none'; } catch {}
      return;
    }
    setDragActive(true);
  };

  const handleDragEnter = (e) => {
    if (roomItemModalOpen) {
      setDragActive(false);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (!activeRoom) return;
    if (isInternalMessageDrag(e.dataTransfer)) {
      setDragActive(false);
      return;
    }
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const current = e.currentTarget;
    const related = e.relatedTarget;
    if (current && related && current.contains(related)) return;
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (roomItemModalOpen) return;
    if (!activeRoom) return;
    if (isInternalMessageDrag(e.dataTransfer)) {
      alert('같은 채팅방에는 다시 공유하지 않습니다. 왼쪽 채팅방 목록의 다른 방에 놓아 주세요.');
      e.dataTransfer?.clearData?.();
      handleMessageDragEnd();
      return;
    }
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      uploadFiles(files);
      e.dataTransfer.clearData?.();
    }
  };

  const handleClipboardPaste = useCallback((e) => {
    if (!activeRoom) return;

    const clipboardData = e.clipboardData || window.clipboardData;
    if (!clipboardData) return;

    const pastedImages = [];

    const items = Array.from(clipboardData.items || []);
    items.forEach((item) => {
      if (item.kind !== 'file') return;
      const file = item.getAsFile?.();
      if (!file || !String(file.type || '').toLowerCase().startsWith('image/')) return;
      pastedImages.push(file);
    });

    if (!pastedImages.length) {
      Array.from(clipboardData.files || []).forEach((file) => {
        if (file && String(file.type || '').toLowerCase().startsWith('image/')) {
          pastedImages.push(file);
        }
      });
    }

    if (!pastedImages.length) return;

    e.preventDefault();
    e.stopPropagation();

    const normalizedFiles = pastedImages
      .map((file, index) => makeClipboardImageFile(file, index))
      .filter(Boolean);

    if (normalizedFiles.length) {
      addPendingAttachments(normalizedFiles);
    }
  }, [activeRoom, addPendingAttachments]);

  const sendMessage = async () => {
    const body = text.trim();
    const filesToSend = pendingAttachments.slice();
    if (!activeRoomId || (!body && filesToSend.length === 0) || sending) return;

    setSending(true);
    setText('');
    setPendingAttachments([]);

    try {
      if (body) {
        // 중요: 텍스트 전송은 REST 한 경로로만 저장합니다.
        // 이전 구조는 Socket.IO 전송 후 ack가 늦으면 REST fallback이 한 번 더 실행되어
        // DB에 서로 다른 id의 동일 메시지가 2건 생성될 수 있었습니다.
        // REST API가 저장 후 Socket.IO로 새 메시지를 broadcast하므로 실시간 수신은 유지됩니다.
        const replyToMessageId = replyTarget?.id ? Number(replyTarget.id) : null;
        const res = await chatApi.post(`/rooms/${activeRoomId}/messages`, { body, reply_to_message_id: replyToMessageId });
        const saved = { ok: true, message: res.data?.message };

        const msg = normalizeOutgoingReadCount(saved?.message);
        if (msg?.id) {
          setMessages((prev) => uniqueById([...prev, msg]));
          setRooms((prev) => prev.map((room) => (
            Number(room.id) === Number(activeRoomId)
              ? { ...room, last_message_body: msg.body, last_message_type: msg.message_type, last_message_at: msg.created_at, last_sender_name: msg.sender_name, unread_count: 0 }
              : room
          )));
        }
      }

      if (filesToSend.length > 0) {
        await uploadFiles(filesToSend.map((item) => item.file).filter(Boolean));
        filesToSend.forEach((item) => {
          if (item?.previewUrl) window.URL.revokeObjectURL(item.previewUrl);
        });
      }

      setReplyTarget(null);
      scrollToBottom();
    } catch (err) {
      console.error('[chat] send failed', err);
      setText(body);
      setPendingAttachments((prev) => [...filesToSend, ...prev]);
      alert(filesToSend.length > 0 && !body ? '파일 전송에 실패했습니다.' : '메시지 전송에 실패했습니다.');
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus?.(), 0);
    }
  };


  const canDeleteMessage = useCallback((msg) => {
    if (!msg || isMessageDeleted(msg) || msg.message_type === 'SYSTEM') return false;
    return Number(msg.sender_employee_id) === Number(currentEmployeeId);
  }, [currentEmployeeId, isMessageDeleted]);

  const clearMessageLongPressTimer = useCallback(() => {
    if (messageLongPressTimerRef.current) {
      window.clearTimeout(messageLongPressTimerRef.current);
      messageLongPressTimerRef.current = null;
    }
  }, []);

  const openMessageMenu = useCallback((msg, event) => {
    if (!msg || msg.message_type === 'SYSTEM' || isMessageDeleted(msg)) return;
    const point = event?.touches?.[0] || event?.changedTouches?.[0] || event;
    const x = Number(point?.clientX || 0);
    const y = Number(point?.clientY || 0);
    suppressMessageMenuCloseRef.current = true;
    setMessageMenu({
      message: msg,
      x: Math.max(12, Math.min(x, window.innerWidth - 180)),
      y: Math.max(12, Math.min(y, window.innerHeight - 120)),
      canDelete: canDeleteMessage(msg),
    });
  }, [canDeleteMessage, isMessageDeleted]);

  const startMessageLongPress = useCallback((msg, event) => {
    clearMessageLongPressTimer();
    if (!msg || msg.message_type === 'SYSTEM' || isMessageDeleted(msg)) return;

    // PC에서 말풍선 전체를 draggable/long-press 대상으로 잡으면 텍스트 복사가 어려워집니다.
    // 메시지 동작은 우클릭 또는 모바일 길게 누르기 메뉴에서 처리합니다.
    const pointerType = String(event?.pointerType || '').toLowerCase();
    if (pointerType && pointerType !== 'touch' && pointerType !== 'pen') return;
    if (event?.button !== undefined && Number(event.button) !== 0) return;

    const point = event?.touches?.[0] || event;
    const clientX = point?.clientX || 0;
    const clientY = point?.clientY || 0;
    messageLongPressTimerRef.current = window.setTimeout(() => {
      openMessageMenu(msg, { clientX, clientY });
    }, 450);
  }, [clearMessageLongPressTimer, openMessageMenu, isMessageDeleted]);

  const markDeletedMessage = useCallback((messageId, deletedAt = null) => {
    const id = Number(messageId);
    if (!id) return;
    const deletedTime = deletedAt || new Date().toISOString();
    setMessages((prev) => prev.map((item) => (Number(item.id) === id
      ? { ...item, deleted_at: item.deleted_at || deletedTime, body: '', attachments: [] }
      : item)));
  }, []);


  const clearTextSelection = () => {
    try {
      const selection = window.getSelection?.();
      if (selection && selection.rangeCount > 0) selection.removeAllRanges();
      if (document.selection?.empty) document.selection.empty();
    } catch (_) {
      // 선택 영역 제거 실패는 공유 동작을 막지 않습니다.
    }
  };

  const openShareMessage = (msg) => {
    if (!msg || isMessageDeleted(msg) || msg.message_type === 'SYSTEM') return;
    clearTextSelection();
    setMessageMenu(null);
    setShareMessage(msg);
    setShareTargetRoomIds([]);
  };

  const closeShareMessage = () => {
    if (sharingMessage) return;
    clearTextSelection();
    setShareMessage(null);
    setShareTargetRoomIds([]);
  };

  const toggleShareTargetRoom = (roomId) => {
    const id = Number(roomId);
    if (!id) return;
    setShareTargetRoomIds((prev) => (prev.includes(id)
      ? prev.filter((item) => item !== id)
      : [...prev, id]));
  };

  const shareMessageToRooms = async () => {
    const ok = await postShareMessage(shareMessage, shareTargetRoomIds, { confirm: false });
    if (ok) closeShareMessage();
  };

  const deleteMessage = async (msg) => {
    if (!activeRoomId || !msg?.id || deletingMessageId || isMessageDeleted(msg)) return;
    setDeletingMessageId(Number(msg.id));
    setMessageMenu(null);
    try {
      const res = await chatApi.delete(`/rooms/${activeRoomId}/messages/${msg.id}`);
      markDeletedMessage(msg.id, res?.data?.deleted_at);
      await loadRooms();
    } catch (err) {
      console.error('[chat] delete message failed', err);
      alert('메시지 삭제에 실패했습니다. 본인이 보낸 메시지만 삭제할 수 있습니다.');
    } finally {
      setDeletingMessageId(null);
    }
  };


  useEffect(() => {
    const token = getToken();
    const currentEmployeeId = Number(getCurrentEmployeeId() || 0);
    if (currentEmployeeId && typeof navigator !== 'undefined' && navigator.serviceWorker) {
      navigator.serviceWorker.ready.then((registration) => {
        const message = { type: 'CHAT_SET_CURRENT_EMPLOYEE', employee_id: currentEmployeeId };
        if (registration.active) registration.active.postMessage(message);
        if (navigator.serviceWorker.controller) navigator.serviceWorker.controller.postMessage(message);
      }).catch(() => {});
    }
    if (!token) return;
    let cancelled = false;

    autoEnsureDevicePushSubscription({ token, source: 'chat-page-load' })
      .then((result) => {
        if (cancelled || !result?.ok) return;
        console.info('[chat/push] device subscription ready:', result.reason || 'ok');
      })
      .catch((err) => {
        if (cancelled) return;
        // 알림 권한이 없거나 지원하지 않는 환경은 정상적인 skip 케이스라 화면을 막지 않는다.
        console.warn('[chat/push] auto subscribe skipped:', err?.message || err);
      });

    return () => {
      cancelled = true;
    };
  }, []);



  const loadRoomNotices = useCallback(async (roomId = activeRoomId) => {
    if (!roomId) return;
    const request = roomRequests.current.begin('notices', roomId);
    setLoadingNotices(true);
    try {
      const res = await chatApi.get(`/rooms/${roomId}/notices`, {signal:request.signal});
      if (!request.current()) return;
      setRoomNotices(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (err) {
      if (!request.current()) return;
      console.error('[chat] notices failed', err);
      setRoomNotices([]);
    } finally {
      if (request.current()) setLoadingNotices(false);
    }
  }, [activeRoomId]);

  const loadRoomNotes = useCallback(async (roomId = activeRoomId) => {
    if (!roomId) return;
    const request = roomRequests.current.begin('notes', roomId);
    setLoadingNotes(true);
    try {
      const res = await chatApi.get(`/rooms/${roomId}/notes`, {signal:request.signal});
      if (!request.current()) return;
      setRoomNotes(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (err) {
      if (!request.current()) return;
      console.error('[chat] notes failed', err);
      setRoomNotes([]);
    } finally {
      if (request.current()) setLoadingNotes(false);
    }
  }, [activeRoomId]);

  useEffect(() => {
    setRoomNotices([]);
    setRoomNotes([]);
    setLoadingNotices(false);
    setLoadingNotes(false);
    if (!activeRoomId) {
      return;
    }
    const timer = window.setTimeout(() => loadRoomNotices(activeRoomId), 80);
    return () => window.clearTimeout(timer);
  }, [activeRoomId, roomRefreshVersion, loadRoomNotices]);

  useEffect(() => {
    if (activeRoomId && notesOpen) loadRoomNotes(activeRoomId);
  }, [activeRoomId, roomRefreshVersion, notesOpen, loadRoomNotes]);


  const makeRoomItemFileItems = useCallback((fileList, options = {}) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return [];
    const addedAt = Date.now();
    return files.map((file, index) => {
      const type = String(file.type || '').toLowerCase();
      return {
        id: `${addedAt}-${index}-${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`,
        file,
        name: file.name || `첨부파일_${index + 1}`,
        size: file.size || 0,
        type,
        inline: Boolean(options.inline),
        previewUrl: type.startsWith('image/') ? window.URL.createObjectURL(file) : '',
      };
    });
  }, []);

  const addRoomItemFiles = useCallback((fileList, setter, options = {}) => {
    const items = makeRoomItemFileItems(fileList, options);
    if (!items.length) return items;
    setter((prev) => [...prev, ...items]);
    return items;
  }, [makeRoomItemFileItems]);

  const removeRoomItemFile = useCallback((pendingId, setter) => {
    setter((prev) => {
      const target = prev.find((item) => item.id === pendingId);
      if (target?.previewUrl) window.URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.id !== pendingId);
    });
  }, []);

  const clearRoomItemFiles = useCallback((items, setter) => {
    (Array.isArray(items) ? items : []).forEach((item) => {
      if (item?.previewUrl) window.URL.revokeObjectURL(item.previewUrl);
    });
    setter([]);
  }, []);

  const roomItemModalOpen = noticesOpen || notesOpen;

  useEffect(() => {
    if (roomItemModalOpen) {
      setDragActive(false);
      setRoomMenuOpen(false);
      setCreateMenuOpen(false);
    }
  }, [roomItemModalOpen]);

  useEffect(() => {
    if (!preview || typeof document === 'undefined') return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [preview]);

  useEffect(() => {
    if (!roomItemModalOpen || typeof document === 'undefined') return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [roomItemModalOpen]);

  useEffect(() => {
    const clearChatDragOverlay = () => setDragActive(false);
    window.addEventListener('drop', clearChatDragOverlay, true);
    window.addEventListener('dragend', clearChatDragOverlay, true);
    window.addEventListener('keyup', clearChatDragOverlay, true);
    return () => {
      window.removeEventListener('drop', clearChatDragOverlay, true);
      window.removeEventListener('dragend', clearChatDragOverlay, true);
      window.removeEventListener('keyup', clearChatDragOverlay, true);
    };
  }, []);

  const stopRoomItemFileEvent = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.nativeEvent?.stopImmediatePropagation) event.nativeEvent.stopImmediatePropagation();
  }, []);

  const getRoomItemDragFiles = useCallback((dataTransfer) => {
    if (!dataTransfer) return [];
    const items = Array.from(dataTransfer.items || []);
    if (items.length) {
      return items
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter(Boolean);
    }
    return Array.from(dataTransfer.files || []).filter(Boolean);
  }, []);

  const getRoomItemClipboardFiles = useCallback((clipboardData) => {
    if (!clipboardData) return [];
    const fromFiles = Array.from(clipboardData.files || []).filter(Boolean);
    if (fromFiles.length) return fromFiles;
    return Array.from(clipboardData.items || [])
      .filter((item) => item.kind === 'file')
      .map((item) => item.getAsFile())
      .filter(Boolean);
  }, []);

  const handleRoomItemDragOver = useCallback((event) => {
    const hasFile = Array.from(event.dataTransfer?.types || []).includes('Files');
    if (!hasFile) return;
    stopRoomItemFileEvent(event);
    setDragActive(false);
    try { event.dataTransfer.dropEffect = 'copy'; } catch {}
  }, [stopRoomItemFileEvent]);

  const handleRoomItemDrop = useCallback((event, setter) => {
    const files = getRoomItemDragFiles(event.dataTransfer);
    if (!files.length) return;
    stopRoomItemFileEvent(event);
    setDragActive(false);
    addRoomItemFiles(files, setter);
    try { event.dataTransfer?.clearData?.(); } catch {}
  }, [addRoomItemFiles, getRoomItemDragFiles, stopRoomItemFileEvent]);

  const handleRoomItemPaste = useCallback((event, setter) => {
    const files = getRoomItemClipboardFiles(event.clipboardData);
    if (!files.length) return;
    stopRoomItemFileEvent(event);
    setDragActive(false);
    addRoomItemFiles(files, setter);
  }, [addRoomItemFiles, getRoomItemClipboardFiles, stopRoomItemFileEvent]);


  const createNoticeInlineImageFigure = useCallback((item = {}) => {
    const figure = document.createElement('figure');
    figure.className = 'hc-notice-inline-token';
    figure.contentEditable = 'false';
    figure.dataset.inlineFileId = item.id || item.attachmentId || item.name || `inline-${Date.now()}`;
    figure.dataset.fileName = item.name || 'image.png';
    figure.dataset.kind = 'image';
    if (item.attachmentId) figure.dataset.existingAttachmentId = String(item.attachmentId);

    const img = document.createElement('img');
    img.alt = item.name || '공지 이미지';
    if (item.previewUrl) img.src = item.previewUrl;

    const caption = document.createElement('figcaption');
    caption.textContent = item.name || '이미지';

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'hc-notice-inline-remove';
    remove.textContent = '×';
    remove.setAttribute('aria-label', '이미지 제거');
    remove.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (item.id && !item.attachmentId) removeRoomItemFile(item.id, setNoticeFiles);
      figure.remove();
    });

    figure.appendChild(img);
    figure.appendChild(caption);
    figure.appendChild(remove);
    return { figure, img };
  }, [removeRoomItemFile]);

  const insertNoticeEditorNode = useCallback((node) => {
    const editor = noticeEditorRef.current;
    if (!editor || !node) return;
    editor.focus();
    const selection = window.getSelection?.();
    const range = selection && selection.rangeCount ? selection.getRangeAt(0) : null;
    const canUseRange = range && editor.contains(range.commonAncestorContainer);
    const targetRange = canUseRange ? range : document.createRange();
    if (!canUseRange) {
      targetRange.selectNodeContents(editor);
      targetRange.collapse(false);
    }
    targetRange.deleteContents();
    targetRange.insertNode(document.createTextNode('\n'));
    targetRange.insertNode(node);
    targetRange.insertNode(document.createTextNode('\n'));
    targetRange.collapse(false);
    selection?.removeAllRanges?.();
    selection?.addRange?.(targetRange);
  }, []);

  const insertNoticeInlineImages = useCallback((files) => {
    const imageFiles = Array.from(files || []).filter((file) => String(file?.type || '').toLowerCase().startsWith('image/'));
    const otherFiles = Array.from(files || []).filter((file) => !String(file?.type || '').toLowerCase().startsWith('image/'));
    if (otherFiles.length) addRoomItemFiles(otherFiles, setNoticeFiles);
    if (!imageFiles.length) return;
    const items = addRoomItemFiles(imageFiles, setNoticeFiles, { inline: true });
    items.forEach((item) => {
      const { figure } = createNoticeInlineImageFigure(item);
      insertNoticeEditorNode(figure);
    });
  }, [addRoomItemFiles, createNoticeInlineImageFigure, insertNoticeEditorNode]);

  const handleNoticeEditorDrop = useCallback((event) => {
    const files = getRoomItemDragFiles(event.dataTransfer);
    if (!files.length) return;
    stopRoomItemFileEvent(event);
    insertNoticeInlineImages(files);
  }, [getRoomItemDragFiles, insertNoticeInlineImages, stopRoomItemFileEvent]);

  const handleNoticeEditorPaste = useCallback((event) => {
    const files = getRoomItemClipboardFiles(event.clipboardData);
    if (!files.length) return;
    stopRoomItemFileEvent(event);
    insertNoticeInlineImages(files);
  }, [getRoomItemClipboardFiles, insertNoticeInlineImages, stopRoomItemFileEvent]);

  const serializeNoticeEditor = useCallback(() => {
    const editor = noticeEditorRef.current;
    if (!editor) return noticeText;
    const walk = (node) => {
      if (!node) return '';
      if (node.nodeType === 3) return node.nodeValue || '';
      if (node.nodeType !== 1) return '';
      const element = node;
      if (element.dataset?.kind === 'image') {
        const name = element.dataset.fileName || 'image.png';
        return `\n[이미지: ${name}]\n`;
      }
      if (element.tagName === 'BR') return '\n';
      let value = Array.from(element.childNodes || []).map(walk).join('');
      if (['DIV', 'P', 'FIGURE'].includes(element.tagName)) value += '\n';
      return value;
    };
    return Array.from(editor.childNodes || []).map(walk).join('').replace(/\n{3,}/g, '\n\n').trim();
  }, [noticeText]);

  const hydrateNoticeEditor = useCallback((body = '', attachments = []) => {
    const editor = noticeEditorRef.current;
    if (!editor) return;

    const text = String(body || '');
    editor.innerHTML = '';

    const appendText = (value = '') => {
      if (!value) return;
      editor.appendChild(document.createTextNode(value));
    };

    const list = Array.isArray(attachments) ? attachments : [];
    const re = /\[이미지:\s*([^\]]+?)\]/g;
    let lastIndex = 0;
    let match;
    while ((match = re.exec(text)) !== null) {
      appendText(text.slice(lastIndex, match.index));
      const name = getRoomItemFileName(match[1]);
      const attachment = findRoomItemAttachmentByName(list, name);
      if (attachment) {
        const { figure, img } = createNoticeInlineImageFigure({
          id: `existing-${attachment.id}`,
          attachmentId: attachment.id,
          name: getRoomItemAttachmentName(attachment) || name,
        });
        editor.appendChild(figure);
        chatApi.get(`/room-item-attachments/${attachment.id}/preview`, { responseType: 'blob' })
          .then((res) => {
            const blob = new Blob([res.data], { type: attachment.mime_type || res.headers?.['content-type'] || 'image/*' });
            const url = window.URL.createObjectURL(blob);
            img.src = url;
            figure.dataset.objectUrl = url;
          })
          .catch(() => {
            figure.classList.add('is-missing');
            img.replaceWith(document.createTextNode('이미지를 불러오지 못했습니다.'));
          });
      } else {
        const missing = document.createElement('span');
        missing.className = 'hc-room-item-inline-missing';
        missing.textContent = `[이미지: ${name || '이미지'}]`;
        editor.appendChild(missing);
      }
      lastIndex = match.index + match[0].length;
    }
    appendText(text.slice(lastIndex));
  }, [createNoticeInlineImageFigure]);

  useEffect(() => () => {
    noticeFiles.forEach((item) => item?.previewUrl && window.URL.revokeObjectURL(item.previewUrl));
  }, [noticeFiles]);

  useEffect(() => () => {
    noteFiles.forEach((item) => item?.previewUrl && window.URL.revokeObjectURL(item.previewUrl));
  }, [noteFiles]);

  const buildRoomItemFormData = (fields, pendingFiles) => {
    const formData = new FormData();
    Object.entries(fields || {}).forEach(([key, value]) => formData.append(key, value == null ? '' : String(value)));
    (pendingFiles || []).forEach((item) => formData.append('files', item.file, item.name));
    return formData;
  };

  const downloadRoomItemAttachment = async (attachment) => {
    if (!attachment?.id || attachment?.is_expired) return;
    try {
      const res = await chatApi.get(`/room-item-attachments/${attachment.id}/download`, { responseType: 'blob' });
      const contentType = res.headers?.['content-type'] || attachment.mime_type || 'application/octet-stream';
      const blob = new Blob([res.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = getDownloadFileName(attachment, res.headers);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[chat] room item attachment download failed', err);
      alert('첨부파일 다운로드에 실패했습니다.');
    }
  };

  const openRoomItemAttachmentPreview = async (attachment) => {
    if (!attachment?.id || !canPreviewAttachment(attachment)) return downloadRoomItemAttachment(attachment);
    try {
      setPreview({ attachment, loading: true, error: '', url: '', text: '', kind: getAttachmentKind(attachment) });
      const res = await chatApi.get(`/room-item-attachments/${attachment.id}/preview`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: attachment.mime_type || res.headers?.['content-type'] || 'application/octet-stream' });
      const kind = getAttachmentKind(attachment);
      if (kind === 'text') {
        const textValue = await blob.text();
        setPreview({ attachment, loading: false, error: '', url: '', text: textValue.slice(0, 200000), kind });
        return;
      }
      const url = window.URL.createObjectURL(blob);
      setPreview({ attachment, loading: false, error: '', url, text: '', kind });
    } catch (err) {
      console.error('[chat] room item attachment preview failed', err);
      setPreview({ attachment, loading: false, error: '미리보기를 불러오지 못했습니다.', url: '', text: '', kind: getAttachmentKind(attachment) });
    }
  };

  const openNoticesPanel = useCallback(() => {
    setNoticeText('');
    clearRoomItemFiles(noticeFiles, setNoticeFiles);
    setNoticeEditMode(false);
    setNoticesOpen(true);
    loadRoomNotices();
  }, [loadRoomNotices, clearRoomItemFiles, noticeFiles]);

  const openNotesPanel = useCallback(() => {
    setNoteTitle('');
    setNoteBody('');
    clearRoomItemFiles(noteFiles, setNoteFiles);
    setEditingNoteId(null);
    setNotesOpen(true);
    loadRoomNotes();
  }, [loadRoomNotes, clearRoomItemFiles, noteFiles]);

  const saveNotice = async () => {
    if (!activeRoomId || savingNotice) return;
    const body = serializeNoticeEditor().trim();
    if (!body && noticeFiles.length === 0) return alert('공지 내용 또는 첨부파일을 입력하세요.');
    setSavingNotice(true);
    try {
      const keepAttachmentIds = noticeEditMode && activeNotice?.attachments
        ? activeNotice.attachments.map((item) => Number(item.id)).filter((id) => Number.isFinite(id) && id > 0)
        : [];
      const formData = buildRoomItemFormData({
        body,
        keep_attachment_ids: keepAttachmentIds.length ? JSON.stringify(keepAttachmentIds) : '',
      }, noticeFiles);
      await chatApi.post(`/rooms/${activeRoomId}/notices`, formData);
      setNoticeText('');
      clearRoomItemFiles(noticeFiles, setNoticeFiles);
      setNoticeEditMode(false);
      await loadRoomNotices(activeRoomId);
    } catch (err) {
      console.error('[chat] save notice failed', err);
      alert('공지 저장에 실패했습니다. 방장 또는 관리자만 공지를 등록할 수 있습니다.');
    } finally {
      setSavingNotice(false);
    }
  };

  const archiveNotice = async (notice) => {
    if (!activeRoomId || !notice?.id) return;
    if (!window.confirm('이 공지를 내리시겠습니까?')) return;
    try {
      await chatApi.delete(`/rooms/${activeRoomId}/notices/${notice.id}`);
      await loadRoomNotices(activeRoomId);
    } catch (err) {
      console.error('[chat] archive notice failed', err);
      alert('공지 내리기에 실패했습니다.');
    }
  };

  const startEditNote = (note) => {
    setEditingNoteId(note?.id || null);
    setNoteTitle(note?.title || '');
    setNoteBody(note?.body || '');
  };

  const resetNoteEditor = () => {
    setEditingNoteId(null);
    setNoteTitle('');
    setNoteBody('');
    clearRoomItemFiles(noteFiles, setNoteFiles);
  };

  const saveNote = async () => {
    if (!activeRoomId || savingNote) return;
    const body = noteBody.trim();
    if (!body && noteFiles.length === 0) return alert('노트 내용 또는 첨부파일을 입력하세요.');
    setSavingNote(true);
    try {
      const formData = buildRoomItemFormData({ title: noteTitle.trim(), body }, noteFiles);
      if (editingNoteId) {
        await chatApi.patch(`/rooms/${activeRoomId}/notes/${editingNoteId}`, formData);
      } else {
        await chatApi.post(`/rooms/${activeRoomId}/notes`, formData);
      }
      resetNoteEditor();
      await loadRoomNotes(activeRoomId);
    } catch (err) {
      console.error('[chat] save note failed', err);
      alert('노트 저장에 실패했습니다.');
    } finally {
      setSavingNote(false);
    }
  };

  const deleteNote = async (note) => {
    if (!activeRoomId || !note?.id) return;
    if (!window.confirm('이 노트를 삭제하시겠습니까?')) return;
    try {
      await chatApi.delete(`/rooms/${activeRoomId}/notes/${note.id}`);
      if (Number(editingNoteId) === Number(note.id)) resetNoteEditor();
      await loadRoomNotes(activeRoomId);
    } catch (err) {
      console.error('[chat] delete note failed', err);
      alert('노트 삭제에 실패했습니다. 작성자 또는 방 관리자만 삭제할 수 있습니다.');
    }
  };

  const statusText = socketState === 'online' ? '실시간 연결됨' : socketState === 'error' ? '실시간 연결 중' : '오프라인';
  const panelTitle = createMode === 'GROUP' ? '그룹 채팅 만들기' : createMode === 'ADD_MEMBERS' ? '참여자 추가' : '1:1 채팅 시작';


  const chatModalOpen = Boolean((activeRoom && searchToolsOpen) || createMode === 'ADD_MEMBERS' || membersOpen || pushSettingsOpen);
  const shellClassName = [
    'hc-shell',
    isMobileLayout ? (activeRoom ? 'hc-mobile-room-open' : 'hc-mobile-room-list') : '',
  ].filter(Boolean).join(' ');
  const shareLayerOpen = Boolean(sharedOpen || shareMessage);

  useEffect(() => {
    if ((!chatModalOpen && !shareLayerOpen) || typeof document === 'undefined') return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [chatModalOpen, shareLayerOpen]);

  const chatModalPortal = chatModalOpen && typeof document !== 'undefined' ? createPortal(
    <>
      {activeRoom && searchToolsOpen ? (
        <div className="hc-members-backdrop hc-chat-tool-backdrop" onClick={() => setSearchToolsOpen(false)}>
          <section className="hc-tool-modal hc-search-modal" onClick={(e) => e.stopPropagation()}>
            <header className="hc-members-head hc-tool-modal-head">
              <div>
                <strong>대화 검색</strong>
                <span>{activeRoom ? getRoomTitle(activeRoom) : ''}</span>
              </div>
              <button type="button" onClick={() => setSearchToolsOpen(false)}>닫기</button>
            </header>
            <div className="hc-tool-modal-body">
              <div className="hc-message-search hc-message-search--modal">
                <input
                  autoFocus
                  type="search"
                  placeholder="대화 내용 / 파일명 검색"
                  value={messageSearchText}
                  onChange={(e) => setMessageSearchText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      searchMessages();
                      setSearchToolsOpen(false);
                    }
                  }}
                />
                <button type="button" disabled={searchingMessages || loadingMessages} onClick={() => { searchMessages(); setSearchToolsOpen(false); }}>검색</button>
                {messageSearchApplied ? <button type="button" onClick={clearMessageSearch}>해제</button> : null}
              </div>
              <div className="hc-date-jump hc-date-jump--modal">
                <input
                  ref={dateJumpInputRef}
                  type="date"
                  value={getSafeJumpDate(jumpDate) || ''}
                  onInput={(e) => setJumpDate(e.currentTarget.value)}
                  onChange={(e) => setJumpDate(e.currentTarget.value)}
                  aria-label="이동할 날짜"
                />
                <button type="button" disabled={dateJumping || loadingMessages} onClick={() => { jumpToDate(); setSearchToolsOpen(false); }}>날짜 이동</button>
                <button type="button" disabled={loadingMessages} onClick={() => { backToLatestMessages(); setSearchToolsOpen(false); }}>최근</button>
              </div>
            </div>
          </section>
        </div>
      ) : null}

      {pushSettingsOpen ? (
        <div className="hc-members-backdrop hc-chat-tool-backdrop" onClick={() => setPushSettingsOpen(false)}>
          <section className="hc-tool-modal hc-push-settings-modal" onClick={(e) => e.stopPropagation()}>
            <header className="hc-members-head hc-tool-modal-head">
              <div>
                <strong>알림 설정</strong>
                <span>이 기기의 채팅 알림 상태를 확인하고 복구합니다.</span>
              </div>
              <button type="button" onClick={() => setPushSettingsOpen(false)}>닫기</button>
            </header>
            <div className="hc-tool-modal-body hc-push-settings-body">
              <div className="hc-push-status-card">
                <strong>알림이 안 올 때 확인할 것</strong>
                <span>휴대폰 알림 권한, 잠금화면 표시, 팝업 알림, 배터리 제한을 먼저 확인하세요.</span>
              </div>
              <div className="hc-push-action-list">
                <button type="button" onClick={testPushNotification} disabled={testingPush || repairingPush}>
                  {testingPush ? '테스트 발송 중...' : '테스트 알림 보내기'}
                </button>
                <button type="button" className="hc-push-repair-action" onClick={repairPushSubscription} disabled={testingPush || repairingPush}>
                  {repairingPush ? '알림 복구 중...' : '알림 복구'}
                </button>
              </div>
              <p className="hc-push-help-text">
                알림 복구는 현재 브라우저/PWA의 푸시 구독을 새로 만들고 서버에 다시 등록합니다. 알림이 앱을 켜야만 오거나, 특정 휴대폰에서만 안 올 때 사용하세요.
              </p>
            </div>
          </section>
        </div>
      ) : null}

      {createMode === 'ADD_MEMBERS' ? (
        <div className="hc-members-backdrop hc-chat-tool-backdrop" onClick={resetCreatePanel}>
          <section className="hc-tool-modal hc-add-members-modal" onClick={(e) => e.stopPropagation()}>
            <header className="hc-members-head hc-tool-modal-head">
              <div>
                <strong>참여자 추가</strong>
                <span>{activeRoom ? getRoomTitle(activeRoom) : ''}</span>
              </div>
              <button type="button" onClick={resetCreatePanel}>닫기</button>
            </header>
            <div className="hc-tool-modal-body">
              <div className="hc-search-row hc-search-row--modal">
                <input
                  autoFocus
                  placeholder="직원 이름, 사번, 부서 검색"
                  value={userQuery}
                  onChange={(e) => searchUsers(e.target.value)}
                />
              </div>
              {selectedMembers.length > 0 ? (
                <div className="hc-selected-list hc-selected-list--modal">
                  {selectedMembers.map((m) => (
                    <button key={m.id} type="button" onClick={() => toggleSelectedMember(m)}>
                      {formatEmployeeDisplayFromUser(m)} ×
                    </button>
                  ))}
                </div>
              ) : null}
              <div className="hc-user-list hc-user-list--modal">
                {users.map((u) => {
                  const selected = selectedMembers.some((item) => Number(item.id) === Number(u.id));
                  const alreadyMember = isAlreadyRoomMember(u);
                  const className = [selected ? 'is-selected' : '', alreadyMember ? 'is-existing-member' : ''].filter(Boolean).join(' ');
                  return (
                    <button
                      key={u.id}
                      type="button"
                      className={className}
                      disabled={alreadyMember}
                      onClick={() => toggleSelectedMember(u)}
                    >
                      <strong>{selected ? '✓ ' : ''}{formatEmployeeDisplayFromUser(u)}</strong>
                      <span>{alreadyMember ? '이미 참여중인 직원입니다.' : (u.employee_number || u.employee_id || '')}</span>
                    </button>
                  );
                })}
                {userQuery && users.length === 0 ? <div className="hc-empty-small">검색 결과가 없습니다.</div> : null}
                {!userQuery ? <div className="hc-empty-small">추가할 직원을 검색하세요.</div> : null}
              </div>
            </div>
            <footer className="hc-tool-modal-foot">
              <button type="button" className="hc-wide-primary" disabled={creatingRoom || selectedMembers.filter((member) => !isAlreadyRoomMember(member)).length === 0} onClick={openAddMembersHistoryConfirm}>선택한 직원 추가</button>
            </footer>
          </section>
        </div>
      ) : null}

      {membersOpen ? (
        <div className="hc-members-backdrop" onClick={() => setMembersOpen(false)}>
          <section className="hc-members-modal" onClick={(e) => e.stopPropagation()}>
            <header className="hc-members-head">
              <div>
                <strong>참여자</strong>
                <span>{activeRoom ? `${getRoomTitle(activeRoom)} · ${roomMembers.length || activeRoom.member_count || 0}명` : ''}</span>
              </div>
              <div>
                <button type="button" onClick={() => loadRoomMembers()}>새로고침</button>
                <button type="button" onClick={() => setMembersOpen(false)}>닫기</button>
              </div>
            </header>
            <div className="hc-members-body">
              {loadingRoomMembers ? <div className="hc-empty-small">참여자 목록을 불러오는 중...</div> : null}
              {!loadingRoomMembers && roomMembers.length === 0 ? <div className="hc-empty-small">표시할 참여자가 없습니다.</div> : null}
              {roomMembers.map((member) => (
                <div key={member.employee_id} className="hc-member-row">
                  <div className="hc-member-info">
                    <strong>{formatEmployeeDisplayFromUser(member)}</strong>
                    <span>{[member.employee_number || member.login_id, getMemberRoleLabel(member.member_role)].filter(Boolean).join(' · ')}</span>
                  </div>
                  <div className="hc-member-actions">
                    {isCurrentRoomOwner && Number(member.employee_id) !== Number(currentEmployeeId) && normalizeMemberRole(member.member_role) !== 'OWNER' ? (
                      <>
                        {normalizeMemberRole(member.member_role) === 'MANAGER' ? (
                          <button type="button" className="hc-member-role-btn" disabled={Number(roleChangingMemberId) === Number(member.employee_id)} onClick={() => changeMemberRole(member, 'MEMBER')}>관리자 해제</button>
                        ) : (
                          <button type="button" className="hc-member-role-btn" disabled={Number(roleChangingMemberId) === Number(member.employee_id)} onClick={() => changeMemberRole(member, 'MANAGER')}>관리자 지정</button>
                        )}
                        <button type="button" className="hc-member-role-btn owner" disabled={Number(roleChangingMemberId) === Number(member.employee_id)} onClick={() => changeMemberRole(member, 'OWNER')}>방장 위임</button>
                      </>
                    ) : null}
                    {canRemoveMember(member) ? (
                      <button type="button" className="hc-member-remove" disabled={Number(removingMemberId) === Number(member.employee_id)} onClick={() => removeMemberFromRoom(member)}>
                        {Number(removingMemberId) === Number(member.employee_id) ? '처리 중' : '내보내기'}
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
            {canAddMembers ? (
              <footer className="hc-members-foot">
                <button
                  type="button"
                  onClick={() => {
                    setMembersOpen(false);
                    openCreatePanel('ADD_MEMBERS');
                  }}
                >
                  참여자 추가
                </button>
              </footer>
            ) : null}
          </section>
        </div>
      ) : null}
    </>,
    document.body
  ) : null;

  const previewModalPortal = preview && typeof document !== 'undefined' ? createPortal(
    <div
      className="hc-preview-backdrop"
      onClick={closePreview}
      onDragOver={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <div className="hc-preview-modal" onClick={(e) => e.stopPropagation()}>
        <header className="hc-preview-head">
          <div>
            <strong title={preview.attachment?.original_name}>{preview.attachment?.original_name || '첨부파일 미리보기'}</strong>
            <span>{formatFileSize(preview.attachment?.size_bytes)}</span>
          </div>
          <button type="button" onClick={closePreview}>닫기</button>
        </header>
        <section className="hc-preview-body">
          {preview.loading ? <div className="hc-preview-state">미리보기를 불러오는 중입니다.</div> : null}
          {preview.error ? <div className="hc-preview-state error">{preview.error}</div> : null}
          {!preview.loading && !preview.error && preview.kind === 'image' ? <img src={preview.url} alt={preview.attachment?.original_name || '이미지'} /> : null}
          {!preview.loading && !preview.error && preview.kind === 'pdf' ? <iframe title="PDF preview" src={preview.url} /> : null}
          {!preview.loading && !preview.error && preview.kind === 'video' ? (
            <div className="hc-preview-video-wrap">
              <video
                src={preview.url}
                controls
                playsInline
                className="hc-preview-media hc-preview-video"
                onClick={(event) => {
                  const video = event.currentTarget;
                  if (video.paused) video.play().catch(() => {});
                }}
              >
                동영상을 재생할 수 없습니다.
              </video>
              <button
                type="button"
                className="hc-preview-video-play"
                onClick={(event) => {
                  const video = event.currentTarget.parentElement?.querySelector('video');
                  if (!video) return;
                  if (video.paused) video.play().catch(() => {});
                  else video.pause();
                }}
              >
                ▶ 재생 / 일시정지
              </button>
            </div>
          ) : null}
          {!preview.loading && !preview.error && preview.kind === 'audio' ? <audio src={preview.url} controls /> : null}
          {!preview.loading && !preview.error && preview.kind === 'text' ? <pre>{preview.text}</pre> : null}
        </section>
        <footer className="hc-preview-foot">
          <button type="button" onClick={() => downloadAttachment(preview.attachment)}>다운로드</button>
        </footer>
      </div>
    </div>,
    document.body
  ) : null;

  const noticeModalPortal = noticesOpen && typeof document !== 'undefined' ? createPortal(
    (
      <div className="hc-note-backdrop" onClick={() => setNoticesOpen(false)}>
                  <section className="hc-note-modal hc-notice-modal-clean" onClick={(e) => e.stopPropagation()}>
                    <header className="hc-notice-modal-head">
                      <div>
                        <strong>{noticeEditMode ? (activeNotice ? '공지 수정' : '공지 등록') : '공지 보기'}</strong>
                        <span>{activeRoom?.title || activeRoom?.name || ''}</span>
                      </div>
                      <button
                        type="button"
                        className="hc-note-close-button"
                        onClick={() => {
                          setNoticeEditMode(false);
                          setNoticeText('');
                          clearRoomItemFiles(noticeFiles, setNoticeFiles);
                          setNoticesOpen(false);
                        }}
                      >
                        닫기
                      </button>
                    </header>
                    <div className="hc-note-body hc-notice-body-simple">
                      {loadingNotices ? <div className="hc-empty-small">공지 불러오는 중...</div> : null}
      
                      {!loadingNotices && activeNotice && !noticeEditMode ? (
                        <article className="hc-notice-view">
                          <div className="hc-notice-view-meta">
                            <span>{formatMessageDate(activeNotice.created_at)} {formatMessageTime(activeNotice.created_at)}</span>
                            <span>{activeNotice.created_by_name || '알 수 없음'}</span>
                          </div>
                          <div className="hc-notice-view-content">
                            {activeNotice.body || (activeNotice.attachments || []).length ? (
                              <RoomItemContent
                                body={activeNotice.body}
                                attachments={activeNotice.attachments}
                                onPreview={openRoomItemAttachmentPreview}
                                onDownload={downloadRoomItemAttachment}
                              />
                            ) : (
                              <div className="hc-empty-small">공지 내용이 없습니다.</div>
                            )}
                          </div>
                        </article>
                      ) : null}
      
                      {!loadingNotices && !activeNotice && !noticeEditMode ? (
                        <div className="hc-notice-current-simple is-empty">등록된 공지가 없습니다.</div>
                      ) : null}
      
                      {canManageCurrentRoom ? (
                        <div className="hc-notice-admin-actions">
                          {!noticeEditMode ? (
                            <button
                              type="button"
                              className="primary"
                              onClick={() => {
                                setNoticeText(activeNotice?.body || '');
                                clearRoomItemFiles(noticeFiles, setNoticeFiles);
                                setNoticeEditMode(true);
                                window.setTimeout(() => hydrateNoticeEditor(activeNotice?.body || '', activeNotice?.attachments || []), 0);
                              }}
                            >
                              {activeNotice ? '수정' : '공지 등록'}
                            </button>
                          ) : null}
                          {activeNotice && !noticeEditMode ? (
                            <button type="button" onClick={() => archiveNotice(activeNotice)}>내리기</button>
                          ) : null}
                        </div>
                      ) : null}
      
                      {canManageCurrentRoom && noticeEditMode ? (
                        <div
                          className="hc-notice-editor hc-notice-editor-simple hc-room-item-drop-zone hc-notice-inline-editor-wrap"
                          onDragEnterCapture={handleRoomItemDragOver}
                          onDragOverCapture={handleRoomItemDragOver}
                          onDropCapture={handleNoticeEditorDrop}
                          onPasteCapture={handleNoticeEditorPaste}
                        >
                          <div
                            ref={noticeEditorRef}
                            className="hc-notice-inline-editor"
                            contentEditable
                            suppressContentEditableWarning
                            data-placeholder="공지 내용을 입력하세요. 이미지는 본문 위치에 붙고, 일반 파일은 첨부파일로 추가됩니다."
                            onInput={(e) => setNoticeText(e.currentTarget.innerText || '')}
                          />
                          <input
                            ref={noticeFileInputRef}
                            type="file"
                            multiple
                            accept=".pdf,.txt,.csv,.xlsx,.xls,.doc,.docx,.ppt,.pptx,.hwp,.hwpx,.zip,.7z"
                            className="hc-hidden-file-input"
                            onChange={(e) => {
                              addRoomItemFiles(e.target.files, setNoticeFiles);
                              e.target.value = '';
                            }}
                          />
                          <div className="hc-notice-editor-foot">
                            <button type="button" className="hc-room-item-file-pick" onClick={() => noticeFileInputRef.current?.click?.()}>첨부파일</button>
                            <span>이미지는 본문에 드래그하거나 Ctrl+V로 붙여넣고, 일반 파일은 첨부파일로 추가합니다.</span>
                          </div>
                          <PendingRoomItemFiles files={noticeFiles.filter((item) => !item.inline)} onRemove={(id) => removeRoomItemFile(id, setNoticeFiles)} />
                          <div className="hc-note-actions">
                            <button type="button" className="primary" disabled={savingNotice} onClick={saveNotice}>{savingNotice ? '저장 중...' : '저장'}</button>
                            <button type="button" disabled={savingNotice} onClick={() => { setNoticeEditMode(false); setNoticeText(''); clearRoomItemFiles(noticeFiles, setNoticeFiles); }}>취소</button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </section>
                </div>
    ),
    document.body
  ) : null;
  const shareLayerPortal = (sharedOpen || shareMessage) && typeof document !== 'undefined' ? createPortal(
    <>
      {sharedOpen ? (
        <div className="hc-shared-backdrop" onClick={() => setSharedOpen(false)}>
          <section className="hc-shared-modal" onClick={(e) => e.stopPropagation()}>
            <header className="hc-shared-head">
              <div>
                <strong>공유함</strong>
                <span>{sharedScope === 'room' && activeRoom ? getRoomTitle(activeRoom) : '내가 참여한 전체 채팅방'}</span>
              </div>
              <button type="button" onClick={() => setSharedOpen(false)}>닫기</button>
            </header>
            <div className="hc-shared-toolbar">
              <div className="hc-shared-segments">
                <button type="button" className={sharedScope === 'room' ? 'active' : ''} disabled={!activeRoom} onClick={() => changeSharedScope('room')}>이 방</button>
                <button type="button" className={sharedScope === 'mine' ? 'active' : ''} onClick={() => changeSharedScope('mine')}>내 전체</button>
              </div>
              <div className="hc-shared-tabs">
                <button type="button" className={sharedType === 'all' ? 'active' : ''} onClick={() => changeSharedType('all')}>전체</button>
                <button type="button" className={sharedType === 'links' ? 'active' : ''} onClick={() => changeSharedType('links')}>링크</button>
                <button type="button" className={sharedType === 'images' ? 'active' : ''} onClick={() => changeSharedType('images')}>사진</button>
                <button type="button" className={sharedType === 'files' ? 'active' : ''} onClick={() => changeSharedType('files')}>파일</button>
              </div>
            </div>
            <div className="hc-shared-list">
              {loadingSharedItems ? (
                <div className="hc-shared-empty">공유 자료를 불러오는 중입니다.</div>
              ) : sharedItems.length === 0 ? (
                <div className="hc-shared-empty">표시할 공유 자료가 없습니다.</div>
              ) : sharedItems.map((item) => {
                const attachment = item.attachment_id ? makeAttachmentFromSharedItem(item) : null;
                const icon = item.item_type === 'LINK' ? '🔗' : item.item_type === 'IMAGE' ? '🖼️' : getAttachmentIcon(attachment || {});
                return (
                  <article key={item.id} className={`hc-shared-item ${String(item.item_type || '').toLowerCase()}`}>
                    <button type="button" className="hc-shared-main" onClick={() => handleSharedItemOpen(item)}>
                      <span className="hc-shared-icon">{icon}</span>
                      <span className="hc-shared-text">
                        <strong title={item.title || item.original_name || item.url}>{item.title || item.original_name || item.url || '공유 자료'}</strong>
                        <em>{[item.room_title, item.sender_display, formatRoomTime(item.created_at)].filter(Boolean).join(' · ')}</em>
                        {item.url ? <small>{item.url}</small> : null}
                        {item.size_bytes ? <small>{formatFileSize(item.size_bytes)}</small> : null}
                      </span>
                    </button>
                    {item.attachment_id ? (
                      <button type="button" className="hc-shared-download" onClick={() => handleSharedItemDownload(item)}>다운로드</button>
                    ) : null}
                  </article>
                );
              })}
            </div>
          </section>
        </div>
      ) : null}


      {shareMessage ? (
        <div className="hc-share-backdrop" onClick={closeShareMessage}>
          <section className="hc-share-modal" onClick={(event) => event.stopPropagation()}>
            <header className="hc-share-head">
              <div>
                <strong>다른 채팅방에 공유</strong>
                <span>보낸 사람 정보와 메시지/파일을 함께 공유합니다. 모바일에서는 방을 선택한 뒤 하단의 공유 버튼을 누르세요.</span>
              </div>
              <button type="button" onClick={closeShareMessage}>닫기</button>
            </header>

            <div className="hc-share-source">
              <b>{shareMessage.message_type === 'FILE' ? '공유할 파일/메시지' : '공유할 메시지'}</b>
              <p>{getSharePreviewLine(shareMessage, roomMembers) || '첨부파일'}</p>
            </div>

            <div className="hc-share-room-list">
              {rooms.filter((room) => Number(room.id) !== Number(activeRoomId)).length === 0 ? (
                <div className="hc-share-empty">공유할 수 있는 다른 채팅방이 없습니다.</div>
              ) : rooms.filter((room) => Number(room.id) !== Number(activeRoomId)).map((room) => {
                const roomId = Number(room.id);
                const checked = shareTargetRoomIds.includes(roomId);
                return (
                  <label key={room.id} className={`hc-share-room ${checked ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleShareTargetRoom(roomId)}
                    />
                    <span className="hc-room-type-mini">{getRoomTypeInfo(room).label}</span>
                    <span className="hc-share-room-text">
                      <strong>{getRoomTitle(room)}</strong>
                      <em>{room.room_type === 'DIRECT' ? '1:1 대화' : `참여자 ${room.member_count || 0}명`}</em>
                    </span>
                  </label>
                );
              })}
            </div>

            <footer className="hc-share-actions">
              <button type="button" onClick={closeShareMessage} disabled={sharingMessage}>취소</button>
              <button
                type="button"
                className="primary"
                disabled={sharingMessage || shareTargetRoomIds.length === 0}
                onClick={shareMessageToRooms}
              >
                {sharingMessage ? '공유 중...' : `선택한 ${shareTargetRoomIds.length || ''}개 방에 공유`}
              </button>
            </footer>
          </section>
        </div>
      ) : null}

    </>,
    document.body
  ) : null;


  return (
    <div className={shellClassName} data-chat-open-version={CHAT_OPEN_VERSION} data-active-room-id={activeRoomId || ''}>
      <aside className={`hc-sidebar ${sidebarOpen ? 'is-open' : ''}`}>
        <div className="hc-side-head">
          <div className="hc-side-brand">
            <h1>Hansero Chat {totalUnreadCount > 0 ? <em className="hc-total-badge" aria-label={`읽지 않은 메시지 ${totalUnreadCount}개`}>{formatBadgeCount(totalUnreadCount)}</em> : null}</h1>
            <span className={`hc-status ${socketState}`}>{statusText}</span>
          </div>
          <div className="hc-create-menu-wrap">
            <button className="hc-new-chat-btn" type="button" onClick={() => setCreateMenuOpen((v) => !v)}>
              + 새 채팅
            </button>
            {createMenuOpen ? (
              <div className="hc-create-menu">
                <button type="button" onClick={() => openCreatePanel('DIRECT')}>1:1 채팅 시작</button>
                <button type="button" onClick={() => openCreatePanel('GROUP')}>그룹 채팅 만들기</button>
                <button type="button" onClick={openSelfRoom}>나와의 채팅</button>
              </div>
            ) : null}
          </div>
        </div>

        {createMode && createMode !== 'ADD_MEMBERS' && (
          <div className="hc-search-panel">
            <div className="hc-panel-title-row">
              <strong>{panelTitle}</strong>
              <button onClick={resetCreatePanel}>닫기</button>
            </div>

            {createMode === 'GROUP' ? (
              <input
                className="hc-full-input"
                placeholder="채팅방 이름"
                value={groupTitle}
                onChange={(e) => setGroupTitle(e.target.value)}
              />
            ) : null}

            <div className="hc-search-row">
              <input
                autoFocus
                placeholder="직원 이름, 사번, 부서 검색"
                value={userQuery}
                onChange={(e) => searchUsers(e.target.value)}
              />
            </div>

            {createMode !== 'DIRECT' && selectedMembers.length > 0 ? (
              <div className="hc-selected-list">
                {selectedMembers.map((m) => (
                  <button key={m.id} onClick={() => toggleSelectedMember(m)}>
                    {formatEmployeeDisplayFromUser(m)} ×
                  </button>
                ))}
              </div>
            ) : null}

            <div className="hc-user-list">
              {users.map((u) => {
                const selected = selectedMembers.some((item) => Number(item.id) === Number(u.id));
                const alreadyMember = isAlreadyRoomMember(u);
                const className = [selected ? 'is-selected' : '', alreadyMember ? 'is-existing-member' : ''].filter(Boolean).join(' ');
                return createMode === 'DIRECT' ? (
                  <button key={u.id} onClick={() => openDirectRoom(u.id)}>
                    <strong>{formatEmployeeDisplayFromUser(u)}</strong>
                    <span>{u.employee_number || u.employee_id || ''}</span>
                  </button>
                ) : (
                  <button key={u.id} className={className} disabled={alreadyMember} onClick={() => toggleSelectedMember(u)}>
                    <strong>{selected ? '✓ ' : ''}{formatEmployeeDisplayFromUser(u)}</strong>
                    <span>{alreadyMember ? '이미 참여중인 직원입니다.' : (u.employee_number || u.employee_id || '')}</span>
                  </button>
                );
              })}
              {userQuery && users.length === 0 ? <div className="hc-empty-small">검색 결과가 없습니다.</div> : null}
            </div>

            {createMode === 'GROUP' ? (
              <button className="hc-wide-primary" disabled={creatingRoom} onClick={createGroupRoom}>그룹 채팅방 만들기</button>
            ) : null}
            {createMode === 'ADD_MEMBERS' ? (
              <button className="hc-wide-primary" disabled={creatingRoom || selectedMembers.filter((member) => !isAlreadyRoomMember(member)).length === 0} onClick={openAddMembersHistoryConfirm}>선택한 직원 추가</button>
            ) : null}
          </div>
        )}

        <div className="hc-room-list">
          {loadingRooms ? <div className="hc-empty-small">채팅방 불러오는 중...</div> : null}
          {!loadingRooms && rooms.length === 0 ? (
            <div className="hc-empty-box">
              <strong>아직 채팅방이 없습니다.</strong>
              <span>상단의 새 채팅 버튼으로 대화를 시작하세요.</span>
            </div>
          ) : null}
          {selfRooms.length > 0 ? (
            <div className="hc-self-pinned-rooms" aria-label="나와의 대화">
              {selfRooms.map((room) => {
                const typeInfo = getRoomTypeInfo(room);
                const notifyState = hcNotifyState(room);
                return (
                <button
                  key={room.id}
                  className={`hc-room hc-room--self-pinned ${Number(activeRoomId) === Number(room.id) ? 'active' : ''} ${getRoomUnreadCount(room) > 0 ? 'has-unread' : ''} ${Number(dragShareTargetRoomId) === Number(room.id) ? 'is-share-drop-target' : ''} ${dragShareMessage && Number(activeRoomId) === Number(room.id) ? 'is-share-drop-blocked' : ''}`}
                  onDragOver={(event) => handleRoomDragOver(room, event)}
                  onDragLeave={(event) => handleRoomDragLeave(room, event)}
                  onDrop={(event) => handleRoomDropShare(room, event)}
                  onClick={() => openRoom(room.id)}
                >
                  <div className="hc-room-main">
                    <div className="hc-room-title-row">
                      <em className={`hc-room-type-pill ${typeInfo.className}`}>{getRoomTypeLabel(room)}</em>
                      <strong>{getRoomTitle(room)}</strong>
                    </div>
                  </div>
                  <div className="hc-room-meta">
                    <em
                      role="button"
                      tabIndex={0}
                      className={`hc-room-alert-chip hc-room-alert-chip--${notifyState.className || 'notice'}`}
                      title={`${notifyState.label} · 클릭하면 ${notifyState.nextLabel}으로 변경`}
                      aria-label={`${getRoomTitle(room)} 알림 상태: ${notifyState.label}. 클릭하면 ${notifyState.nextLabel}으로 변경`}
                      onClick={(event) => cycleRoomNotificationMode(event, room)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') cycleRoomNotificationMode(event, room);
                      }}
                    >
                      {notifyState.label || '알림'}
                    </em>
                    <span>{formatRoomTime(room.last_message_at || room.updated_at)}</span>
                    {getRoomUnreadCount(room) > 0 ? <em className="hc-unread-badge" aria-label={`읽지 않은 메시지 ${getRoomUnreadCount(room)}개`}>{formatBadgeCount(getRoomUnreadCount(room))}</em> : null}
                  </div>
                </button>
                );
              })}
            </div>
          ) : null}

          {roomSections.map((section) => {
            const collapsed = Boolean(collapsedRoomSections?.[section.key]);
            return (
            <section className={`hc-room-section hc-room-section--${section.key} ${collapsed ? 'is-collapsed' : ''}`} key={section.key}>
              <button
                type="button"
                className="hc-room-section-head"
                onClick={() => toggleRoomSectionCollapsed(section.key)}
                aria-expanded={!collapsed}
              >
                <span className="hc-room-section-chevron" aria-hidden="true" />
                <span className="hc-room-section-title">
                  <strong>{section.title}</strong>
                </span>
                <span className="hc-room-section-count">{section.rooms.length}</span>
                {section.unreadTotal > 0 ? (
                  <em className="hc-unread-badge hc-room-section-unread" aria-label={`${section.title} 읽지 않은 메시지 ${section.unreadTotal}개`}>
                    {formatBadgeCount(section.unreadTotal)}
                  </em>
                ) : null}
              </button>

              {!collapsed ? (
              <div className="hc-room-section-body">
                {section.rooms.map((room) => {
                  const typeInfo = getRoomTypeInfo(room);
                  const notifyState = hcNotifyState(room);
                  return (
                  <button
                    key={room.id}
                    className={`hc-room ${Number(activeRoomId) === Number(room.id) ? 'active' : ''} ${getRoomUnreadCount(room) > 0 ? 'has-unread' : ''} ${Number(dragShareTargetRoomId) === Number(room.id) ? 'is-share-drop-target' : ''} ${dragShareMessage && Number(activeRoomId) === Number(room.id) ? 'is-share-drop-blocked' : ''}`}
                    onDragOver={(event) => handleRoomDragOver(room, event)}
                    onDragLeave={(event) => handleRoomDragLeave(room, event)}
                    onDrop={(event) => handleRoomDropShare(room, event)}
                    onClick={() => openRoom(room.id)}
                  >
                    <div className="hc-room-main">
                      <div className="hc-room-title-row">
                        <em className={`hc-room-type-pill ${typeInfo.className}`}>{getRoomTypeLabel(room)}</em>
                        <strong>{getRoomTitle(room)}</strong>
                      </div>
                    </div>
                    <div className="hc-room-meta">
                      <em
                        role="button"
                        tabIndex={0}
                        className={`hc-room-alert-chip hc-room-alert-chip--${notifyState.className || 'notice'}`}
                        title={`${notifyState.label} · 클릭하면 ${notifyState.nextLabel}으로 변경`}
                        aria-label={`${getRoomTitle(room)} 알림 상태: ${notifyState.label}. 클릭하면 ${notifyState.nextLabel}으로 변경`}
                        onClick={(event) => cycleRoomNotificationMode(event, room)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') cycleRoomNotificationMode(event, room);
                        }}
                      >
                        {notifyState.label || '알림'}
                      </em>
                      <span>{formatRoomTime(room.last_message_at || room.updated_at)}</span>
                      {getRoomUnreadCount(room) > 0 ? <em className="hc-unread-badge" aria-label={`읽지 않은 메시지 ${getRoomUnreadCount(room)}개`}>{formatBadgeCount(getRoomUnreadCount(room))}</em> : null}
                    </div>
                  </button>
                  );
                })}
              </div>
              ) : null}
            </section>
            );
          })}
        </div>

        <div className="hc-side-foot">
          <div>
            <strong>{user?.name || '사용자'}</strong>
            <span>{socketState === 'online' ? '접속 중' : statusText}</span>
          </div>
          <button type="button" onClick={onLogout}>로그아웃</button>
        </div>
      </aside>

      {sidebarOpen ? <button className="hc-backdrop" onClick={() => setSidebarOpen(false)} aria-label="close" /> : null}

      <main className={`hc-main ${dragActive && !roomItemModalOpen ? 'is-dragging' : ''}`} onPaste={handleClipboardPaste} onDragEnter={handleDragEnter} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
        <header className="hc-topbar">
          <div className="hc-top-main">
            <button
              className={`hc-menu-btn ${isMobileLayout && activeRoom ? 'hc-menu-btn--back' : ''}`}
              onClick={() => {
                if (isMobileLayout && activeRoom) {
                  closeMobileRoom();
                  return;
                }
                setSidebarOpen(true);
              }}
              aria-label={isMobileLayout && activeRoom ? '채팅방 목록으로 돌아가기' : (totalUnreadCount > 0 ? `채팅방 목록 열기, 읽지 않은 메시지 ${totalUnreadCount}개` : '채팅방 목록 열기')}
            >
              <span>{isMobileLayout && activeRoom ? '←' : '☰'}</span>
              {!(isMobileLayout && activeRoom) && totalUnreadCount > 0 ? <em className="hc-menu-badge" aria-label={`전체 읽지 않은 메시지 ${totalUnreadCount}개`}>{formatBadgeCount(totalUnreadCount)}</em> : null}
            </button>
            <div className="hc-title-area">
              {activeRoom ? (
                <div className="hc-active-room-title">
                  {canViewMembers ? (
                    <button
                      type="button"
                      className="hc-room-type-pill-wrap"
                      onClick={openMembersPanel}
                      onMouseEnter={() => { if (!roomMembers.length) loadRoomMembers(); }}
                      onFocus={() => { if (!roomMembers.length) loadRoomMembers(); }}
                      aria-label="참여자 보기"
                    >
                      <em className={`hc-room-type-pill ${getRoomTypeInfo(activeRoom).className}`}>{getRoomHeaderMeta(activeRoom)}</em>
                      <span className="hc-room-member-popover" role="tooltip">
                        <strong>참여자</strong>
                        {loadingRoomMembers ? (
                          <span className="hc-room-member-popover-empty">불러오는 중...</span>
                        ) : activeRoomMemberPreview.length > 0 ? (
                          <>
                            {activeRoomMemberPreview.map((member) => (
                              <span key={member.employee_id} className="hc-room-member-popover-row">
                                <span>{formatEmployeeDisplayFromUser(member)}</span>
                              </span>
                            ))}
                            {Number(activeRoom.member_count || 0) > activeRoomMemberPreview.length ? (
                              <span className="hc-room-member-popover-more">외 {Number(activeRoom.member_count || 0) - activeRoomMemberPreview.length}명</span>
                            ) : null}
                          </>
                        ) : (
                          <span className="hc-room-member-popover-empty">표시할 참여자가 없습니다.</span>
                        )}
                      </span>
                    </button>
                  ) : (
                    <em className={`hc-room-type-pill ${getRoomTypeInfo(activeRoom).className}`}>{getRoomHeaderMeta(activeRoom)}</em>
                  )}
                  <h2>{getRoomTitle(activeRoom)}</h2>
                  <button
                    type="button"
                    className={`hc-room-search-trigger ${messageSearchApplied ? 'is-active' : ''}`}
                    onClick={() => { setRoomMenuOpen(false); setSearchToolsOpen(true); }}
                    aria-label={`${getRoomTitle(activeRoom)} 대화 검색`}
                    title={messageSearchApplied ? `검색 중: ${messageSearchApplied}` : '이 대화방 검색'}
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
                      <circle cx="10.8" cy="10.8" r="6.3" fill="none" stroke="currentColor" strokeWidth="2" />
                      <path d="m15.5 15.5 5 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <span>검색</span>
                  </button>
                </div>
              ) : (
                <>
                  <h2>채팅방을 선택하세요.</h2>
                  <span>직원 1:1, 그룹, 부서, 프로젝트 채팅으로 확장 가능한 구조입니다.</span>
                </>
              )}
              {totalUnreadCount > 0 ? (
                <em className="hc-mobile-unread-summary" aria-label={`전체 읽지 않은 메시지 ${totalUnreadCount}개`}>
                  읽지 않음 {formatBadgeCount(totalUnreadCount)}
                </em>
              ) : null}
            </div>
            {lineHistoryImportOpen && activeRoom ? <LineHistoryImport key={activeRoom.id} room={{...activeRoom,title:getRoomTitle(activeRoom)}} onClose={() => setLineHistoryImportOpen(false)} onImported={async (summary) => { await loadRooms(); await loadMessages(summary.roomId); }} /> : null}
            <div className="hc-room-actions">
              {activeRoom ? (
                <div className="hc-room-menu-wrap">
                  <button
                    type="button"
                    className="hc-room-menu-trigger"
                    onClick={() => setRoomMenuOpen((open) => !open)}
                    aria-expanded={roomMenuOpen}
                    aria-haspopup="menu"
                  >
                    메뉴
                  </button>
                  {roomMenuOpen ? (
                    <div className="hc-room-menu" role="menu">
                      {canRenameRoom(activeRoom) && canManageCurrentRoom ? (
                        <button type="button" role="menuitem" onClick={() => { setRoomMenuOpen(false); openRenameRoom(); }}>방 이름 변경</button>
                      ) : null}
                      <button type="button" role="menuitem" onClick={() => { setRoomMenuOpen(false); openNoticesPanel(); }}>{canManageCurrentRoom ? '공지 관리' : '공지 보기'}</button>
                      <button type="button" role="menuitem" onClick={() => { setRoomMenuOpen(false); openNotesPanel(); }}>노트</button>
                      <button type="button" role="menuitem" onClick={() => { setRoomMenuOpen(false); setPushSettingsOpen(true); }}>알림 설정</button>
                      <button type="button" role="menuitem" onClick={() => { setRoomMenuOpen(false); openExportPanel(); }}>대화 백업</button>
                      {((activeRoom.room_type === 'DIRECT' && !isSelfRoom) || (activeRoom.room_type === 'GROUP' && (canManageCurrentRoom || user?.roles?.includes('SUPER_ADMIN')))) ? <button type="button" role="menuitem" onClick={() => { setRoomMenuOpen(false); setLineHistoryImportOpen(true); }}>과거 LINE 대화 가져오기</button> : null}
                      {!isSelfRoom ? (
                        <button type="button" role="menuitem" className="hc-room-menu-danger-soft" disabled={leavingRoom || deletingRoom} onClick={() => { setRoomMenuOpen(false); leaveCurrentRoom(); }}>나가기</button>
                      ) : null}
                      {activeRoom.room_type !== 'DIRECT' && isCurrentRoomOwner ? (
                        <button type="button" role="menuitem" className="hc-room-menu-danger" disabled={leavingRoom || deletingRoom} onClick={() => { setRoomMenuOpen(false); deleteCurrentRoom(); }}>방 삭제</button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </header>

        {activeRoom && activeNotice ? (
          <button type="button" className="hc-room-notice-strip" onClick={openNoticesPanel} title={activeNotice.body || '공지'}>
            <span className="hc-room-notice-strip__badge">공지</span>
            <span className="hc-room-notice-strip__text">{activeNotice.body}</span>
            <span className="hc-room-notice-strip__more">보기</span>
          </button>
        ) : null}

        <section
          ref={messagesRef}
          className="hc-messages"
          onScroll={(e) => {
            if (e.currentTarget.scrollTop < 24) loadOlderMessages();
          }}
        >
          {!activeRoom ? (
            <div className="hc-center-empty">
              <strong>{roomOpenError || '채팅방을 선택하세요.'}</strong>
              <span>{roomOpenError ? '방 목록을 확인한 뒤 다시 선택해 주세요.' : '왼쪽의 새 채팅 버튼으로 대화를 시작할 수 있습니다.'}</span>
            </div>
          ) : loadingMessages ? (
            <div className="hc-center-empty">메시지 불러오는 중...</div>
          ) : messages.length === 0 ? (
            <div className="hc-center-empty">{roomOpenError || '아직 메시지가 없습니다.'}</div>
          ) : (
            <>
              {hasOlderMessages ? (
                <button className="hc-load-older" disabled={loadingOlderMessages} onClick={loadOlderMessages}>
                  {loadingOlderMessages ? '이전 대화 불러오는 중...' : '이전 대화 더 보기'}
                </button>
              ) : (
                <div className="hc-history-start">표시 가능한 대화의 시작입니다.</div>
              )}
              {dateJumpNotice ? <div className="hc-date-jump-notice">{dateJumpNotice}</div> : null}
              {messages.map((msg, index) => {
                const mine = Number(msg.sender_employee_id) === Number(currentEmployeeId);
                const system = msg.message_type === 'SYSTEM';
                const displayTimestamp = getMessageDisplayTimestamp(msg);
                const currentDateKey = getDateKey(displayTimestamp);
                const prevDateKey = index > 0 ? getDateKey(getMessageDisplayTimestamp(messages[index - 1])) : '';
                const forceSearchDateHeader = Boolean(messageSearchApplied || messageSearchText.trim());
                const showDateHeader = currentDateKey && (forceSearchDateHeader || currentDateKey !== prevDateKey);
                const showReadMarker = readMarkerMessageId !== null
                  && !messageSearchApplied
                  && !jumpDate
                  && Number(msg.id) > Number(readMarkerMessageId)
                  && (index === 0 || Number(messages[index - 1]?.id || 0) <= Number(readMarkerMessageId));
                const metadata = getMessageMetadata(msg);
                const reply = metadata.reply || null;
                const sharedCopyInfo = getSharedCopyInfo(msg, metadata);
                const deleted = isMessageDeleted(msg);
                const senderParts = getEmployeeDisplayPartsFromMessage(msg, roomMembers);
                const unreadForMessage = Number(msg.unread_count || msg.unread_member_count || 0);
                const showMessageReadCount = mine && !system && !deleted && unreadForMessage > 0;

                return (
                  <React.Fragment key={msg.id}>
                    {showDateHeader ? (
                      <div className="hc-date-separator">{formatMessageDate(displayTimestamp)}</div>
                    ) : null}
                    {showReadMarker ? <div className="hc-read-marker">여기까지 읽으셨습니다.</div> : null}
                    {system ? (
                      <div className="hc-system-message">{sanitizeSystemMessageBody(getMessageDisplayBody(msg))}</div>
                    ) : (
                      <div
                        className={`hc-message ${mine ? 'mine' : 'other'} ${deleted ? 'is-deleted' : ''}`}
                        data-message-id={msg.id}
                        onContextMenu={(event) => {
                          event.preventDefault();
                          openMessageMenu(msg, event);
                        }}
                        onPointerDown={(event) => startMessageLongPress(msg, event)}
                        onPointerUp={clearMessageLongPressTimer}
                        onPointerLeave={clearMessageLongPressTimer}
                        onPointerCancel={clearMessageLongPressTimer}
                      >
                        <div className="hc-message-meta">
                          <SenderName parts={senderParts} />
                          <span>{formatMessageTime(displayTimestamp)}</span>
                        </div>
                        <div className="hc-message-body-row">
                          {showMessageReadCount ? (
                            <em className="hc-message-unread-count" title={`${unreadForMessage}명이 아직 읽지 않았습니다.`}>{formatBadgeCount(unreadForMessage)}</em>
                          ) : null}
                          <div className={`hc-bubble ${msg.message_type === 'FILE' ? 'is-file' : ''}`}>
                          {deleted ? (
                            <div className="hc-deleted-message">삭제된 메시지입니다.</div>
                          ) : (
                            <>
                              {reply ? <LineReplyPreview reply={reply} onJump={id=>{
                                const target=messagesRef.current?.querySelector?.(`[data-message-id="${id}"]`);
                                if(!target)return false;
                                target.scrollIntoView?.({block:'center',behavior:'smooth'});
                                target.classList.remove('hc-message-shake');
                                window.setTimeout(()=>target.classList.add('hc-message-shake'),40);
                                window.setTimeout(()=>target.classList.remove('hc-message-shake'),1400);
                                return true;
                              }}/> : null}
                              {sharedCopyInfo ? (
                                <SharedCopyContent info={sharedCopyInfo} />
                              ) : getMessageDisplayBody(msg) ? (
                                <div className={`hc-bubble-text ${reply ? 'is-reply-body' : ''}`}>{renderMessageTextWithLinksAndHighlight(getMessageDisplayBody(msg), messageSearchApplied || messageSearchText)}</div>
                              ) : <span />}
                              {Array.isArray(msg.attachments) && msg.attachments.length > 0 ? (
                                <div className="hc-attachment-list">
                                  {msg.attachments.map((att) => (
                                    <AttachmentCard
                                      key={att.id}
                                      attachment={att}
                                      mine={mine}
                                      onDownload={downloadAttachment}
                                      onPreview={openAttachmentPreview}
                                      onContentResize={() => {
                                        if (keepDateJumpTopRef.current || jumpDate || messageSearchApplied) {
                                          if (keepDateJumpTopRef.current) scrollToTopStable();
                                          return;
                                        }
                                        scrollToBottom();
                                      }}
                                    />
                                  ))}
                                </div>
                              ) : null}
                            </>
                          )}
                          </div>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
              {hasNewerMessages ? (
                <button className="hc-load-newer" disabled={loadingNewerMessages} onClick={loadNewerMessages}>
                  {loadingNewerMessages ? '다음 대화 불러오는 중...' : '다음 대화 더 보기'}
                </button>
              ) : null}
            </>
          )}
          <div ref={messageEndRef} />
        </section>

        {dragActive && !roomItemModalOpen ? (
          <div className="hc-drop-overlay">
            <div>
              <strong>파일을 놓으면 업로드됩니다.</strong>
              <span>대용량 파일도 백그라운드로 올라가며, 채팅 입력은 계속 사용할 수 있습니다.</span>
            </div>
          </div>
        ) : null}

        {uploadTasks.length > 0 ? (
          <div className="hc-upload-panel">
            <div className="hc-upload-panel-head">
              <strong>파일 업로드</strong>
              <span>{uploadTasks.filter((task) => task.status === 'uploading').length > 0 ? '업로드 중에도 메시지를 보낼 수 있습니다.' : '완료된 항목은 잠시 후 사라집니다.'}</span>
            </div>
            <div className="hc-upload-list">
              {uploadTasks.map((task) => (
                <div key={task.id} className={`hc-upload-item ${task.status}`}>
                  <div className="hc-upload-info">
                    <strong title={task.name}>{task.name}</strong>
                    <span>
                      {task.status === 'waiting' ? '대기 중' : null}
                      {task.status === 'uploading' ? `${task.progress}% · ${formatFileSize(task.size)}` : null}
                      {task.status === 'done' ? `완료 · ${formatFileSize(task.size)}` : null}
                      {task.status === 'error' ? `실패 · ${task.error}` : null}
                    </span>
                  </div>
                  <div className="hc-upload-bar">
                    <i style={{ width: `${task.status === 'done' ? 100 : task.progress || 0}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {messageMenu ? (
          <div
            className="hc-message-menu"
            style={{ left: messageMenu.x, top: messageMenu.y }}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => {
                setReplyTarget(messageMenu.message);
                setMessageMenu(null);
                setTimeout(() => inputRef.current?.focus?.(), 0);
              }}
            >
              답장
            </button>
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => openShareMessage(messageMenu.message)}
            >
              전달
            </button>
            {messageMenu.canDelete ? (
              <button
                type="button"
                className="danger"
                disabled={deletingMessageId === Number(messageMenu.message?.id)}
                onClick={() => deleteMessage(messageMenu.message)}
              >
                삭제
              </button>
            ) : null}
          </div>
        ) : null}

        <footer className={`hc-composer ${replyTarget ? 'is-replying' : ''}`}>
          <input
            ref={fileInputRef}
            className="hc-file-input"
            type="file"
            multiple
            onChange={(e) => uploadFiles(e.target.files)}
          />
          {pendingAttachments.length > 0 ? (
            <div className="hc-pending-attachments">
              <div className="hc-pending-head">
                <strong>전송 대기 첨부</strong>
                <span>전송을 누르면 채팅방에 올라갑니다.</span>
              </div>
              <div className="hc-pending-list">
                {pendingAttachments.map((item) => (
                  <div key={item.id} className="hc-pending-item">
                    {item.previewUrl ? (
                      <img src={item.previewUrl} alt="붙여넣은 이미지 미리보기" />
                    ) : (
                      <span className="hc-pending-file-icon">📎</span>
                    )}
                    <div>
                      <strong title={item.name}>{item.name}</strong>
                      <span>{formatFileSize(item.size)}</span>
                    </div>
                    <button type="button" onClick={() => removePendingAttachment(item.id)}>삭제</button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {replyTarget ? (
            <div className="hc-reply-composer" role="status" aria-live="polite">
              <div className="hc-reply-composer-content">
                <strong>{`${formatEmployeeDisplayFromMessage(replyTarget, roomMembers)}에게 답장`}</strong>
                <span>{getMessagePreviewText(replyTarget) || '첨부파일'}</span>
              </div>
              <button type="button" className="hc-reply-composer-close" onClick={() => setReplyTarget(null)} aria-label="답장 취소">×</button>
            </div>
          ) : null}
          <div className="hc-composer-input-row">
            {replyTarget ? <span className="hc-reply-input-mark" aria-hidden="true">↳</span> : null}
            <textarea
              ref={inputRef}
              disabled={!activeRoom || sending}
              placeholder={!activeRoom ? '채팅방을 선택하세요.' : replyTarget ? '답장 입력' : (isMobileLayout ? '' : 'Shift+Enter 줄바꿈 · Ctrl+V 이미지 붙여넣기 후 전송')}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onPaste={handleClipboardPaste}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
            />
            <button
              type="button"
              className="hc-file-button"
              disabled={!activeRoom}
              onClick={() => fileInputRef.current?.click?.()}
              title="파일 첨부"
              aria-label="파일 첨부"
            >
              +
            </button>
            <button
              type="button"
              className="hc-send-button"
              disabled={!activeRoom || (!text.trim() && pendingAttachments.length === 0) || sending}
              onClick={sendMessage}
            >
              전송
            </button>
          </div>
        </footer>

        {noticeModalPortal}

        {notesOpen ? (
          <div className="hc-note-backdrop" onClick={() => setNotesOpen(false)}>
            <section className="hc-note-modal hc-note-modal-wide" onClick={(e) => e.stopPropagation()}>
              <header className="hc-note-head">
                <div>
                  <strong>방 노트</strong>
                  <span>{activeRoom ? getRoomTitle(activeRoom) : ''}</span>
                </div>
                <button type="button" onClick={() => setNotesOpen(false)}>닫기</button>
              </header>
              <div className="hc-notes-layout">
                <div
                  className="hc-note-editor hc-room-item-drop-zone"
                  onDragEnterCapture={handleRoomItemDragOver}
                  onDragOverCapture={handleRoomItemDragOver}
                  onDropCapture={(e) => handleRoomItemDrop(e, setNoteFiles)}
                  onPasteCapture={(e) => handleRoomItemPaste(e, setNoteFiles)}
                >
                  <input
                    value={noteTitle}
                    onChange={(e) => setNoteTitle(e.target.value)}
                    placeholder="노트 제목, 선택"
                    maxLength={200}
                  />
                  <textarea
                    value={noteBody}
                    onChange={(e) => setNoteBody(e.target.value)}
                    placeholder="회의 내용, 업무 기준, 자주 쓰는 링크 등을 정리하세요."
                    rows={10}
                  />
                  <input
                    ref={noteFileInputRef}
                    type="file"
                    multiple
                    accept="image/*,.pdf,.txt,.csv,.xlsx,.xls,.doc,.docx,.ppt,.pptx,.hwp,.hwpx"
                    className="hc-hidden-file-input"
                    onChange={(e) => {
                      addRoomItemFiles(e.target.files, setNoteFiles);
                      e.target.value = '';
                    }}
                  />
                  <button type="button" className="hc-room-item-file-pick" onClick={() => noteFileInputRef.current?.click?.()}>사진/파일 첨부</button>
                  <PendingRoomItemFiles files={noteFiles} onRemove={(id) => removeRoomItemFile(id, setNoteFiles)} />
                  <div className="hc-note-actions">
                    {editingNoteId ? <button type="button" onClick={resetNoteEditor}>새 노트</button> : null}
                    <button type="button" className="primary" disabled={savingNote} onClick={saveNote}>{savingNote ? '저장 중...' : editingNoteId ? '노트 수정' : '노트 추가'}</button>
                  </div>
                </div>
                <div className="hc-note-list">
                  {loadingNotes ? <div className="hc-empty-small">노트 불러오는 중...</div> : null}
                  {!loadingNotes && roomNotes.length === 0 ? <div className="hc-empty-small">등록된 노트가 없습니다.</div> : null}
                  {roomNotes.map((note) => {
                    const canEditNote = canManageCurrentRoom || Number(note.created_by) === Number(currentEmployeeId);
                    return (
                      <article key={note.id} className={`hc-note-item ${Number(editingNoteId) === Number(note.id) ? 'is-editing' : ''}`}>
                        <div className="hc-note-item-head">
                          <strong>{note.title || '제목 없음'}</strong>
                          <span>{formatMessageDate(note.updated_at)} {formatMessageTime(note.updated_at)}</span>
                        </div>
                        {note.body ? <p>{note.body}</p> : null}
                        <RoomItemAttachmentList
                          attachments={note.attachments}
                          onPreview={openRoomItemAttachmentPreview}
                          onDownload={downloadRoomItemAttachment}
                        />
                        <footer>
                          <span>{note.created_by_name || '알 수 없음'}</span>
                          <div>
                            {canEditNote ? <button type="button" onClick={() => startEditNote(note)}>수정</button> : null}
                            {canEditNote ? <button type="button" className="danger" onClick={() => deleteNote(note)}>삭제</button> : null}
                          </div>
                        </footer>
                      </article>
                    );
                  })}
                </div>
              </div>
            </section>
          </div>
        ) : null}

        {renameOpen ? (
          <div className="hc-rename-backdrop" onClick={() => setRenameOpen(false)}>
            <div className="hc-rename-modal" onClick={(event) => event.stopPropagation()}>
              <div className="hc-rename-head">
                <strong>채팅방 이름 수정</strong>
                <button type="button" onClick={() => setRenameOpen(false)}>닫기</button>
              </div>
              <input
                autoFocus
                value={renameTitle}
                maxLength={80}
                onChange={(e) => setRenameTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveRoomTitle();
                  if (e.key === 'Escape') setRenameOpen(false);
                }}
                placeholder="채팅방 이름"
              />
              <div className="hc-rename-actions">
                <button type="button" onClick={() => setRenameOpen(false)}>취소</button>
                <button type="button" disabled={renamingRoom} onClick={saveRoomTitle}>저장</button>
              </div>
            </div>
          </div>
        ) : null}





        {addMembersHistoryOpen ? (
          <div className="hc-confirm-backdrop" onClick={() => !creatingRoom && setAddMembersHistoryOpen(false)}>
            <section className="hc-confirm-modal" onClick={(e) => e.stopPropagation()}>
              <header className="hc-confirm-head">
                <strong>이전 대화 공개 설정</strong>
                <button type="button" disabled={creatingRoom} onClick={() => setAddMembersHistoryOpen(false)}>닫기</button>
              </header>
              <div className="hc-confirm-body">
                <p>
                  선택한 직원 {selectedMembers.length}명을 추가합니다. 새 참여자가 기존 대화 내용을 볼 수 있는지 선택하세요.
                </p>
                <label className={`hc-history-option ${historyAll ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="history-mode"
                    checked={historyAll}
                    onChange={() => setHistoryAll(true)}
                  />
                  <span>
                    <strong>이전 대화 전체 공개</strong>
                    <em>업무 맥락을 바로 확인할 수 있습니다.</em>
                  </span>
                </label>
                <label className={`hc-history-option ${!historyAll ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="history-mode"
                    checked={!historyAll}
                    onChange={() => setHistoryAll(false)}
                  />
                  <span>
                    <strong>초대 이후 대화만 공개</strong>
                    <em>기존 대화는 보이지 않습니다.</em>
                  </span>
                </label>
              </div>
              <footer className="hc-confirm-actions">
                <button type="button" disabled={creatingRoom} onClick={() => setAddMembersHistoryOpen(false)}>취소</button>
                <button type="button" className="primary" disabled={creatingRoom} onClick={addMembersToRoom}>
                  {creatingRoom ? '추가 중...' : '선택한 직원 추가'}
                </button>
              </footer>
            </section>
          </div>
        ) : null}

        
      {exportOpen ? (
        <div className="hc-export-floating">
          <div className={`hc-export-modal ${exportingRoom ? 'is-running' : ''} hc-export-modal--${String(exportProgress?.status || 'idle').toLowerCase()}`}>
            <div className="hc-export-head">
              <div>
                <strong>대화 백업</strong>
                <span>{exportRoomTitle || (activeRoom ? getRoomTitle(activeRoom) : '')}</span>
              </div>
            </div>

            <div className="hc-export-body">
              <label className="hc-export-check hc-export-all-check">
                <input
                  type="checkbox"
                  checked={exportAllDates}
                  onChange={(e) => setExportAllDates(e.target.checked)}
                  disabled={exportingRoom}
                />
                <span>전체 기간</span>
              </label>

              <div className="hc-export-row">
                <label>
                  <span>시작일</span>
                  <input
                    type="date"
                    value={getSafeJumpDate(exportStartDate) || ''}
                    onChange={(e) => setExportStartDate(e.target.value)}
                    disabled={exportingRoom || exportAllDates}
                  />
                </label>
                <label>
                  <span>종료일</span>
                  <input
                    type="date"
                    value={getSafeJumpDate(exportEndDate) || ''}
                    onChange={(e) => setExportEndDate(e.target.value)}
                    disabled={exportingRoom || exportAllDates}
                  />
                </label>
              </div>

              <label className="hc-export-check">
                <input
                  type="checkbox"
                  checked={exportIncludeAttachments}
                  onChange={(e) => setExportIncludeAttachments(e.target.checked)}
                  disabled={exportingRoom}
                />
                <span>첨부파일 포함</span>
              </label>

              {exportProgress ? (
                <div className="hc-export-progress">
                  <div className="hc-export-progress-top">
                    <strong>{exportProgress.phase || '진행 중'}</strong>
                    <span>{Math.round(Number(exportProgress.percent || 0))}%</span>
                  </div>
                  <div className="hc-export-progress-bar" aria-label="백업 진행률">
                    <span style={{ width: `${Math.max(0, Math.min(100, Number(exportProgress.percent || 0)))}%` }} />
                  </div>
                  <p className="hc-export-progress-message">{exportProgress.message || '처리 중입니다.'}</p>
                  {exportProgress.current_file_name ? (
                    <p className="hc-export-current-file">
                      <span>현재 파일</span>
                      <b>{exportProgress.current_file_name}</b>
                    </p>
                  ) : null}
                  {exportProgress.include_attachments ? (
                    <small>
                      첨부파일 {Number(exportProgress.processed_attachments || 0)} / {Number(exportProgress.total_attachments || 0)}
                      {Number(exportProgress.skipped_attachments || 0) > 0 ? ` · 건너뜀 ${Number(exportProgress.skipped_attachments || 0)}` : ''}
                    </small>
                  ) : null}
                </div>
              ) : null}

              <p className="hc-export-help">
                <span>ZIP 안에 대화 TXT와 첨부파일을 저장합니다.</span>
                <span>창을 숨겨도 작업은 계속됩니다.</span>
              </p>
            </div>

            <div className="hc-export-actions">
              <button type="button" onClick={hideExportPanel}>{exportingRoom ? '숨기기' : '닫기'}</button>
              {String(exportProgress?.status || '').toUpperCase() === 'COMPLETED' ? (
                <>
                  <button type="button" onClick={() => triggerExportDownload(exportProgress)}>다시 다운로드</button>
                  <button type="button" className="primary" onClick={resetExportPanel}>새로 만들기</button>
                </>
              ) : String(exportProgress?.status || '').toUpperCase() === 'FAILED' ? (
                <>
                  <button type="button" onClick={resetExportPanel}>새로 만들기</button>
                  <button type="button" className="primary" onClick={downloadRoomExport} disabled={exportingRoom}>다시 시도</button>
                </>
              ) : String(exportProgress?.status || '').toUpperCase() === 'CANCELLED' ? (
                <>
                  <button type="button" onClick={resetExportPanel}>새로 만들기</button>
                  <button type="button" className="primary" onClick={downloadRoomExport} disabled={exportingRoom}>다시 시작</button>
                </>
              ) : exportingRoom ? (
                <>
                  <button type="button" className="danger" onClick={cancelExportJob}>중지</button>
                  <button type="button" className="primary" disabled>생성 중...</button>
                </>
              ) : (
                <button type="button" className="primary" onClick={downloadRoomExport}>
                  백업 시작
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {!exportOpen && exportProgress ? (
        <button type="button" className={`hc-export-status-chip hc-export-status-chip--${String(exportProgress.status || '').toLowerCase()}`} onClick={() => setExportOpen(true)}>
          <span>{exportRoomTitle || '대화 백업'}</span>
          <strong>{exportStatusText || '백업 진행 중'}</strong>
        </button>
      ) : null}

{previewModalPortal}
      </main>
      {chatModalPortal}
      {shareLayerPortal}
    </div>
  );
}
